import { QueryClient } from '@tanstack/react-query'
import { CancelToken } from 'axios'
import { noop } from 'lodash'
import moment from 'moment/moment'
import React, { useEffect } from 'react'
import Rollbar from 'rollbar'

import { PSApplication } from '../components/app/PSApplication'
import {
    LoginResponse,
    apiClient,
    loginWithAssociatedAccountUserID,
    loginWithCode,
} from '../modules/apiclient'
import { Result, VoidResult, errorResult, okResult, voidResult } from '../types/utils'
import { addImage, batch } from '../utils'
import {
    IS_DEVELOPMENT,
    LOCAL_STORAGE_DEVICE_ID_KEY,
    LOCAL_STORAGE_INDUSTRIES_KEY,
    LOCAL_STORAGE_LOGGED_IN_KEY,
    LOCAL_STORAGE_USER_ID_KEY,
    POCKETSUITE_USER_ID,
} from '../utils/constants'
import { formatTimeStamp } from '../utils/dates'
import { Adjustment } from './Adjustment'
import { Alias } from './Alias'
import { Availability } from './Availability'
import { Badge } from './Badge'
import { Balance } from './Balance'
import { BaseRecord } from './BaseRecord'
import { Block } from './Block'
import { Bookable } from './Bookable'
import { Booking } from './Booking'
import { CacheFactory } from './CacheFactory'
import { Calendar } from './Calendar'
import { CalendarFilter } from './CalendarFilter'
import { Campaign } from './Campaign'
import { Category } from './Category'
import { Change } from './Change'
import { Channel } from './Channel'
import { Charge } from './Charge'
import { Client } from './Client'
import { ComputeFactory } from './ComputeFactory'
import { Conference } from './Conference'
import { Contact } from './Contact'
import { Contract } from './Contract'
import { Criteria } from './Criteria'
import { isCustomizable } from './Customizable'
import { DASHBOARD_TAB, DASHBOARD_TYPE, Dashboard } from './Dashboard'
import { Deposit } from './Deposit'
import { Device } from './Device'
import { Edition } from './Edition'
import { Estimate } from './Estimate'
import { Feature } from './Feature'
import { Feed } from './Feed'
import { Field } from './Field'
import { Gift } from './Gift'
import { Goal } from './Goal'
import { GoogleLocation } from './GoogleLocation'
import { GoogleReview } from './GoogleReview'
import { ISQLExecutor } from './ISQLExecutor'
import { Image } from './Image'
import { Industry } from './Industry'
import { Invoice } from './Invoice'
import { Item } from './Item'
import { Job } from './Job'
import { Leaderboard } from './Leaderboard'
import { Lesson } from './Lesson'
import { Line } from './Line'
import { Location } from './Location'
import { Message } from './Message'
import { Note } from './Note'
import { Order } from './Order'
import { PSComponent } from './PSComponent'
import { PSCredential } from './PSCredential'
import { PSEvent } from './PSEvent'
import { PSLink } from './PSLink'
import { PSRecord } from './PSRecord'
import { Package } from './Package'
import { PAYEE_TYPE, Payee } from './Payee'
import { Payment } from './Payment'
import { PERSON_TYPE, Person } from './Person'
import { PhoneNumber } from './PhoneNumber'
import { Post } from './Post'
import { Promotion } from './Promotion'
import { Reader } from './Reader'
import { RecordDataMap } from './RecordDataMap'
import { Report } from './Report'
import { ReportCriteria } from './ReportCriteria'
import { ReportResult } from './ReportResult'
import { ReportTotal } from './ReportTotal'
import { Requirement } from './Requirement'
import { Resource } from './Resource'
import { Review } from './Review'
import { Royalty } from './Royalty'
import { SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Schedule } from './Schedule'
import { Signature } from './Signature'
import { Source } from './Source'
import { Subaccount } from './Subaccount'
import { Subscriber } from './Subscriber'
import { Subscription } from './Subscription'
import { TCR } from './TCR'
import { Task } from './Task'
import { Taxcode } from './Taxcode'
import { Team } from './Team'
import { Template } from './Template'
import { Transaction } from './Transaction'
import { Trigger } from './Trigger'
import { Type } from './Type'
import { User } from './User'

const KEY_SQL = 'REPLACE INTO keys(id, type) VALUES (?, ?)'

export class RecordFactory {
    private static instance: RecordFactory | null = null
    private executor: ISQLExecutor

    static getInstance() {
        if (!RecordFactory.instance) {
            RecordFactory.instance = new RecordFactory()
        }
        return RecordFactory.instance
    }

    constructor(executor: ISQLExecutor = SQLiteFactory.getInstance()) {
        this.executor = executor
    }

    // See the comment in providers/NotificationsManagerProvider for more on this hacky thing.
    get postNotification() {
        return (window as any).pocketsuite_postNotification ?? noop
    }

    async fetchRecord(id: string, model: any) {
        if (!id) return undefined

        const tableName = model.dataType()
        const sql = new SQLBuilder({
            columns: ['jsonData'],
            tables: [tableName],
            filters: ['id = ?'],
            binds: [id],
            fk: 'fk' in model ? model.fk : {},
        })
        const row = await this.executor.executeSingleRowQuery(sql)

        if (!row) return undefined
        const record = new model(JSON.parse(row['jsonData']))

        return record
    }

    async loadFieldsByOwner(owner: string): Promise<Field[]> {
        const results: Field[] = []
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('owner')
        sql.addField('record')
        sql.addField('jsonData')
        sql.addTable('fields')
        sql.filterBy('fields.owner = ?', owner)
        sql.filterBy('fields.record IS NULL')
        sql.filterBy('fields.cancelledDate IS NULL')
        if (PSApplication.me().perm === User.PERM_ROLE_PARTNER) {
            sql.filterBy(
                "(fields.standardField IS NULL OR fields.standardField NOT LIKE 'subaccount.%')"
            )
        }
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Field.fk
        const items = await this.executor.executeSql(sql)
        for (const item of items) {
            const field = new Field(JSON.parse(item['jsonData']))
            results.push(field)
            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name || '', owner, item['record']),
                field.type
            )
        }
        return results
    }

    async fetchCalendarBatch(keys: string[]): Promise<VoidResult> {
        const KEY_LIMIT = 1000
        if (keys.length > KEY_LIMIT) {
            const fetchResult = await this.fetchCalendarRecords(keys.slice(0, KEY_LIMIT))
            if (!fetchResult.ok) {
                return fetchResult
            }
            return this.fetchCalendarBatch(keys.slice(KEY_LIMIT))
        }
        else {
            return this.fetchCalendarRecords(keys)
        }
    }

    async fetchCalendarRecords(keys: string[]): Promise<VoidResult> {
        const begin = moment()

        const url = '/calendar/sync/records'
        const jsonData: any = { keys: keys || [] }
        try {
            const result = await apiClient.post(url, jsonData)
            const results = result.data
            const lessonData = results['classes']
            const bookingData = results['bookings']
            const itemData = results['items']
            const clientData = results['clients']
            const contactsData = results['contacts']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    bookingData,
                    txManager.recordFactory.storeBooking
                )
                if (bookingData?.length)
                    console.debug(`SYNC: Synced ${bookingData.length} bookings`)
                await txManager.recordFactory.storeAll(
                    lessonData,
                    txManager.recordFactory.storeClass
                )
                if (lessonData?.length) console.debug(`SYNC: Synced ${lessonData.length} lessons`)
                await txManager.recordFactory.storeAll(itemData, txManager.recordFactory.storeItem)
                if (itemData?.length) console.debug(`SYNC: Synced ${itemData.length} items`)
                await txManager.recordFactory.storeAll(
                    contactsData,
                    txManager.recordFactory.storeUser
                )
                if (contactsData?.length)
                    console.debug(`SYNC: Synced ${contactsData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
                console.debug(`FETCH: Calendar Fetch Time (${moment().diff(begin)})`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult()
        }
    }

    async fetchCalendarKeys(lastSyncDate: moment.Moment): Promise<VoidResult> {
        const url = '/calendar/sync/keys'
        const postArgs: any = {}
        if (lastSyncDate) {
            postArgs.lmt = lastSyncDate.toISOString()
        }
        const response = await apiClient.post(url, postArgs)
        const results = response.data
        const allClasses = results.all_classes
        const classes = results.classes
        const allItems = results.all_items
        const items = results.items
        const allBookings = results.all_bookings
        const bookings = results.bookings
        await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
            for (const booking of allBookings) {
                await txManager.executeUpdate(
                    'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                    booking
                )
            }
            for (const lesson of allClasses) {
                await txManager.executeUpdate(
                    'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                    lesson
                )
            }
            for (const item of allItems) {
                await txManager.executeUpdate(
                    'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                    item
                )
            }
        })
        const allSyncKeys: string[] = [...bookings, ...classes, ...items]
        return await this.fetchCalendarBatch(allSyncKeys)
    }

    // todo: implement GoogleLocation
    // async fetchGoogleLocations(credentialId: string, onLoad?: (result: any) => void, onFail?: (result: any) => void) {
    //     const url = "/externallocation/sync";
    //     const jsonData = { credential_id: credentialId };
    //     const response = await apiClient.post(url, jsonData);
    //     const results = response.data;
    //     const locations = results.locations;
    //     const credential = results.credential;
    //     const me = results.user;
    //     await this.storeCredential(credential);
    //     await this.storeUser(me);
    //     if (onLoad) {
    //         onLoad(locations.map((location: any) => new GoogleLocation(location)));
    //     }
    // }

    async fetchCalendars(): Promise<VoidResult> {
        const begin = moment()

        try {
            const result = await apiClient.get('/externalcalendars/sync')
            const results = result.data
            const calendars = results['calendars']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    calendars,
                    txManager.recordFactory.storeCalendar
                )
            })
            console.debug(`FETCH: fetchCalendars Time (${moment().diff(begin)})`)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCalendar(
        start: moment.Moment,
        end: moment.Moment,
        person?: string,
        item?: string,
        filter?: CalendarFilter,
        signal?: AbortSignal
    ): Promise<Result<Bookable[]>> {
        const begin = moment()
        const args: any = {
            start: start,
            end: end,
        }
        if (person) {
            args.person = person
        }
        if (item) {
            args.item = item
        }
        if (filter) {
            if (filter.hasPeople) {
                args.pros = filter.pros
            }
            if (filter.hasItems) {
                args.items = filter.allItems
            }
            if (filter.hasResources) {
                args.resources = filter.resources
            }
        }
        try {
            const result = await apiClient.post('/calendar/sync', args, { signal })
            const itemData = result.data.items
            const taskData = result.data.tasks
            const classData = result.data.classes
            const bookingData = result.data.bookings
            const blockData = result.data.blocks
            const eventData = result.data.events
            const orderData = result.data.orders
            const contactData = result.data.contacts
            const clientData = result.data.clients
            const payeeData = result.data.payees
            const subaccountData = result.data.subaccounts
            const packageData = result.data.packages
            const lessons: Lesson[] = []
            const bookings: Booking[] = []
            const orders: Order[] = []
            const items: Item[] = []
            const blocks: Block[] = []
            const tasks: Task[] = []
            const events: PSEvent[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                items.push(
                    ...(await Promise.all<Item>(
                        itemData.map(async (i: Item) => await txManager.recordFactory.storeItem(i))
                    ))
                )
                bookings.push(
                    ...(await Promise.all<Booking>(
                        bookingData.map(
                            async (b: Booking) => await txManager.recordFactory.storeBooking(b)
                        )
                    ))
                )
                lessons.push(
                    ...(await Promise.all<Lesson>(
                        classData.map(
                            async (l: Lesson) => await txManager.recordFactory.storeClass(l)
                        )
                    ))
                )
                blocks.push(
                    ...(await Promise.all<Block>(
                        blockData.map(
                            async (b: Block) => await txManager.recordFactory.storeBlock(b)
                        )
                    ))
                )
                tasks.push(
                    ...(await Promise.all<Task>(
                        taskData.map(async (t: Task) => await txManager.recordFactory.storeTask(t))
                    ))
                )
                orders.push(
                    ...(await Promise.all<Order>(
                        orderData.map(
                            async (o: Order) => await txManager.recordFactory.storeOrder(o)
                        )
                    ))
                )
                events.push(
                    ...(await Promise.all<PSEvent>(
                        eventData.map(
                            async (e: PSEvent) => await txManager.recordFactory.storeEvent(e)
                        )
                    ))
                )

                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                await txManager.recordFactory.storeAll(
                    payeeData,
                    txManager.recordFactory.storePayee
                )
                await txManager.recordFactory.storeAll(
                    subaccountData,
                    txManager.recordFactory.storeSubaccount
                )
                await txManager.recordFactory.storeAll(
                    packageData,
                    txManager.recordFactory.storePackage
                )

                console.debug(`FETCH: Calendar Fetch Time (${moment().diff(begin)})`)
            })
            const me = PSApplication.me()
            const owner = me.owner!
            const l: any[] = []
            if (me.sp && bookings.length > 0) {
                l.push(...bookings)
            }
            if (me.sp && lessons.length > 0 && owner.allowClass) {
                l.push(...lessons)
            }
            if (me.sp && items.length > 0 && owner.allowReservation) {
                l.push(...items)
            }
            if (me.sp && orders.length > 0 && owner.allowProduct) {
                l.push(...orders)
            }
            await this.pushBookings(l)

            return okResult([
                ...items,
                ...bookings,
                ...lessons,
                ...blocks,
                ...tasks,
                ...orders,
                ...events,
            ])
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCalendarSummary(
        start: moment.Moment,
        end: moment.Moment,
        filter: CalendarFilter
    ): Promise<Result<any>> {
        const args: any = {}
        args['start'] = start
        args['end'] = end
        if (filter) {
            if (filter.hasPeople) args['pros'] = filter.pros
            if (filter.hasItems) args['items'] = filter.allItems
            if (filter.hasResources) args['resources'] = filter.resources
        }

        try {
            const result = await apiClient.post('/calendar/sync/summary', args)
            const results = result.data
            const colorsByDate: any = {}

            for (const dateStr in results) {
                const l = results[dateStr]
                const colors: string[] = []
                for (const jsonSummaryData of l) {
                    colors.push(jsonSummaryData['color'])
                }
                const date = moment(dateStr).format('YYYY-MM-DD')
                colorsByDate[date] = colors
            }

            return okResult(colorsByDate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendInstantPayout(): Promise<VoidResult> {
        const url = '/deposit/instant'
        try {
            const result = await apiClient.post(url)
            const results = result.data
            const balanceData = results['balances']
            const paymentData = results['payments']
            const depositData = results['deposit']

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    balanceData,
                    txManager.recordFactory.storeBalance
                )
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                await txManager.recordFactory.storeDeposit(depositData)
            })

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBalances(): Promise<VoidResult> {
        const url = '/balances'
        try {
            const result = await apiClient.get(url)
            const results = result.data
            const balanceData = results['balances']
            const paymentData = results['payments']
            const depositData = results['deposits']

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    balanceData,
                    txManager.recordFactory.storeBalance
                )
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                await txManager.recordFactory.storeAll(
                    depositData,
                    txManager.recordFactory.storeDeposit
                )
            })

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchAudience(trigger: Trigger): Promise<Result<RecordDataMap>> {
        const jsonData: any = {}
        if (trigger.id) jsonData['id'] = trigger.id
        jsonData['audience'] = trigger.audience
        jsonData['audience_item'] = trigger.audienceItem ? trigger.audienceItem.id : ''
        jsonData['audience_amount'] = trigger.audienceAmount || ''
        jsonData['audience_count'] = trigger.audienceCount || ''

        try {
            const result = await apiClient.post('/trigger/audience', jsonData)

            const users: User[] = []
            const results = result.data
            const userData = results['users']
            const clientData = results['clients']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const user of userData)
                    users.push(await txManager.recordFactory.storeUser(user, true))
                if (userData?.length) console.debug(`SYNC: Synced ${userData.length} users`)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
            })
            return okResult(new RecordDataMap({ users: users, count: results['count'] }))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCreditPayments(): Promise<Result<RecordDataMap>> {
        try {
            const result = await apiClient.get('/billing/history/credit')

            const payments: Payment[] = []
            const results = result.data
            const paymentData = results['payments']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const payment of paymentData)
                    payments.push(await txManager.recordFactory.storePayment(payment, true))
                if (paymentData?.length)
                    console.debug(`SYNC: Synced ${paymentData.length} payments`)
            })
            return okResult(
                new RecordDataMap({
                    payments: payments,
                    credits: results['credits'],
                    lastPayment: results['last_payment'],
                    allowCredits: results['allow_credits'],
                })
            )
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPremiumPayments(): Promise<VoidResult> {
        try {
            const result = await apiClient.get('/billing/history/subscription')
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storePayment
                )
            })
            console.debug(`SYNC: Synced ${resultData.length} payments`)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPayableBookings(idString: string): Promise<VoidResult> {
        try {
            const result = await apiClient.get(`/bookings/${idString}/payable`)
            const resultData = result.data
            const bookingData = resultData['bookings']
            const taxcodeData = resultData['taxcodes']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    bookingData,
                    txManager.recordFactory.storeBooking
                )
                console.debug(`SYNC: Synced ${bookingData.length} bookings`)
                await txManager.recordFactory.storeAll(
                    taxcodeData,
                    txManager.recordFactory.storeTaxcode
                )
                console.debug(`SYNC: Synced ${taxcodeData.length} taxcodes`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBillableBookings(idString: string): Promise<VoidResult> {
        try {
            const result = await apiClient.get(`/bookings/${idString}/billable`)
            const resultData = result.data
            const bookingData = resultData['bookings']
            const taxcodeData = resultData['taxcodes']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    bookingData,
                    txManager.recordFactory.storeBooking
                )
                console.debug(`SYNC: Synced ${bookingData.length} bookings`)
                await txManager.recordFactory.storeAll(
                    taxcodeData,
                    txManager.recordFactory.storeTaxcode
                )
                console.debug(`SYNC: Synced ${taxcodeData.length} taxcodes`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult()
        }
    }

    async fetchInvoice(invoice: Invoice | string, type?: string): Promise<VoidResult> {
        const start = moment()
        try {
            const jsonData: any = {}
            jsonData['id'] = invoice instanceof Invoice ? invoice.id : invoice
            if (!!type) jsonData['type'] = type
            if (invoice instanceof Invoice && invoice.client) jsonData['client'] = invoice.client.id

            const result = await apiClient.post('/invoice/sync', jsonData)
            const results = result.data
            const bookingData = results['bookings']
            const paymentData = results['payments']
            const giftData = results['gifts']
            const itemData = results['items']
            const invoiceData = results['invoices']
            const recordData = results['records']
            const signatureData = results['signatures']

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['invoice'])
                    await txManager.recordFactory.storeInvoice(results['invoice'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (bookingData?.length)
                    await txManager.recordFactory.storeAll(
                        bookingData,
                        txManager.recordFactory.storeBooking
                    )
                if (itemData?.length)
                    await txManager.recordFactory.storeAll(
                        itemData,
                        txManager.recordFactory.storeItem
                    )
                if (giftData?.length)
                    await txManager.recordFactory.storeAll(
                        giftData,
                        txManager.recordFactory.storeGift
                    )
                if (invoiceData?.length)
                    await txManager.recordFactory.storeAll(
                        invoiceData,
                        txManager.recordFactory.storeInvoice
                    )
                if (paymentData?.length)
                    await txManager.recordFactory.storeAll(
                        paymentData,
                        txManager.recordFactory.storePayment
                    )
                if (recordData?.length)
                    await txManager.recordFactory.storeAll(
                        recordData,
                        txManager.recordFactory.storeRecord
                    )
                if (signatureData?.length)
                    await txManager.recordFactory.storeAll(
                        signatureData,
                        txManager.recordFactory.storeSignature
                    )
                console.debug(`Invoice Sync Time (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchEstimate(idString: string): Promise<VoidResult> {
        const start = moment()
        try {
            const result = await apiClient.post('/estimate/sync', { id: idString })
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['estimate'])
                    await txManager.recordFactory.storeEstimate(results['estimate'])
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['source']) await txManager.recordFactory.storeSource(results['source'])
                if (results['items']) {
                    const itemData = results['items']
                    await txManager.recordFactory.storeAll(
                        itemData,
                        txManager.recordFactory.storeItem
                    )
                }
                if (results['records']) {
                    const recordData = results['records']
                    await txManager.recordFactory.storeAll(
                        recordData,
                        txManager.recordFactory.storeRecord
                    )
                }
                if (results['signatures']) {
                    const signatureData = results['signatures']
                    await txManager.recordFactory.storeAll(
                        signatureData,
                        txManager.recordFactory.storeSignature
                    )
                }
                console.debug(`Estimate Sync Time (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPayment(payment: Payment | string): Promise<VoidResult> {
        const start = moment()
        try {
            const paymentId = payment instanceof Payment ? payment.id : payment
            const result = await apiClient.post('/payment/sync', { id: paymentId })
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storePayment(results['payment'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['booking'])
                    await txManager.recordFactory.storeBooking(results['booking'])
                if (results['message'])
                    await txManager.recordFactory.storeMessage(results['message'])
                if (results['invoice'])
                    await txManager.recordFactory.storeInvoice(results['invoice'])
                if (results['package'])
                    await txManager.recordFactory.storePackage(results['package'])
                if (results['gift']) await txManager.recordFactory.storeGift(results['gift'])
                if (results['order']) await txManager.recordFactory.storeOrder(results['order'])
                if (results['estimate'])
                    await txManager.recordFactory.storeEstimate(results['estimate'])
                if (results['deposit'])
                    await txManager.recordFactory.storeDeposit(results['deposit'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['subscription'])
                    await txManager.recordFactory.storeSubscription(results['subscription'])
                console.debug(`Payment Sync Time (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCharge(charge: Charge): Promise<VoidResult> {
        const start = moment()
        try {
            const result = await apiClient.post('/charge/sync', { id: charge.id })
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeCharge(results['charge'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['payment'])
                    await txManager.recordFactory.storePayment(results['payment'])
                if (results['message'])
                    await txManager.recordFactory.storeMessage(results['message'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                console.debug(`Charge Sync Time (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchConference(
        conference: Conference,
        booking: Booking
    ): Promise<Result<string | Conference | undefined>> {
        const start = moment()
        try {
            const result = await apiClient.post('/conference/sync', {
                id: conference.id,
                booking: booking.id,
            })
            const results = result.data
            const conference1 = results['conference']
                ? await this.storeConference(results['conference'])
                : undefined
            if (results['participants']) {
                const participantArray = results['participants']
                await this.storeAll(participantArray, this.storeUser)
                console.debug(`SYNC: Synced ${participantArray.length} participants`)
            }
            console.debug(`Conference Sync Time ${moment().diff(start)}`)
            return okResult(conference1)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBookingsForSchedule(scheduleID: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/bookings', { schedule: scheduleID })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeBooking
                )
                console.debug(`SYNC: Synced ${resultData.length} bookings`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBills(person: User): Promise<VoidResult> {
        const start = moment()
        try {
            const url = '/bills'
            const jsonData: any = {}
            if (person) jsonData['person'] = person.id
            const result = await apiClient.post(url, jsonData)
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const item of results['items']) await txManager.recordFactory.storeItem(item)
                for (const form of results['forms']) await txManager.recordFactory.storeType(form)
                for (const field of results['fields'])
                    await txManager.recordFactory.storeField(field)
                for (const booking of results['bookings'])
                    await txManager.recordFactory.storeBooking(booking)
                for (const gift of results['gifts']) await txManager.recordFactory.storeGift(gift)
                for (const invoice of results['invoices'])
                    await txManager.recordFactory.storeInvoice(invoice)
                for (const packageItem of results['packages'])
                    await txManager.recordFactory.storePackage(packageItem)
                for (const estimate of results['lessons'])
                    await txManager.recordFactory.storeEstimate(estimate)
                for (const contract of results['contracts'])
                    await txManager.recordFactory.storeContract(contract)
                for (const record of results['records'])
                    await txManager.recordFactory.storeRecord(record)
                for (const signature of results['signatures'])
                    await txManager.recordFactory.storeSignature(signature)
                for (const subscription of results['subscriptions'])
                    await txManager.recordFactory.storeSubscription(subscription)
                console.debug(
                    `BILL SYNC: Fetch ${results['number_of_bills']} bills (${moment().diff(start)}ms)`
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchJobs(person: User, booking: Booking, forPayable: boolean): Promise<VoidResult> {
        const start = moment()
        try {
            const url = '/jobs'
            const jsonData: any = {}
            if (person) jsonData['person'] = person.id
            if (forPayable) jsonData['payable'] = forPayable
            if (booking) jsonData['booking'] = booking.id
            const result = await apiClient.post(url, jsonData)
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const job of results['jobs']) await txManager.recordFactory.storeJob(job)
                for (const client of results['clients'])
                    await txManager.recordFactory.storeUser(client)
                for (const bookingItem of results['bookings'])
                    await txManager.recordFactory.storeBooking(bookingItem)
                for (const lesson of results['lessons'])
                    await txManager.recordFactory.storeClass(lesson)
                for (const invoice of results['invoices'])
                    await txManager.recordFactory.storeInvoice(invoice)
                for (const packageItem of results['packages'])
                    await txManager.recordFactory.storePackage(packageItem)
                for (const subscription of results['subscriptions'])
                    await txManager.recordFactory.storeSubscription(subscription)
                console.debug(
                    `JOB SYNC: Fetch ${results['jobs'].length} jobs (${moment().diff(start)}ms)`
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchProducts(
        type: string,
        category: Category,
        booking: Booking
    ): Promise<Result<{ products: Item[] }>> {
        const jsonData: any = {}
        jsonData['type'] = type
        let url: string

        if (category) {
            url = '/category/products'
            jsonData['category'] = category.id
            jsonData['owner'] = category.owner!.id
        }
        else {
            url = '/booking/products'
            jsonData['owner'] = booking.owner!.id
            if (booking.lines?.length) {
                jsonData['lines'] = booking.lines
            }
        }

        try {
            const result = await apiClient.post(url, jsonData)
            const results = result.data
            const productData = results['products'] || []
            const products: Item[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const product of productData)
                    products.push(await txManager.recordFactory.storeItem(product, true))
            })

            return okResult({
                products: products,
            })
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchLesson(lessonId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/class/sync', { id: lessonId })
            const results = result.data

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeItem(results['item'])
                await txManager.recordFactory.storeClass(results['lesson'])

                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['person']) await txManager.recordFactory.storeUser(results['person'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['type']) await txManager.recordFactory.storeType(results['type'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['signatures']) {
                    for (const signature of results['signatures'])
                        await txManager.recordFactory.storeSignature(signature)
                }
                if (results['fields']) {
                    for (const field of results['fields'])
                        await txManager.recordFactory.storeField(field)
                }
            })

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // method name in Java is fetchRecord, here is fetchRecordType
    async fetchRecordType(idString: string, type: string): Promise<VoidResult> {
        const postData: any = { type: type }
        if (idString) postData['id'] = idString

        try {
            const result = await apiClient.post('/record/sync', postData)
            const results = result.data

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['person']) await txManager.recordFactory.storeUser(results['person'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['type']) await txManager.recordFactory.storeType(results['type'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['signatures']) {
                    for (const signature of results['signatures'])
                        await txManager.recordFactory.storeSignature(signature)
                }
                if (results['fields']) {
                    for (const field of results['fields'])
                        await txManager.recordFactory.storeField(field)
                }
            })

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSignature(idString: string): Promise<Result<Signature | undefined>> {
        const start = moment()
        try {
            const result = await apiClient.post('/signature/sync', { id: idString })
            const results = result.data
            let newSignature: Signature | undefined = undefined
            if (results['signature'])
                newSignature = await this.storeSignature(results['signature'], true)
            if (results['contract']) await this.storeContract(results['contract'])
            if (results['owner']) await this.storeUser(results['owner'])
            if (results['person']) await this.storeUser(results['person'])
            if (results['employee']) await this.storeUser(results['employee'])
            if (results['subaccount']) await this.storeSubaccount(results['subaccount'])
            console.debug(`Sign Sync Time (ms) ${moment().diff(start)}`)
            return okResult(newSignature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchItemBookings(itemId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/bookings', { item: itemId })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeBooking
                )
                if (resultData?.length) console.debug(`SYNC: Synced ${resultData.length} bookings`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchGift(gift: Gift | string): Promise<VoidResult> {
        const start = moment()
        try {
            const id = typeof gift === 'string' ? gift : gift.id
            const result = await apiClient.post('/gift/sync', { id })
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['gift']) await txManager.recordFactory.storeGift(results['gift'])
                console.debug('SYNC: Synced 1 gift')
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['customer'])
                    await txManager.recordFactory.storeUser(results['customer'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['item']) await txManager.recordFactory.storeItem(results['item'])
                console.debug('SYNC: Synced 1 item')
                for (const booking of results['bookings'])
                    await txManager.recordFactory.storeBooking(booking)
                if (results['bookings']?.length)
                    console.debug(`SYNC: Synced ${results['bookings'].length} bookings`)
                for (const invoice of results['invoices'])
                    await txManager.recordFactory.storeInvoice(invoice)
                if (results['invoices']?.length)
                    console.debug(`SYNC: Synced ${results['invoices'].length} invoices`)
                for (const packageItem of results['packages'])
                    await txManager.recordFactory.storePackage(packageItem)
                if (results['packages']?.length)
                    console.debug(`SYNC: Synced ${results['packages'].length} packages`)
                if (results['records']?.length) {
                    for (const record of results['records'])
                        await txManager.recordFactory.storeRecord(record)
                    console.debug(`SYNC: Synced ${results['records'].length} records`)
                }
                if (results['signatures']?.length) {
                    for (const signature of results['signatures'])
                        await txManager.recordFactory.storeSignature(signature)
                    console.debug(`SYNC: Synced ${results['signatures'].length} signatures`)
                }
                for (const subscription of results['subscriptions'])
                    await txManager.recordFactory.storeSubscription(subscription)
                if (results['subscriptions']?.length)
                    console.debug(`SYNC: Synced ${results['subscriptions'].length} subscriptions`)
                console.debug(`Gift Sync Time (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchUser(user: string | User): Promise<Result<any>> {
        let userId = undefined
        if (typeof user === 'string') {
            userId = user
        }
        else {
            userId = user.id
        }
        const start = moment()
        try {
            const result = await apiClient.post('/user/sync', { id: userId })
            const results = result.data
            const dashboardData: any = {}
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['user']) await txManager.recordFactory.storeUser(results['user'])
                if (results['employer'])
                    await txManager.recordFactory.storeUser(results['employer'])
                if (results['client']) await txManager.recordFactory.storeClient(results['client'])
                if (results['payee']) await txManager.recordFactory.storePayee(results['payee'])
                if (results['location'])
                    await txManager.recordFactory.storeLocation(results['location'])
                if (results['subscriber'])
                    await txManager.recordFactory.storeSubscriber(results['subscriber'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['sources']) {
                    for (const source of results['sources']) {
                        await txManager.recordFactory.storeSource(source)
                    }
                }
                if (results['subaccounts']) {
                    for (const subaccount of results['subaccounts']) {
                        await txManager.recordFactory.storeSubaccount(subaccount)
                    }
                }
                if (results['income']) dashboardData['income'] = results['income']
                if (results['balance']) dashboardData['balance'] = results['balance']
                if (results['next_date']) dashboardData['next_date'] = results['next_date']
                if (results['last_date']) dashboardData['last_date'] = results['last_date']
                if (results['payments']) dashboardData['payments'] = results['payments']
                if (results['open_jobs']) dashboardData['open_jobs'] = results['open_jobs']
                if (results['next_job']) dashboardData['next_job'] = results['next_job']
                if (results['last_job']) dashboardData['last_job'] = results['last_job']
                if (results['note_count']) dashboardData['note_count'] = results['note_count']
                console.debug(`User Sync Time (ms) ${moment().diff(start)}`)
            })
            return okResult(dashboardData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSubaccounts(owner: User, client: User): Promise<Result<Subaccount[]>> {
        const start = moment()
        try {
            const result = await apiClient.post('/subaccount/sync', {
                owner: owner.id,
                client: client.id,
            })
            const results = result.data
            const subaccounts: Subaccount[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                for (const subaccount of results['subaccounts']) {
                    subaccounts.push(
                        await txManager.recordFactory.storeSubaccount(subaccount, true)
                    )
                }
                console.debug(`SYNC: Synced ${subaccounts.length} subaccounts`)
                console.debug(`Subaccounts Sync Time (ms) ${moment().diff(start)}`)
            })
            return okResult(subaccounts)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSubaccount(subaccountID: string): Promise<Result<any>> {
        const start = moment()
        try {
            const result = await apiClient.post('/subaccount/sync', { id: subaccountID })
            const results = result.data
            const dashboardData: any = {}
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['income']) dashboardData['income'] = results['income']
                if (results['balance']) dashboardData['balance'] = results['balance']
                if (results['next_date']) dashboardData['next_date'] = results['next_date']
                if (results['last_date']) dashboardData['last_date'] = results['last_date']
                if (results['payments']) dashboardData['payments'] = results['payments']
                if (results['open_jobs']) dashboardData['open_jobs'] = results['open_jobs']
                if (results['next_job']) dashboardData['next_job'] = results['next_job']
                if (results['last_job']) dashboardData['last_job'] = results['last_job']
                if (results['note_count']) dashboardData['note_count'] = results['note_count']
                console.debug(`Subaccount Sync Time (ms) ${moment().diff(start)}`)
            })
            return okResult(dashboardData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPost(post: Post): Promise<Result<string | Post>> {
        const start = moment()
        try {
            const result = await apiClient.post('/post/sync', { id: post.id })
            const results = result.data
            const newPost = await this.storePost(results['posts'][0])
            if (results['users']) {
                for (const user of results['users']) {
                    await this.storeUser(user)
                }
            }
            console.debug(`Post Sync Time (ms): ${moment().diff(start)}`)
            return okResult(newPost)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchLinks(): Promise<VoidResult> {
        const start = moment()
        try {
            const result = await apiClient.post('/link/sync')
            const results = result.data

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const link of results['links']) {
                    await txManager.recordFactory.storeLink(link)
                }
            })
            console.debug(`SYNC: Synced ${results['links'].length} links`)
            console.debug(`Links Sync Time (ms): ${moment().diff(start)}`)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPackage(aPackage: Package, type: string): Promise<VoidResult> {
        const start = moment()
        try {
            const result = await apiClient.post('/package/sync', {
                id: aPackage.id,
                type: type,
                client: aPackage.client?.id,
            })
            const results = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['package'])
                    await txManager.recordFactory.storePackage(results['package'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['customer'])
                    await txManager.recordFactory.storeUser(results['customer'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['item']) await txManager.recordFactory.storeItem(results['item'])
                if (results['payments'])
                    for (const payment of results['payments'])
                        await txManager.recordFactory.storePayment(payment)
                if (results['bookings'])
                    for (const booking of results['bookings'])
                        await txManager.recordFactory.storeBooking(booking)
                if (results['gifts'])
                    for (const gift of results['gifts'])
                        await txManager.recordFactory.storeGift(gift)
                if (results['records'])
                    for (const record of results['records'])
                        await txManager.recordFactory.storeRecord(record)
                if (results['signatures'])
                    for (const signature of results['signatures'])
                        await txManager.recordFactory.storeSignature(signature)
                console.debug(`Package Sync Time (ms): ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSubscription(subscription: Subscription, type: string): Promise<VoidResult> {
        const start = moment()
        try {
            const result = await apiClient.post('/subscription/sync', {
                id: subscription.id,
                type: type,
                client: subscription.client ? subscription.client.id : undefined,
            })
            const results = result.data
            const packageData = results['packages']
            const paymentData = results['payments']
            const giftData = results['gifts']
            const recordData = results['records']
            const signatureData = results['signatures']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['plan']) await txManager.recordFactory.storeItem(results['plan'])
                if (results['owner']) await txManager.recordFactory.storeUser(results['owner'])
                if (results['client']) await txManager.recordFactory.storeUser(results['client'])
                if (results['employee'])
                    await txManager.recordFactory.storeUser(results['employee'])
                if (results['subscription'])
                    await txManager.recordFactory.storeSubscription(results['subscription'])
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                await txManager.recordFactory.storeAll(
                    packageData,
                    txManager.recordFactory.storePackage
                )
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                await txManager.recordFactory.storeAll(giftData, txManager.recordFactory.storeGift)
                await txManager.recordFactory.storeAll(
                    recordData,
                    txManager.recordFactory.storeRecord
                )
                await txManager.recordFactory.storeAll(
                    signatureData,
                    txManager.recordFactory.storeSignature
                )
                console.debug(`Subscription Sync (ms) ${moment().diff(start)}`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchRoles(
        workflow: 'login' | 'switch' | 'register-mle' | 'copy-data'
    ): Promise<Result<User[]>> {
        try {
            const result = await apiClient.post('/role/sync', { workflow })
            const roles = result.data.roles
            const employers = result.data.employers

            const users: User[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const role of roles) {
                    users.push(await txManager.recordFactory.storeUser(role, true))
                }
                if (roles.length > 0) {
                    console.debug(`Synced ${roles.length} roles`)
                }
                for (const employer of employers) {
                    await txManager.recordFactory.storeUser(employer, false)
                }
                if (employers.length > 0) {
                    console.debug(`Synced ${employers.length} employers`)
                }
            })

            return okResult(users)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchItem(itemId: string, type: string): Promise<Result<Item | null>> {
        const start = moment()
        try {
            const result = await apiClient.post('/item/sync', {
                id: itemId,
                type: type,
            })
            const results = result.data
            const itemData = results['items']
            const userData = results['users']
            const formData = results['forms']
            const payeeData = results['payees']
            const lessonData = results['lessons']
            const contractData = results['contracts']
            const scheduleData = results['schedules']
            const instances: Item[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                instances.push(
                    ...(await Promise.all(
                        itemData.map(async (item: any) =>
                            txManager.recordFactory.storeItem(item, true)
                        )
                    ))
                )
                await txManager.recordFactory.storeAll(userData, txManager.recordFactory.storeUser)
                await txManager.recordFactory.storeAll(
                    payeeData,
                    txManager.recordFactory.storePayee
                )
                await txManager.recordFactory.storeAll(
                    lessonData,
                    txManager.recordFactory.storeClass
                )
                await txManager.recordFactory.storeAll(formData, txManager.recordFactory.storeType)
                await txManager.recordFactory.storeAll(
                    contractData,
                    txManager.recordFactory.storeContract
                )
                await txManager.recordFactory.storeAll(
                    scheduleData,
                    txManager.recordFactory.storeSchedule
                )
                console.debug(`Item Sync (ms) ${moment().diff(start)}`)
            })
            return okResult(itemId && instances.length > 0 ? instances[0] : null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchChange(change: Change): Promise<Result<Change[]>> {
        try {
            const result = await apiClient.post('/change/sync', {
                id: change.id,
            })
            const resultData = result.data
            const images = resultData['images']
            const changes = resultData['changes']
            const instances: Change[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (resultData['field'])
                    await txManager.recordFactory.storeField(resultData['field'])
                if (resultData['client'])
                    await txManager.recordFactory.storeClient(resultData['client'])
                if (resultData['person'])
                    await txManager.recordFactory.storeUser(resultData['person'])
                if (resultData['order'])
                    await txManager.recordFactory.storeOrder(resultData['order'])
                if (resultData['record'])
                    await txManager.recordFactory.storeRecord(resultData['record'])
                if (resultData['booking'])
                    await txManager.recordFactory.storeBooking(resultData['booking'])
                if (resultData['subaccount'])
                    await txManager.recordFactory.storeSubaccount(resultData['subaccount'])
                await txManager.recordFactory.storeAll(images, (image, loadFk) =>
                    txManager.recordFactory.storeImage(new RecordDataMap(image), loadFk)
                )
                for (const change of changes)
                    instances.push(await txManager.recordFactory.storeChange(change, true))
            })
            return okResult(instances)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchNumber(numberId: string): Promise<Result<PhoneNumber | null>> {
        const start = moment()
        try {
            const result = await apiClient.post('/number/sync', {
                id: numberId,
            })
            const results = result.data
            const numberData = results['numbers']
            const contactData = results['contacts']
            const instances: PhoneNumber[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const number of numberData)
                    instances.push(await txManager.recordFactory.storeNumber(number, true))
                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                console.debug(`Number Sync (ms) ${moment().diff(start)}`)
            })
            return okResult(numberId && instances.length > 0 ? instances[0] : null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchIndustry(
        industryId: string
    ): Promise<Result<{ colors: RecordDataMap[]; images: RecordDataMap[]; industry: Industry }>> {
        const start = moment()
        try {
            const result = await apiClient.post('/industry/sync', {
                id: industryId,
            })
            const results = result.data
            const industryData = results['industries']
            const instances: Industry[] = []
            const colors: RecordDataMap[] = []
            const images: RecordDataMap[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const industry of industryData)
                    instances.push(await txManager.recordFactory.storeIndustry(industry, true))
                for (const color of results['colors']) colors.push(new RecordDataMap(color))
                for (const image of results['images']) images.push(new RecordDataMap(image))
                console.debug(
                    `Industry Sync Time: ${moment().diff(start)} synced ${colors.length} colors, ${images.length} images`
                )
            })
            return okResult({
                colors: colors,
                images: images,
                industry: instances[0],
            })
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCategory(categoryId: string): Promise<Result<Category | null>> {
        const start = moment()
        try {
            const result = await apiClient.post('/category/sync', {
                id: categoryId,
            })
            const results = result.data
            const categoryData = results['categories']
            const itemData = results['items']
            const instances: Category[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const category of categoryData)
                    instances.push(await txManager.recordFactory.storeCategory(category, true))
                await txManager.recordFactory.storeAll(itemData, txManager.recordFactory.storeItem)
                console.debug(`Category Sync (ms) ${moment().diff(start)}`)
            })
            return okResult(categoryId && instances.length > 0 ? instances[0] : null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchNotes(person: User, filter: string): Promise<Result<Note[]>> {
        try {
            const result = await apiClient.post('/notes', {
                person: person.id,
                filter: filter,
            })
            const resultData = result.data
            const notes: Note[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                notes.push(
                    ...(await Promise.all(
                        resultData.map(
                            async (n: Note) => await txManager.recordFactory.storeNote(n, true)
                        )
                    ))
                )
            })
            return okResult(notes)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBookingNotes(bookingId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/notes', {
                booking: bookingId,
            })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeNote
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchLocationNotes(locationId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/notes', {
                location: locationId,
            })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeNote
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchEstimateNotes(estimateId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/notes', {
                estimate: estimateId,
            })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeNote
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchOrderNotes(orderId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/notes', {
                order: orderId,
            })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeNote
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSubaccountNotes(subaccountId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.post('/notes', {
                subaccount: subaccountId,
            })
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeNote
                )
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchDeposit(depositId: string): Promise<VoidResult> {
        try {
            const result = await apiClient.get(`/deposit/${depositId}/sync`)
            const results = result.data
            const depositData = results['deposit']
            const userData = results['contacts']
            const clientData = results['clients']
            const paymentData = results['payments']

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeDeposit(depositData, false)
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                await txManager.recordFactory.storeAll(userData, txManager.recordFactory.storeUser)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
            })

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async loadIndustries(): Promise<Industry[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('industries')
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('name')
        sql.fk = Industry.fk
        const items = await this.executor.executeSql(sql)
        const industries: Industry[] = []
        for (const item of items) {
            industries.push(new Industry(JSON.parse(item['jsonData'])))
        }
        return industries
    }

    async loadFeatures(features?: string[]): Promise<Feature[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('features')
        if (features && features.length > 0) {
            sql.filterBy(`id in (${features.map(() => '?').join(',')})`, ...features)
        }
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('CAST(seqnum as integer)')
        sql.orderBy('name')
        sql.fk = Feature.fk
        const items = await this.executor.executeSql(sql)
        const results: Feature[] = []
        for (const item of items) {
            results.push(new Feature(JSON.parse(item['jsonData'])))
        }
        return results
    }

    async loadLocations(): Promise<Location[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('locations')
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('name')
        sql.fk = Location.fk
        const items = await this.executor.executeSql(sql)
        const locations: Location[] = []
        for (const item of items) {
            locations.push(new Location(JSON.parse(item['jsonData'])))
        }
        return locations
    }

    async loadBookings(): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('bookings')
        sql.orderBy('date')
        sql.fk = Booking.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: Booking[] = []
        for (const item of dbItems) {
            results.push(new Booking(JSON.parse(item['jsonData'])))
        }
        return results
    }

    async loadContacts(): Promise<Contact[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('contacts')
        sql.orderBy('NAME')
        sql.fk = Contract.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: Contact[] = []
        for (const item of dbItems) {
            results.push(new Contact(JSON.parse(item['jsonData'])))
        }
        return results
    }

    async loadSources(): Promise<Source[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('sources')
        sql.filterBy('owner = ?', PSApplication.me().id!)
        sql.orderBy('createdDate', true)
        sql.fk = Source.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: Source[] = []
        for (const item of dbItems) {
            const source = new Source(JSON.parse(item['jsonData']))
            if (!source.isBnpl) {
                results.push(source)
            }
        }
        return results
    }

    async loadClientSources(owner: string): Promise<Source[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('sources')
        if (PSApplication.role() === User.PERM_ROLE_ADMIN) {
            const employer = PSApplication.me().owner!.id!
            sql.filterBy(
                'owner = ? and (creator = ? OR creator IS NULL OR EXISTS(select 1 from users where ((employer=? and users.role is not null) or users.id=?) and users.id=creator))',
                owner,
                PSApplication.me().id!,
                employer,
                employer
            )
        }
        else {
            sql.filterBy(
                'owner = ? and (creator = ? OR creator IS NULL OR EXISTS(select 1 from users where employer=? and users.role is not null and users.id=creator))',
                owner,
                PSApplication.me().id!,
                PSApplication.me().id!
            )
        }
        sql.orderBy('createdDate', true)
        sql.fk = Source.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: Source[] = []
        for (const item of dbItems) {
            const source = new Source(JSON.parse(item['jsonData']))
            if (!source.isBnpl) {
                results.push(source)
            }
        }
        return results
    }

    async loadAllSources(): Promise<Source[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('sources')
        sql.orderBy('createdDate', true)
        sql.fk = Source.fk
        const dbItems = await this.executor.executeSql(sql)
        return dbItems.map((item: any) => new Source(JSON.parse(item['jsonData'])))
    }

    async loadEvents(): Promise<PSEvent[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('events')
        const device = await Device.currentDevice()
        if (device?.id) {
            sql.filterBy('device=?', device.id)
        }
        sql.orderBy('DATE')
        sql.fk = PSEvent.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: PSEvent[] = []
        for (const item of dbItems) {
            results.push(new PSEvent(JSON.parse(item['jsonData'])))
        }
        return results
    }

    async loadUsers(type: PERSON_TYPE): Promise<User[]> {
        return ComputeFactory.getInstance().fetchUsers(type, undefined, [])
    }

    private loadChannelsFromDBItems(dbItems: any[]): Channel[] {
        const results: Channel[] = []
        for (const item of dbItems) {
            results.push(new Channel(JSON.parse(item['jsonData'])))
        }
        return results
    }

    async loadOnboardingChannels(): Promise<Channel[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('channels')
        sql.filterBy('COALESCE(onboarding,0) > ?', 0)
        sql.orderBy('onboarding')
        sql.fk = Channel.fk
        const dbItems = await this.executor.executeSql(sql)
        return this.loadChannelsFromDBItems(dbItems)
    }

    async loadLeadSourceChannels(): Promise<Channel[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('channels')
        sql.filterBy('leadSource = ?', true)
        sql.orderBy('sortRank')
        sql.fk = Channel.fk
        const dbItems = await this.executor.executeSql(sql)
        return this.loadChannelsFromDBItems(dbItems)
    }

    async loadChannel(idString: string): Promise<Channel | undefined> {
        if (!idString) return undefined
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('channels')
        sql.filterBy('id = ?', idString)
        sql.fk = Channel.fk
        const object = await this.executor.executeSingleRowQuery(sql)
        if (object) return new Channel(JSON.parse(object['jsonData']))
        return undefined
    }

    // In Java this is loadFields, translated to loadFieldsOwner since loadFields already exists
    async loadFieldsOwner(owner: string): Promise<Field[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('owner')
        sql.addField('record')
        sql.addField('jsonData')
        sql.addTable('fields')
        sql.filterBy('fields.owner = ?', owner)
        sql.filterBy('fields.record IS NULL')
        sql.filterBy('fields.cancelledDate IS NULL')
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Field.fk
        const items = await this.executor.executeSql(sql)
        const results: Field[] = []
        for (const item of items) {
            const field = new Field(JSON.parse(item['jsonData']))
            results.push(field)
            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name!, field.owner!.id!, item['record']),
                field.type
            )
        }
        return results
    }

    async loadAllFields(): Promise<Field[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('owner')
        sql.addField('record')
        sql.addField('jsonData')
        sql.addTable('fields')
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Field.fk
        const items = await this.executor.executeSql(sql)
        const results: Field[] = []
        for (const item of items) {
            const field = new Field(JSON.parse(item['jsonData']))
            results.push(field)
            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name!, field.owner!.id!, item['record']),
                field.type
            )
        }
        return results
    }

    async loadRecordFields(type: string, is_public: boolean): Promise<Field[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('owner')
        sql.addField('record')
        sql.addField('jsonData')
        sql.addTable('fields')
        sql.filterBy('fields.record = ?', type)
        sql.filterBy('fields.cancelledDate IS NULL')
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Field.fk
        if (is_public) sql.filterBy('fields.public = ? ', '1')
        const items = await this.executor.executeSql(sql)
        const results: Field[] = []
        for (const item of items) {
            const field = new Field(JSON.parse(item['jsonData']))
            results.push(field)
            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name!, field.owner!.id!, item['record']),
                field.type
            )
        }
        return results
    }

    async loadBookingFields(owner: string, client: boolean, publicOnly: boolean): Promise<Field[]> {
        return this.loadFields(
            owner,
            undefined,
            client,
            true,
            false,
            false,
            false,
            false,
            publicOnly,
            true
        )
    }

    async loadClientFields(owner: string, publicOnly: boolean): Promise<Field[]> {
        return this.loadFields(
            owner,
            undefined,
            true,
            false,
            false,
            false,
            false,
            false,
            publicOnly,
            true
        )
    }

    async loadOrderFields(owner: string, client: boolean, publicOnly: boolean): Promise<Field[]> {
        return this.loadFields(
            owner,
            undefined,
            client,
            false,
            true,
            false,
            false,
            false,
            publicOnly,
            true
        )
    }

    async loadSubaccountFields(owner: string, publicOnly: boolean): Promise<Field[]> {
        return this.loadFields(
            owner,
            undefined,
            false,
            false,
            false,
            true,
            false,
            false,
            publicOnly,
            true
        )
    }

    async loadLoationFields(owner: string, publicOnly: boolean): Promise<Field[]> {
        return this.loadFields(
            owner,
            undefined,
            false,
            false,
            false,
            false,
            true,
            false,
            publicOnly,
            true
        )
    }

    async loadFields(
        owner: string,
        type: string | undefined,
        client: boolean,
        booking: boolean,
        order: boolean,
        subaccount: boolean,
        location: boolean,
        other: boolean,
        publicOnly: boolean,
        customOnly: boolean
    ): Promise<Field[]> {
        const results: Field[] = []
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('owner')
        sql.addField('jsonData')
        sql.addTable('fields')
        sql.filterBy('fields.owner = ?', owner)
        sql.filterBy('fields.record IS NULL')
        sql.filterBy('fields.cancelledDate IS NULL')
        sql.orderBy('CAST(seqnum as integer)')
        if (type) sql.filterBy('fields.type = ? ', type)
        if (client) sql.filterBy('fields.client = ? ', true)
        if (booking) sql.filterBy('fields.booking = ? ', true)
        if (location) sql.filterBy('fields.location = ? ', true)
        if (subaccount) sql.filterBy('fields.subaccount = ? ', true)
        if (order) sql.filterBy('fields."order" = ? ', true)
        if (publicOnly) {
            sql.filterBy('fields.public = ? ', true)
            if (other) sql.filterBy('fields.checkoutOther = ? ', true)
            if (order) sql.filterBy('fields.checkoutOrder = ? ', true)
            if (client) sql.filterBy('fields.checkoutClient = ? ', true)
            if (booking) sql.filterBy('fields.checkoutBooking = ? ', true)
            if (location) sql.filterBy('fields.checkoutLocation = ? ', true)
        }
        if (customOnly) sql.filterBy('fields.standardField IS NULL')
        if (PSApplication.me().perm === User.PERM_ROLE_PARTNER)
            sql.filterBy(
                "(fields.standardField IS NULL OR fields.standardField NOT LIKE 'subaccount.%')"
            )
        sql.fk = Field.fk
        const items = await this.executor.executeSql(sql)
        for (const item of items) {
            const field = new Field(JSON.parse(item['jsonData']))
            results.push(field)

            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name || '', field.owner!.id!, item['record']),
                field.type
            )
        }
        return results
    }

    async loadItems(
        owner: string,
        type?: string | string[],
        category?: Category,
        forCalendarSync = false
    ): Promise<Item[]> {
        const results: Item[] = []
        const sql = new SQLBuilder()
        sql.addField('items.jsonData')
        sql.addTable('items')
        sql.filterBy('items.owner = ?', owner)
        sql.filterBy('items.cancelledDate IS NULL')
        if (type && typeof type === 'string') sql.filterBy('items.type = ?', type)
        else if (type && Array.isArray(type))
            sql.filterBy(`items.type IN (${type.map(() => '?').join(', ')})`, ...type)
        if (forCalendarSync) {
            const role = PSApplication.role()
            if (
                role === User.PERM_ROLE_EMPLOYEE ||
                role === User.PERM_ROLE_CONTRACTOR ||
                type !== 'reservation'
            )
                return results
            sql.filterBy('items.startDate is not null')
        }
        if (category) {
            sql.addField('listings.seqnum', 'seqnum')
            sql.addTable('listings', 'listings.item=items.id')
            if (category.id) sql.filterBy('listings.category = ?', category.id)
            sql.orderBy('CAST(listings.seqnum as integer)')
        }
        else sql.orderBy('CAST(items.seqnum as integer)')
        sql.fk = Item.fk
        const dbItems = await this.executor.executeSql(sql)

        for (const dbItem of dbItems) {
            const map = new RecordDataMap(JSON.parse(dbItem['jsonData']))
            if (category) map.put('seqnum', dbItem['seqnum'])
            const item = new Item(map as any)
            results.push(item)
        }
        return results
    }

    async loadLinks(): Promise<PSLink[]> {
        const results: PSLink[] = []
        const sql = new SQLBuilder()
        sql.addField('links.jsonData')
        sql.addTable('links')
        sql.filterBy('links.owner=?', PSApplication.me().owner!.id!)
        sql.filterBy('links.cancelledDate IS NULL')
        sql.orderBy('CAST(links.seqnum as integer)')
        sql.fk = PSLink.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new PSLink(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadCategories(ownerString: string, type?: string | string[]): Promise<Category[]> {
        if (!type) type = 'service'
        const results: Category[] = []
        const sql = new SQLBuilder()
        sql.addField('categories.jsonData')
        sql.addTable('categories')
        if (type && typeof type === 'string') sql.filterBy('categories.type = ?', type)
        else if (type && Array.isArray(type))
            sql.filterBy(`categories.type IN (${type.map(() => '?').join(', ')})`, ...type)
        sql.filterBy('categories.owner = ?', ownerString)
        sql.filterBy('categories.cancelledDate IS NULL')
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Category.fk
        const dbItems = await this.executor.executeSql(sql)

        for (const dbItem of dbItems) {
            const item = new Category(JSON.parse(dbItem['jsonData']))
            results.push(item)
        }
        return results
    }

    async loadClients(): Promise<User[]> {
        const results: User[] = []
        const sql = new SQLBuilder()
        sql.addField('users.jsonData')
        sql.addField('clients.jsonData', 'typeData')
        sql.addTable('clients')
        sql.addTable('users', 'clients.person=users.id')
        sql.filterBy('clients.cancelledDate IS NULL')
        const me = PSApplication.me()
        sql.filterBy('clients.owner = ?', me.owner!.id!)
        if (me.payee && me.perm === User.PERM_ROLE_EMPLOYEE) {
            const payee = await this.fetchPayee(me.id!, false)
            if (payee?.showClients)
                sql.filterBy('(clients.employee = ? OR clients.employee IS NULL)', me.id!)
        }
        else if (
            me.payee &&
            (me.perm === User.PERM_ROLE_CONTRACTOR || me.perm === User.PERM_ROLE_EMPLOYEE)
        )
            sql.filterBy('clients.employee = ?', me.id!)
        sql.fk = User.fk
        const l = await this.executor.executeSql(sql)
        for (const item of l) {
            const user = new User(JSON.parse(item['jsonData']))
            user.subtype = new Client(JSON.parse(item['typeData']))
            results.push(user)
        }
        return results
    }

    async loadPayees(type: PAYEE_TYPE): Promise<User[]> {
        const results: User[] = []
        const sql = new SQLBuilder()
        sql.addField('users.jsonData')
        sql.addField('payees.jsonData', 'typeData')
        sql.addTable('payees')
        sql.addTable('users', 'payees.person=users.id')
        sql.filterBy('payees.cancelledDate IS NULL')
        const me = PSApplication.me()
        if (me.client) {
            sql.filterBy('users.role = ?', 'owner') // super admins only
            sql.filterBy('payees.owner = ?', me.id!)
        }
        else {
            const owner = me.owner!
            sql.filterBy('payees.owner = ?', owner.id!)
            if (type === PAYEE_TYPE.PAYEE_TYPE_STAFF_ONLY) {
                sql.filterBy('users.role = ?', 'employee')
                if (owner.company) sql.filterBy('users.employer = ?', owner.id!)
            }
            else if (type === PAYEE_TYPE.PAYEE_TYPE_PICKUP_STAFF_ONLY) {
                sql.filterBy('payees.allowPickup = ?', true)
                if (owner.company) sql.filterBy('users.employer = ?', owner.id!)
            }
            else if (type === PAYEE_TYPE.PAYEE_TYPE_ANY_EMPLOYEE) {
                if (owner.company) {
                    sql.filterBy('(users.employer = ? OR users.id = ?)', owner.id!, owner.id!)
                    sql.filterBy(
                        '(users.role IN (?, ?) OR users.id = ?)',
                        'admin',
                        'employee',
                        owner.id!
                    )
                }
                else sql.filterBy('users.role IN (?, ?)', 'admin', 'employee')
            }
            else if (type === PAYEE_TYPE.PAYEE_TYPE_ANY_EMPLOYEE_OR_CONTRACTOR) {
                if (owner.company) {
                    sql.filterBy('(users.employer = ? OR users.id = ?)', owner.id!, owner.id!)
                    sql.filterBy(
                        '(users.role IN (?, ?, ?) OR users.id = ?)',
                        'admin',
                        'employee',
                        'contractor',
                        owner.id!
                    )
                }
                else sql.filterBy('users.role IN (?, ?, ?)', 'admin', 'employee', 'contractor')
            }
            sql.filterBy('users.id != ?', POCKETSUITE_USER_ID)
            if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) sql.filterBy('users.id = ?', me.id!)
        }
        sql.orderBy('users.name')
        sql.fk = User.fk
        const l = await this.executor.executeSql(sql)
        for (const item of l) {
            const user = new User(JSON.parse(item['jsonData']))
            if (type === PAYEE_TYPE.PAYEE_TYPE_BOOKABLE_ONLINE && !(user.sp && user.allowWidget))
                continue
            user.subtype = new Payee(JSON.parse(item['typeData']))
            results.push(user)
        }
        return results
    }

    async loadSubaccounts(client: User): Promise<Subaccount[]> {
        const results: Subaccount[] = []
        const sql = new SQLBuilder()
        sql.addField('subaccounts.jsonData', 'typeData')
        sql.addTable('subaccounts')
        sql.filterBy('subaccounts.cancelledDate IS NULL')
        const me = PSApplication.me()
        sql.filterBy('subaccounts.owner = ?', me.owner!.id!)
        if (me.payee && me.perm === User.PERM_ROLE_EMPLOYEE) {
            const payee = await this.fetchPayee(me.id!, false)
            if (payee?.showClients)
                sql.filterBy('(subaccounts.employee = ? OR subaccounts.employee IS NULL)', me.id!)
        }
        else if (
            me.payee &&
            (me.perm === User.PERM_ROLE_CONTRACTOR || me.perm === User.PERM_ROLE_EMPLOYEE)
        )
            sql.filterBy('subaccounts.employee = ?', me.id!)
        if (client) sql.filterBy('subaccounts.client = ?', client.id!)
        sql.fk = Subaccount.fk
        const l = await this.executor.executeSql(sql)
        for (const item of l) {
            const subaccount = new Subaccount(JSON.parse(item['jsonData']))
            results.push(subaccount)
        }
        return results
    }

    async loadSubscribers(): Promise<User[]> {
        const results: User[] = []
        const sql = new SQLBuilder()
        sql.addField('users.jsonData')
        sql.addField('subscribers.jsonData', 'typeData')
        sql.addTable('subscribers')
        sql.addTable('users', 'subscribers.person=users.id')
        sql.filterBy('subscribers.cancelledDate IS NULL')
        const me = PSApplication.me()
        sql.filterBy('subscribers.owner = ?', me.owner!.id!)
        sql.fk = User.fk
        const l = await this.executor.executeSql(sql)
        for (const item of l) {
            const user = new User(JSON.parse(item['jsonData']))
            user.subtype = new Subscriber(JSON.parse(item['typeData']))
            results.push(user)
        }
        return results
    }

    async loadClasses(): Promise<Lesson[]> {
        const results: Lesson[] = []
        const me = PSApplication.me().id!
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('lessons')
        sql.filterBy('owner = ? OR employee = ?', me, me)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('date')
        sql.fk = Lesson.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) {
            const item = new Lesson(JSON.parse(dbItem['jsonData']))
            results.push(item)
        }
        return results
    }

    async loadContracts(): Promise<Contract[]> {
        const results: Contract[] = []
        const sql = new SQLBuilder()
        sql.addField('contracts.jsonData')
        sql.addTable('contracts')
        sql.filterBy('contracts.owner=?', PSApplication.me().owner!.id!)
        sql.filterBy('contracts.cancelledDate IS NULL')
        sql.fk = Contract.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new Contract(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadNumbers(): Promise<PhoneNumber[]> {
        const results: PhoneNumber[] = []
        const sql = new SQLBuilder()
        sql.addField('numbers.jsonData')
        sql.addTable('numbers')
        sql.filterBy('numbers.owner=?', PSApplication.me().id!)
        sql.filterBy('numbers.cancelledDate IS NULL')
        sql.fk = PhoneNumber.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new PhoneNumber(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadReaders(): Promise<Reader[]> {
        const results: Reader[] = []
        const sql = new SQLBuilder()
        sql.addField('readers.jsonData')
        sql.addTable('readers')
        sql.filterBy('readers.owner=?', PSApplication.me().owner!.id!)
        sql.filterBy('readers.cancelledDate IS NULL')
        sql.orderBy('createdDate', true)
        sql.fk = Reader.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new Reader(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadTypes(ownerString: string): Promise<Type[]> {
        const results: Type[] = []
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addField(
            '(select count(*) from fields where record=types.id and cancelledDate IS NULL)',
            'field_count'
        )
        sql.addTable('types')
        sql.filterBy('types.owner=?', ownerString)
        sql.filterBy('types.cancelledDate IS NULL')
        sql.orderBy('createdDate')
        sql.fk = Type.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) {
            const fieldCount = dbItem['field_count']
            const item = new Type(JSON.parse(dbItem['jsonData']), fieldCount)
            results.push(item)
        }
        return results
    }

    async loadTemplates(): Promise<Template[]> {
        const results: Template[] = []
        const sql = new SQLBuilder()
        sql.addField('templates.jsonData')
        sql.addTable('templates')
        sql.filterBy('templates.owner=?', PSApplication.me().owner!.id!)
        sql.filterBy('templates.cancelledDate IS NULL')
        sql.fk = Template.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new Template(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadGoals(): Promise<Goal[]> {
        const results: Goal[] = []
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('goals')
        sql.orderBy('CAST(seqnum as integer)')
        sql.fk = Goal.fk
        const dbItems = await this.executor.executeSql(sql)
        for (const dbItem of dbItems) results.push(new Goal(JSON.parse(dbItem['jsonData'])))
        return results
    }

    async loadBalances(): Promise<Balance[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('balances')
        sql.orderBy('eventDate', true)
        sql.orderBy('type', true)
        sql.fk = Balance.fk
        const results = await this.executor.executeSql(sql)
        const balanceArray: Balance[] = []
        for (const balanceData of results) {
            balanceArray.push(new Balance(JSON.parse(balanceData['jsonData'])))
        }
        return balanceArray
    }

    async loadEstimate(idString: string): Promise<Estimate | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Estimate)
    }

    async loadBooking(idString: string): Promise<Booking | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Booking)
    }

    async loadOrder(idString: string): Promise<Order | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Order)
    }

    async loadRoyalty(idString: string): Promise<Royalty | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Royalty)
    }

    async loadInvoice(idString: string): Promise<Invoice | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Invoice)
    }

    async loadPayment(idString: string): Promise<Payment | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Payment)
    }

    async loadCharge(idString: string): Promise<Charge | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Charge)
    }

    async loadDeposit(idString: string): Promise<Deposit | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Deposit)
    }

    async loadSource(idString: string): Promise<Source | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Source)
    }

    async loadUser(idString: string): Promise<User | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, User)
    }

    async loadPost(idString: string): Promise<Post | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Post)
    }

    async loadTeam(idString: string): Promise<Team | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Team)
    }

    async loadConference(idString: string): Promise<Conference | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Conference)
    }

    async loadTCR(idString: string): Promise<TCR | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, TCR)
    }

    async loadNumber(idString: string): Promise<PhoneNumber | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, PhoneNumber)
    }

    async loadReview(idString: string): Promise<Review | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Review)
    }

    async loadReader(idString: string): Promise<Reader | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Reader)
    }

    async loadLink(idString: string): Promise<PSLink | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, PSLink)
    }

    async loadNote(idString: string): Promise<Note | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Note)
    }

    async loadTask(idString: string): Promise<Task | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Task)
    }

    async loadType(idString: string): Promise<Type | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Type)
    }

    async loadTemplate(idString: string): Promise<Template | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Template)
    }

    async loadTrigger(idString: string): Promise<Trigger | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Trigger)
    }

    async loadCampaign(idString: string): Promise<Campaign | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Campaign)
    }

    async loadItem(idString: string): Promise<Item | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Item)
    }

    async loadCategory(idString: string): Promise<Category | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Category)
    }

    async loadDevice(idString: string): Promise<Device | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Device)
    }

    async loadRecord(idString: string): Promise<PSRecord | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, PSRecord)
    }

    async loadField(idString: string): Promise<Field | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Field)
    }

    async loadFeature(idString: string): Promise<Feature | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Feature)
    }

    async loadLocation(idString: string): Promise<Location | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Location)
    }

    async fetchLocation(idOrInstanceString: string, copy: boolean): Promise<Location | undefined> {
        if (!idOrInstanceString) return undefined
        let item: Location | undefined = undefined
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('locations')
        sql.filterBy('instance=?', idOrInstanceString)
        sql.fk = Location.fk
        const object = await this.executor.executeSingleRowQuery(sql)
        if (object) {
            if (copy) {
                item = new Location(JSON.parse(object['jsonData']))
            }
            else item = await this.fetchRecord(object['id'], Location)
        }
        else {
            const obj = await this.fetchRecord(idOrInstanceString, Location)
            if (obj instanceof Location) item = obj
        }
        return item
    }

    async loadMessage(idString: string): Promise<Message | undefined> {
        if (!idString) return undefined
        if (idString.length <= 25) {
            let item: Message | undefined = undefined
            const sql = new SQLBuilder()
            sql.addField('jsonData')
            sql.addTable('messages')
            sql.filterBy('uri=?', idString)
            sql.fk = Message.fk
            const object = await this.executor.executeSingleRowQuery(sql)
            if (object != null) {
                item = new Message(JSON.parse(object['jsonData']))
            }
            return item
        }
        else return this.fetchRecord(idString, Message)
    }

    async loadSchedule(idString: string): Promise<Schedule | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Schedule)
    }

    async loadClient(idOrPersonString: string): Promise<Client | undefined> {
        return this.fetchClient(idOrPersonString, true)
    }

    async fetchClient(idOrPersonString: string, copy: boolean): Promise<Client | undefined> {
        if (!idOrPersonString) return undefined
        let item: Client | undefined = undefined
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('clients')
        sql.filterBy('person=?', idOrPersonString)
        sql.fk = Client.fk
        const object = await this.executor.executeSingleRowQuery(sql)
        if (object != null) {
            if (copy) {
                item = new Client(JSON.parse(object['jsonData']))
            }
            else item = await this.fetchRecord(object['id'], Client)
        }
        else {
            const obj = await this.fetchRecord(idOrPersonString, Client)
            if (obj instanceof Client) item = obj
        }
        return item
    }

    async loadSubaccount(idString: string): Promise<Subaccount | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Subaccount)
    }

    async loadPayee(idOrPersonString: string): Promise<Payee | undefined> {
        return this.fetchPayee(idOrPersonString, true)
    }

    async fetchPayee(idOrPersonString: string, copy: boolean): Promise<Payee | undefined> {
        if (!idOrPersonString) return undefined
        let item: Payee | undefined = undefined
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('payees')
        sql.filterBy('person=?', idOrPersonString)
        sql.fk = Payee.fk
        const object = await this.executor.executeSingleRowQuery(sql)
        if (object != undefined) {
            if (copy) {
                item = new Payee(JSON.parse(object['jsonData']))
            }
            else item = await this.fetchRecord(object['id'], Payee)
        }
        else {
            const obj = await this.fetchRecord(idOrPersonString, Payee)
            if (obj instanceof Payee) item = obj
        }
        return item
    }

    async loadSubscriber(personString: string): Promise<Subscriber | undefined> {
        return this.fetchSubscriber(personString, true)
    }

    async fetchSubscriber(personString: string, copy: boolean): Promise<Subscriber | undefined> {
        if (!personString) return undefined
        let item: Subscriber | undefined = undefined
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('subscribers')
        sql.filterBy('person=?', personString)
        sql.fk = Subscriber.fk
        const object = await this.executor.executeSingleRowQuery(sql)
        if (object != undefined) {
            if (copy) {
                item = new Subscriber(JSON.parse(object['jsonData']))
            }
            else item = await this.fetchRecord(object['id'], Subscriber)
        }
        return item
    }

    async loadEvent(idString: string): Promise<PSEvent | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, PSEvent)
    }

    async loadClass(idString: string): Promise<Lesson | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Lesson)
    }

    async loadGift(idString: string): Promise<Gift | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Gift)
    }

    async loadPackage(idString: string): Promise<Package | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Package)
    }

    async loadSubscription(idString: string): Promise<Subscription | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Subscription)
    }

    async loadContract(idString: string): Promise<Contract | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Contract)
    }

    async loadSignature(idString: string): Promise<Signature | undefined> {
        if (!idString) return undefined
        return this.fetchRecord(idString, Signature)
    }

    async loadDashboard(idString: string): Promise<Dashboard | undefined> {
        let item: Dashboard | undefined = undefined
        if (idString) {
            const sql = new SQLBuilder()
            sql.addField('jsonData')
            sql.addTable('dashboards')
            sql.filterBy('id=?', idString)
            sql.fk = Dashboard.fk
            const object = await this.executor.executeSingleRowQuery(sql)
            if (object != undefined) item = new Dashboard(JSON.parse(object['jsonData']))
        }
        return item
    }

    async loadJob(id: string): Promise<Job | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, Job)
    }

    async loadBlock(id: string): Promise<Block | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, Block)
    }

    async loadCredentials(provider: string): Promise<PSCredential[]> {
        return this.loadCredentialsWithFilter((c: PSCredential) => c.provider === provider)
    }

    async loadCredentialsWithFilter(filter: (c: PSCredential) => boolean): Promise<PSCredential[]> {
        const sql = new SQLBuilder()
        sql.addField('credentials.jsonData')
        sql.addTable('credentials')
        sql.filterBy('credentials.cancelledDate IS NULL')
        sql.orderBy('credentials.createdDate', false)
        sql.fk = PSCredential.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: PSCredential[] = []
        for (const dbItem of dbItems) {
            const item = new PSCredential(JSON.parse(dbItem['jsonData']))
            if (filter(item)) {
                results.push(item)
            }
        }
        return results
    }

    async loadCredential(id: string): Promise<PSCredential | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, PSCredential)
    }

    async loadCalendars(): Promise<Calendar[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('calendars')
        sql.orderBy('createdDate')
        sql.fk = Calendar.fk
        const dbItems = await this.executor.executeSql(sql)
        const results: Calendar[] = []
        for (const dbItem of dbItems) {
            results.push(new Calendar(JSON.parse(dbItem['jsonData'])))
        }
        return results
    }

    async loadCalendar(id: string): Promise<Calendar | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, Calendar)
    }

    async loadEdition(id: string): Promise<Edition | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, Edition)
    }

    async storeEstimate<LoadFKST extends boolean>(
        estimate: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Estimate : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO estimates(id, date, status, client, vendor, creator, employee, total, deposit, invoicedDate, scheduledDate, paidDate, paidDepositDate, markPaidDate, confirmedDate, cancelledDate, completedDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                estimate.id,
                estimate.date,
                estimate.status,
                estimate.client,
                estimate.vendor,
                estimate.creator,
                estimate.employee,
                estimate.total,
                estimate.deposit,
                estimate.invoiced_date,
                estimate.scheduled_date,
                estimate.paid_date,
                estimate.paid_deposit_date,
                estimate.mark_paid_date,
                estimate.confirmed_date,
                estimate.cancelled_date,
                estimate.completed_date,
                estimate.last_modified_date,
                JSON.stringify(estimate),
                estimate.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, estimate.id, 'estimate'),
            this.executor.executeUpdate('DELETE from lines where trandoc = ?', estimate.id),
        ])
        if (estimate.lines)
            await Promise.all(
                estimate.lines.map(async (line: any) =>
                    this.executor.executeUpdate(
                        'REPLACE INTO lines(id, seqnum, trandoc, type, total, amount, category, item, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                        line.id,
                        line.seqnum,
                        estimate.id,
                        line.type,
                        line.total,
                        line.amount,
                        line.category,
                        line.item,
                        line.last_modified_date,
                        JSON.stringify(line),
                        line.created_date
                    )
                )
            )

        CacheFactory.getInstance().removeRecord(Estimate.dataType(), estimate)
        await Field.storeFieldData(this, new RecordDataMap(estimate), estimate.owner, undefined)
        return loadFks ? this.fetchRecord(estimate.id, Estimate) : estimate.id
    }

    async storeIndustry<LoadFKST extends boolean>(
        industry: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Industry : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO industries(id, name, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                industry.id,
                industry.name,
                industry.cancelled_date,
                JSON.stringify(industry),
                industry.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, industry.id, 'industry'),
        ])
        CacheFactory.getInstance().removeRecord(Industry.dataType(), industry)
        return loadFks ? this.fetchRecord(industry.id, Industry) : industry.id
    }

    async storeFeature(feature: any, loadFks = true): Promise<Feature | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO features(id, name, seqnum, parent, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                feature.id,
                feature.name,
                feature.seqnum,
                feature.parent,
                feature.cancelled_date,
                JSON.stringify(feature),
                feature.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, feature.id, 'feature'),
        ])
        CacheFactory.getInstance().removeRecord(Feature.dataType(), feature)
        return loadFks ? this.fetchRecord(feature.id, Feature) : feature.id
    }

    async storeLocation(location: any, loadFks = true): Promise<Location | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO locations(id, owner, instance, name, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                location.id,
                location.owner,
                location.instance,
                location.name,
                location.cancelled_date,
                JSON.stringify(location),
                location.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, location.id, 'location'),
        ])
        CacheFactory.getInstance().removeRecord(Location.dataType(), location)
        return loadFks ? this.fetchRecord(location.id, Location) : location.id
    }

    async storeSignature<LoadFKST extends boolean>(
        signature: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Signature : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO signatures(id, owner, person, creator, employee, contract, signedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                signature.id,
                signature.owner,
                signature.person,
                signature.creator,
                signature.employee,
                signature.contract,
                signature.signed_date,
                signature.cancelled_date,
                JSON.stringify(signature),
                signature.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, signature.id, 'signature'),
        ])
        CacheFactory.getInstance().removeRecord(Signature.dataType(), signature)
        return loadFks ? this.fetchRecord(signature.id, Signature) : signature.id
    }

    async storeContract<LoadFKST extends boolean>(
        contract: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Contract : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO contracts(id, owner, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                contract.id,
                contract.owner,
                contract.cancelled_date,
                JSON.stringify(contract),
                contract.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, contract.id, 'contract'),
        ])
        CacheFactory.getInstance().removeRecord(Contract.dataType(), contract)
        return loadFks ? this.fetchRecord(contract.id, Contract) : contract.id
    }

    async storeSchedule(schedule: any, loadFks = true): Promise<Schedule | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO schedules(id, owner, item, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                schedule.id,
                schedule.owner,
                schedule.item,
                JSON.stringify(schedule),
                schedule.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, schedule.id, 'schedule'),
        ])
        CacheFactory.getInstance().removeRecord(Schedule.dataType(), schedule)
        return loadFks ? this.fetchRecord(schedule.id, Schedule) : schedule.id
    }

    async storeSubscription<LoadFKST extends boolean>(
        subscription: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Subscription : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO subscriptions(id, client, plan, vendor, owner, gift, creator, employee, amount, total, date, nextInvoiceDate, confirmedDate, cancelledDate, completedDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                subscription.id,
                subscription.client,
                subscription.plan,
                subscription.vendor,
                subscription.owner,
                subscription.gift,
                subscription.creator,
                subscription.employee,
                subscription.amount,
                subscription.total,
                subscription.date,
                subscription.next_invoice_date,
                subscription.confirmed_date,
                subscription.cancelled_date,
                subscription.completed_date,
                subscription.last_modified_date,
                JSON.stringify(subscription),
                subscription.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, subscription.id, 'subscription'),
        ])
        CacheFactory.getInstance().removeRecord(Subscription.dataType(), subscription)
        return loadFks ? this.fetchRecord(subscription.id, Subscription) : subscription.id
    }

    async storeGift<LoadFKST extends boolean>(
        gift: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Gift : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO gifts(id, owner, vendor, creator, employee, item, client, total, customer, paidDate, markPaidDate, expiredDate, completedDate, cancelledDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                gift.id,
                gift.owner,
                gift.owner,
                gift.creator,
                gift.employee,
                gift.item,
                gift.client,
                gift.total,
                gift.customer,
                gift.paid_date,
                gift.mark_paid_date,
                gift.expired_date,
                gift.completed_date,
                gift.cancelled_date,
                gift.last_modified_date,
                JSON.stringify(gift),
                gift.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, gift.id, 'gift'),
        ])
        CacheFactory.getInstance().removeRecord(Gift.dataType(), gift)
        return loadFks ? this.fetchRecord(gift.id, Gift) : gift.id
    }

    async storePackage<LoadFKST extends boolean>(
        aPackage: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Package : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO packages(id, owner, vendor, creator, employee, customer, gift, item, client, total, capacity, subscription, paidDate, markPaidDate, expiredDate, completedDate, cancelledDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                aPackage.id,
                aPackage.owner,
                aPackage.owner,
                aPackage.creator,
                aPackage.employee,
                aPackage.customer,
                aPackage.gift,
                aPackage.item,
                aPackage.client,
                aPackage.total,
                aPackage.capacity,
                aPackage.subscription,
                aPackage.paid_date,
                aPackage.mark_paid_date,
                aPackage.expired_date,
                aPackage.completed_date,
                aPackage.cancelled_date,
                aPackage.last_modified_date,
                JSON.stringify(aPackage),
                aPackage.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, aPackage.id, 'package'),
        ])
        CacheFactory.getInstance().removeRecord(Package.dataType(), aPackage)
        return loadFks ? this.fetchRecord(aPackage.id, Package) : aPackage.id
    }

    async storeClass<LoadFKST extends boolean>(
        lesson: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Lesson : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO lessons(id, date, item, owner, creator, employee, schedule, cancelledDate, completedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                lesson.id,
                lesson.date,
                lesson.item,
                lesson.owner,
                lesson.creator,
                lesson.employee,
                lesson.schedule,
                lesson.cancelled_date,
                lesson.completed_date,
                JSON.stringify(lesson),
                lesson.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, lesson.id, 'lesson'),
        ])
        if (lesson.schedule_instance) await this.storeSchedule(lesson.schedule_instance)
        CacheFactory.getInstance().removeRecord(Lesson.dataType(), lesson)

        if (
            !lesson.cancelled_date &&
            (PSApplication.role() > User.PERM_ROLE_EMPLOYEE ||
                PSApplication.userId() === lesson.employee?.id)
        )
            await this.executor.executeUpdate(
                'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                lesson.id
            )
        else await this.executor.executeUpdate('DELETE from calendar_keys where id = ?', lesson.id)
        return loadFks ? this.fetchRecord(lesson.id, Lesson) : lesson.id
    }

    async storeItem<LoadFKST extends boolean>(
        item: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Item : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO items(id, name, type, seqnum, duration, owner, isPublic, packageAll, startDate, endDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                item.id,
                item.name,
                item.type,
                item.seqnum,
                item.duration,
                item.owner,
                item.is_public,
                item.package_all,
                item.start_date,
                item.end_date,
                item.cancelled_date,
                JSON.stringify(item),
                item.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, item.id, 'item'),
        ])

        if ('package' === item.type) {
            await this.executor.executeUpdate('DELETE from item_components where item = ?', item.id)
            if (item.components)
                await Promise.all(
                    item.components.map(async (component: any) =>
                        this.executor.executeUpdate(
                            'REPLACE INTO item_components(item, component, capacity, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, CURRENT_TIMESTAMP)',
                            item.id,
                            component.component,
                            component.capacity,
                            item.created_date
                        )
                    )
                )
        }
        if (['reservation', 'class', 'service'].includes(item.type)) {
            await this.executor.executeUpdate('DELETE from addons where item = ?', item.id)
            if (item.addons)
                await Promise.all(
                    item.addons.map(async (addon: any) =>
                        this.executor.executeUpdate(
                            'REPLACE INTO addons(item, addon, createdDate, lastSyncDate) VALUES (?, ?, ?, CURRENT_TIMESTAMP)',
                            item.id,
                            addon,
                            item.created_date
                        )
                    )
                )
        }
        CacheFactory.getInstance().removeRecord(Item.dataType(), item)
        if (!item.cancelled_date && 'reservation' === item.type && !!item.start_date)
            await this.executor.executeUpdate(
                'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                item.id
            )
        else await this.executor.executeUpdate('DELETE from calendar_keys where id = ?', item.id)
        return loadFks ? this.fetchRecord(item.id, Item) : item.id
    }

    async storeResource(resource: any, loadFks = true): Promise<Resource | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO resources(id, owner, seqnum, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                resource.id,
                resource.owner,
                resource.seqnum,
                resource.cancelled_date,
                JSON.stringify(resource),
                resource.created_date
            ),
            this.executor.executeUpdate(
                'REPLACE INTO keys(id, type) VALUES (?, ?)',
                resource.id,
                'resource'
            ),
        ])
        CacheFactory.getInstance().removeRecord(Resource.dataType(), resource)
        return loadFks ? this.fetchRecord(resource.id, Resource) : resource.id
    }

    async storeRequirement(requirement: any, loadFks = true): Promise<Requirement | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO requirements(id, owner, seqnum, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                requirement.id,
                requirement.owner,
                requirement.seqnum,
                requirement.cancelled_date,
                JSON.stringify(requirement),
                requirement.created_date
            ),
            this.executor.executeUpdate(
                'REPLACE INTO keys(id, type) VALUES (?, ?)',
                requirement.id,
                'requirement'
            ),
        ])

        this.executor.executeUpdate(
            'DELETE from allocations where booking IS NULL and requirement = ?',
            requirement.id
        )
        if (requirement.resources) {
            for (const resourceId of requirement.resources) {
                await this.executor.executeUpdate(
                    'REPLACE INTO allocations(requirement, resource, lastModifiedDate, createdDate) VALUES (?, ?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)',
                    requirement.id,
                    resourceId
                )
            }
        }
        CacheFactory.getInstance().removeRecord(Requirement.dataType(), requirement)
        return loadFks ? this.fetchRecord(requirement.id, Requirement) : requirement.id
    }

    async storeCategory<LoadFKST extends boolean>(
        category: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Category : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO categories(id, type, name, seqnum, owner, isPublic, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                category.id,
                category.type,
                category.name,
                category.seqnum,
                category.owner,
                category.isPublic,
                category.cancelled_date,
                JSON.stringify(category),
                category.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, category.id, 'category'),
        ])

        await this.executor.executeUpdate('DELETE from listings where category = ?', category.id)
        if (category.listings) {
            await Promise.all(
                category.listings.map(async (listing: any, i: number) =>
                    this.executor.executeUpdate(
                        'REPLACE INTO listings(category, item, seqnum, lastSyncDate) VALUES (?, ?, ?, CURRENT_TIMESTAMP)',
                        category.id,
                        listing,
                        i + 1
                    )
                )
            )
        }

        CacheFactory.getInstance().removeRecord(Category.dataType(), category)
        return loadFks ? this.fetchRecord(category.id, Category) : category.id
    }

    async storeDevice<LoadFKST extends boolean>(
        device: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Device : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO devices(id, owner, device, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                device.id,
                device.owner,
                device.device,
                JSON.stringify(device),
                device.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, device.id, 'device'),
        ])
        CacheFactory.getInstance().removeRecord(Device.dataType(), device)
        return loadFks ? this.fetchRecord(device.id, Device) : device.id
    }

    async storeImage<LoadFKST extends boolean>(
        image: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Image : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO images(id, owner, name, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                image.id,
                image.owner,
                image.name,
                JSON.stringify(image),
                image.created_date,
                image.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, image.id, 'image'),
        ])
        CacheFactory.getInstance().removeRecord(Image.dataType(), image)
        return loadFks ? this.fetchRecord(image.id, Image) : image.id
    }

    async storeChange<LoadFKST extends boolean>(
        change: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Change : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO changes(id, owner, person, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                change.id,
                change.owner,
                change.person,
                JSON.stringify(change),
                change.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, change.id, 'change'),
        ])
        CacheFactory.getInstance().removeRecord(Change.dataType(), change)
        return loadFks ? this.fetchRecord(change.id, Change) : change.id
    }

    async storeField<LoadFKST extends boolean>(
        field: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Field : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO fields(id, standardField, service, seqnum, owner, record, type, client, "order", booking, location, subaccount, public, checkoutOther, checkoutClient, checkoutOrder, checkoutBooking, checkoutLocation, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                field.id,
                field.standard_field,
                field.service,
                field.seqnum,
                field.owner,
                field.record,
                field.type,
                field.is_client,
                field.is_order,
                field.is_booking,
                field.is_location,
                field.is_subaccount,
                field.is_public,
                field.checkout_other,
                field.checkout_client,
                field.checkout_order,
                field.checkout_booking,
                field.checkout_location,
                field.cancelled_date,
                JSON.stringify(field),
                field.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, field.id, 'field'),
        ])
        CacheFactory.getInstance().removeRecord(Field.dataType(), field)
        if (!field.cancelled_date) {
            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.name, field.owner, field.record),
                field.type
            )
        }
        return loadFks ? this.fetchRecord(field.id, Field) : field.id
    }

    async storeRecord<LoadFKST extends boolean>(
        record: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? PSRecord : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO records(id, name, owner, person, creator, employee, type, completedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                record.id,
                record.name,
                record.owner,
                record.person,
                record.creator,
                record.employee,
                record.type,
                record.completed_date,
                record.cancelled_date,
                JSON.stringify(record),
                record.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, record.id, 'record'),
        ])
        CacheFactory.getInstance().removeRecord(PSRecord.dataType(), record)
        await Field.storeFieldData(this, new RecordDataMap(record), record.owner, record.type)
        return loadFks ? this.fetchRecord(record.id, PSRecord) : record.id
    }

    async storeType<LoadFKST extends boolean>(
        type: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Type : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO types(id, owner, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                type.id,
                type.owner,
                type.cancelled_date,
                JSON.stringify(type),
                type.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, type.id, 'type'),
        ])
        CacheFactory.getInstance().removeRecord(Type.dataType(), type)
        return loadFks ? this.fetchRecord(type.id, Type) : type.id
    }

    async storeTrigger<LoadFKST extends boolean>(
        trigger: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Trigger : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO triggers(id, owner, type, item, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                trigger.id,
                trigger.owner,
                trigger.type,
                trigger.item,
                trigger.cancelled_date,
                JSON.stringify(trigger),
                trigger.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, trigger.id, 'trigger'),
        ])
        CacheFactory.getInstance().removeRecord(Trigger.dataType(), trigger)
        return loadFks ? this.fetchRecord(trigger.id, Trigger) : trigger.id
    }

    async storeEdition<LoadFKST extends boolean>(
        edition: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Edition : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO editions(id, owner, name, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                edition.id,
                edition.owner,
                edition.name,
                edition.cancelled_date,
                JSON.stringify(edition),
                edition.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, edition.id, 'edition'),
        ])
        CacheFactory.getInstance().removeRecord(Edition.dataType(), edition)
        return loadFks ? this.fetchRecord(edition.id, Edition) : edition.id
    }

    async storePromotion<LoadFKST extends boolean>(
        promotion: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Promotion : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO promotions(id, owner, client, item, trigger, expiredDate, completedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                promotion.id,
                promotion.owner,
                promotion.client,
                promotion.item,
                promotion.trigger,
                promotion.expired_date,
                promotion.completed_date,
                promotion.cancelled_date,
                JSON.stringify(promotion),
                promotion.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, promotion.id, 'promotion'),
        ])
        CacheFactory.getInstance().removeRecord(Promotion.dataType(), promotion)
        return loadFks ? this.fetchRecord(promotion.id, Promotion) : promotion.id
    }

    async storeTemplate<LoadFKST extends boolean>(
        template: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Template : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO templates(id, owner, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                template.id,
                template.owner,
                template.cancelled_date,
                JSON.stringify(template),
                template.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, template.id, 'template'),
        ])
        CacheFactory.getInstance().removeRecord(Template.dataType(), template)
        return loadFks ? this.fetchRecord(template.id, Template) : template.id
    }

    async storeCampaign<LoadFKST extends boolean>(
        campaign: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Campaign : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO campaigns(id, owner, creator, team, person, date, completedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                campaign.id,
                campaign.owner,
                campaign.creator,
                campaign.team,
                campaign.person,
                campaign.date,
                campaign.completed_date,
                campaign.cancelled_date,
                JSON.stringify(campaign),
                campaign.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, campaign.id, 'campaign'),
        ])
        CacheFactory.getInstance().removeRecord(Campaign.dataType(), campaign)
        return loadFks ? this.fetchRecord(campaign.id, Campaign) : campaign.id
    }

    async storeMessage<LoadFKST extends boolean>(
        message: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Message : string> {
        if (message.channel) {
            await Promise.all([
                this.executor.executeUpdate(
                    'REPLACE INTO messages(id, owner, type, thread, record, uri, sender, subtype, viewedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                    message.id,
                    message.owner,
                    message.type,
                    message.thread,
                    message.record,
                    message.uri,
                    message.sender,
                    message.subtype === '' ? null : message.subtype,
                    message.viewed_date,
                    message.cancelled_date,
                    JSON.stringify(message),
                    message.created_date
                ),
                this.executor.executeUpdate(KEY_SQL, message.id, 'message'),
            ])
        }
        CacheFactory.getInstance().removeRecord(Message.dataType(), message)
        return loadFks ? this.fetchRecord(message.id, Message) : message.id
    }

    async storeBalance<LoadFKST extends boolean>(
        balance: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? string : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO balances(id, amount, type, deposit, payment, jsonData, createdDate, eventDate, cancelledDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                balance.id,
                balance.amount,
                balance.type,
                balance.deposit,
                balance.payment,
                JSON.stringify(balance),
                balance.created_date,
                balance.event_date,
                balance.cancelled_date,
                balance.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, balance.id, 'balance'),
        ])
        CacheFactory.getInstance().removeRecord(Balance.dataType(), balance)
        return loadFks ? this.fetchRecord(balance.id, Balance) : balance.id
    }

    async storeChannel(channel: any, loadFks = true): Promise<Channel | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO channels(id, name, industry, leadSource, onboarding, sortRank, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                channel.id,
                channel.name,
                channel.industry,
                channel.lead_source,
                channel.onboarding,
                channel.sortRank,
                JSON.stringify(channel),
                channel.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, channel.id, 'channel'),
        ])
        CacheFactory.getInstance().removeRecord(Channel.dataType(), channel)
        return loadFks ? this.fetchRecord(channel.id, Channel) : channel.id
    }

    async storeLink<LoadFKST extends boolean>(
        link: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? PSLink : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO links(id, owner, seqnum, jsonData, createdDate, cancelledDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                link.id,
                link.owner,
                link.seqnum,
                JSON.stringify(link),
                link.created_date,
                link.cancelled_date,
                link.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, link.id, 'link'),
        ])
        CacheFactory.getInstance().removeRecord(PSLink.dataType(), link)
        return loadFks ? this.fetchRecord(link.id, PSLink) : link.id
    }

    async storeNote<LoadFKST extends boolean>(
        note: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Note : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO notes(id, owner, person, booking, "order", estimate, subaccount, location, jsonData, createdDate, cancelledDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                note.id,
                note.owner,
                note.person,
                note.booking,
                note.order,
                note.estimate,
                note.subaccount,
                note.location,
                JSON.stringify(note),
                note.created_date,
                note.cancelled_date,
                note.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, note.id, 'note'),
        ])
        CacheFactory.getInstance().removeRecord(Note.dataType(), note)
        return loadFks ? this.fetchRecord(note.id, Note) : note.id
    }

    async storeTask<LoadFKST extends boolean>(
        task: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Task : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO tasks(id, date, person, owner, employee, title, jsonData, createdDate, cancelledDate, completedDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                task.id,
                task.date,
                task.person,
                task.owner,
                task.employee,
                task.title,
                JSON.stringify(task),
                task.created_date,
                task.cancelled_date,
                task.completed_date,
                task.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, task.id, 'task'),
        ])
        CacheFactory.getInstance().removeRecord(Task.dataType(), task)
        return loadFks ? this.fetchRecord(task.id, Task) : task.id
    }

    async storeUser<LoadFKST extends boolean>(
        user: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? User : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO users(id, name, e164, email, type, employer, role, industry, externalId, firstName, lastName, signupDate, featurecap, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                user.id,
                user.name,
                user.e164,
                user.email,
                user.type,
                user.employer,
                user.role,
                user.industry,
                user.external_id,
                user.first_name,
                user.last_name,
                user.signup_date,
                user.featurecap,
                JSON.stringify(user),
                user.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, user.id, 'user'),
        ])
        CacheFactory.getInstance().removeRecord(User.dataType(), user)
        return loadFks ? this.fetchRecord(user.id, User) : user.id
    }

    async storeAlias<LoadFKST extends boolean>(
        alias: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Alias : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO aliases(id, owner, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                alias.id,
                alias.owner,
                JSON.stringify(alias),
                alias.created_date,
                alias.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, alias.id, 'alias'),
        ])
        CacheFactory.getInstance().removeRecord(Alias.dataType(), alias)
        return loadFks ? this.fetchRecord(alias.id, Alias) : alias.id
    }

    async storePost<LoadFKST extends boolean>(
        post: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Post : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO posts(id, owner, archivedDate, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                post.id,
                post.owner,
                post.archived_date,
                post.cancelled_date,
                JSON.stringify(post),
                post.created_date,
                post.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, post.id, 'post'),
        ])
        CacheFactory.getInstance().removeRecord(Post.dataType(), post)
        return loadFks ? this.fetchRecord(post.id, Post) : post.id
    }

    async storeFeed<LoadFKST extends boolean>(
        feed: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Feed : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO feeds(id, owner, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                feed.id,
                feed.owner,
                feed.cancelled_date,
                JSON.stringify(feed),
                feed.created_date,
                feed.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, feed.id, 'feed'),
        ])
        CacheFactory.getInstance().removeRecord(Feed.dataType(), feed)
        return loadFks ? this.fetchRecord(feed.id, Feed) : feed.id
    }

    async storeSubscriber<LoadFKST extends boolean>(
        subscriber: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Subscriber : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO subscribers(id, owner, person, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                subscriber.id,
                subscriber.owner,
                subscriber.person,
                subscriber.cancelled_date,
                JSON.stringify(subscriber),
                subscriber.created_date,
                subscriber.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, subscriber.id, 'subscriber'),
        ])
        CacheFactory.getInstance().removeRecord(Subscriber.dataType(), subscriber)
        return loadFks ? this.fetchRecord(subscriber.id, Subscriber) : subscriber.id
    }

    async storeGoal(goal: any, loadFks = true) {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO goals(id, name, seqnum, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                goal.id,
                goal.name,
                goal.seqnum,
                JSON.stringify(goal),
                goal.created_date,
                goal.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, goal.id, 'goal'),
        ])

        CacheFactory.getInstance().removeRecord(Goal.dataType(), goal)
        return loadFks ? this.fetchRecord(goal.id, Goal) : goal.id
    }

    async storeTeam<LoadFKST extends boolean>(
        team: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Team : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO teams(id, owner, name, bcc, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                team.id,
                team.owner,
                team.name,
                team.bcc,
                team.cancelled_date,
                JSON.stringify(team),
                team.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, team.id, 'team'),
        ])
        if (team.members) {
            await this.executor.executeUpdate('DELETE FROM members where team = ?', team.id)
            await Promise.all(
                team.members.map(async (member: any) =>
                    this.executor.executeUpdate(
                        'INSERT INTO members(team, person, createdDate, cancelledDate, lastSyncDate) VALUES (?, ?, ?, ?, CURRENT_TIMESTAMP)',
                        team.id,
                        member.person,
                        member.created_date,
                        member.cancelled_date
                    )
                )
            )
        }
        CacheFactory.getInstance().removeRecord(Team.dataType(), team)
        return loadFks ? this.fetchRecord(team.id, Team) : team.id
    }

    async storeConference<LoadFKST extends boolean>(
        conference: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Conference : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO conferences(id, owner, refnum, completedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                conference.id,
                conference.owner,
                conference.refnum,
                conference.completed_date,
                conference.cancelled_date,
                JSON.stringify(conference),
                conference.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, conference.id, 'conference'),
        ])
        CacheFactory.getInstance().removeRecord(Conference.dataType(), conference)
        return loadFks ? this.fetchRecord(conference.id, Conference) : conference.id
    }

    async storeTCR(tcr: any, loadFks = true): Promise<TCR | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO tcrs(id, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, CURRENT_TIMESTAMP)',
                tcr.id,
                JSON.stringify(tcr),
                tcr.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, tcr.id, 'tcr'),
        ])
        CacheFactory.getInstance().removeRecord(TCR.dataType(), tcr)
        return loadFks ? this.fetchRecord(tcr.id, TCR) : tcr.id
    }

    async storeReview(review: any, loadFks = true): Promise<Review | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO reviews(id, booking, "order", jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                review.id,
                review.booking,
                review.order,
                JSON.stringify(review),
                review.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, review.id, 'review'),
        ])
        CacheFactory.getInstance().removeRecord(Review.dataType(), review)
        return loadFks ? this.fetchRecord(review.id, Review) : review.id
    }

    async storeNumber<LoadFKST extends boolean>(
        number: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? PhoneNumber : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO numbers(id, owner, name, person, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                number.id,
                number.owner,
                number.name,
                number.person,
                number.cancelled_date,
                JSON.stringify(number),
                number.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, number.id, 'number'),
        ])
        CacheFactory.getInstance().removeRecord(PhoneNumber.dataType(), number)
        return loadFks ? this.fetchRecord(number.id, PhoneNumber) : number.id
    }

    async storeReader<LoadFKST extends boolean>(
        reader: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Reader : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO readers(id, owner, serialNumber, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                reader.id,
                reader.owner,
                reader.serialNumber,
                reader.cancelled_date,
                JSON.stringify(reader),
                reader.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, reader.id, 'reader'),
        ])
        CacheFactory.getInstance().removeRecord(Reader.dataType(), reader)
        return loadFks ? this.fetchRecord(reader.id, Reader) : reader.id
    }

    async storeTaxcode<LoadFKST extends boolean>(
        taxcode: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Taxcode : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO taxcodes(id, name, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, CURRENT_TIMESTAMP)',
                taxcode.id,
                taxcode.name,
                JSON.stringify(taxcode),
                taxcode.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, taxcode.id, 'taxcode'),
        ])
        CacheFactory.getInstance().removeRecord(Taxcode.dataType(), taxcode)
        return loadFks ? this.fetchRecord(taxcode.id, Taxcode) : taxcode.id
    }

    async storeSource<LoadFKST extends boolean>(
        source: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Source : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO sources(id, name, owner, creator, instant, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                source.id,
                source.name,
                source.owner,
                source.creator,
                source.instant,
                source.cancelled_date,
                JSON.stringify(source),
                source.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, source.id, 'source'),
        ])
        CacheFactory.getInstance().removeRecord(Source.dataType(), source)
        return loadFks ? this.fetchRecord(source.id, Source) : source.id
    }

    async storeDeposit(deposit: any, loadFks = true): Promise<Deposit | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO deposits(id, source, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                deposit.id,
                deposit.source,
                deposit.cancelled_date,
                JSON.stringify(deposit),
                deposit.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, deposit.id, 'deposit'),
        ])
        CacheFactory.getInstance().removeRecord(Deposit.dataType(), deposit)
        return loadFks ? this.fetchRecord(deposit.id, Deposit) : deposit.id
    }

    async storePayment<LoadFKST extends boolean>(
        payment: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Payment : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO payments(id, client, vendor, creator, employee, source, package, booking, invoice, deposit, subscription, type, amount, depositedDate, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                payment.id,
                payment.client,
                payment.vendor,
                payment.creator,
                payment.employee,
                payment.source,
                payment.package,
                payment.booking,
                payment.invoice,
                payment.deposit,
                payment.subscription,
                payment.type,
                payment.amount,
                payment.deposited_date,
                payment.cancelled_date,
                JSON.stringify(payment),
                payment.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, payment.id, 'payment'),
        ])
        if (payment.balances) await this.storeAll(payment.balances, this.storeBalance)
        CacheFactory.getInstance().removeRecord(Payment.dataType(), payment)
        return loadFks ? this.fetchRecord(payment.id, Payment) : payment.id
    }

    async storeCharge<LoadFKST extends boolean>(
        charge: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Charge : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO charges(id, client, vendor, creator, cancelledDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                charge.id,
                charge.client,
                charge.vendor,
                charge.creator,
                charge.cancelled_date,
                JSON.stringify(charge),
                charge.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, charge.id, 'charge'),
        ])
        CacheFactory.getInstance().removeRecord(Charge.dataType(), charge)
        return loadFks ? this.fetchRecord(charge.id, Charge) : charge.id
    }

    async storeInvoice<LoadFKST extends boolean>(
        invoice: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Invoice : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO invoices(id, date, client, vendor, creator, employee, booking, schedule, gift, type, total, paidDate, markPaidDate, nextInvoiceDate, cancelledDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                invoice.id,
                invoice.date,
                invoice.client,
                invoice.vendor,
                invoice.creator,
                invoice.employee,
                invoice.booking,
                invoice.schedule,
                invoice.gift,
                invoice.type,
                invoice.total,
                invoice.paid_date,
                invoice.mark_paid_date,
                invoice.next_invoice_date,
                invoice.cancelled_date,
                invoice.last_modified_date,
                JSON.stringify(invoice),
                invoice.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, invoice.id, 'invoice'),
            this.executor.executeUpdate('DELETE from lines where trandoc = ?', invoice.id),
        ])
        if (invoice.lines)
            await Promise.all(
                invoice.lines.map(async (line: any) =>
                    this.executor.executeUpdate(
                        'REPLACE INTO lines(id, seqnum, trandoc, type, total, amount, category, item, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                        line.id,
                        line.seqnum,
                        invoice.id,
                        line.type,
                        line.total,
                        line.amount,
                        line.category,
                        line.item,
                        line.last_modified_date,
                        JSON.stringify(line),
                        line.created_date
                    )
                )
            )
        CacheFactory.getInstance().removeRecord(Invoice.dataType(), invoice)
        return loadFks ? this.fetchRecord(invoice.id, Invoice) : invoice.id
    }

    async storeBooking<LoadFKST extends boolean>(
        booking: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Booking : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO bookings(id, date, endDate, type, status, client, vendor, source, schedule, gift, total, deposit, duration, number, creator, employee, lesson, package, item, location, latitude, longitude, invoicedDate, paidDate, paidDepositDate, markPaidDate, cancelledDate, confirmedDate, completedDate, rebookDate, checkinDate, packageCancelledDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                booking.id,
                booking.date,
                booking.end_date,
                booking.type,
                booking.status,
                booking.client,
                booking.vendor === '' ? null : booking.vendor,
                booking.source === '' ? null : booking.source,
                booking.schedule,
                booking.gift,
                booking.total,
                booking.deposit,
                booking.duration,
                booking.number,
                booking.creator,
                booking.employee,
                booking.lesson === '' ? null : booking.lesson,
                booking.package === '' ? null : booking.package,
                booking.item === '' ? null : booking.item,
                booking.location,
                booking.latitude,
                booking.longitude,
                booking.invoiced_date,
                booking.paid_date,
                booking.paid_deposit_date,
                booking.mark_paid_date,
                booking.cancelled_date,
                booking.confirmed_date,
                booking.completed_date,
                booking.rebook_date,
                booking.checkin_date,
                booking.package_cancelled_date,
                booking.last_modified_date,
                JSON.stringify(booking),
                booking.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, booking.id, 'booking'),
            this.executor.executeUpdate('DELETE FROM jobs where booking = ?', booking.id),
            this.executor.executeUpdate('DELETE from lines where trandoc = ?', booking.id),
        ])
        let isMine = false
        if (booking.jobs) {
            await this.storeAll(booking.jobs, this.storeJob)
            for (const job of booking.jobs) {
                isMine ||=
                    PSApplication.role() <= User.PERM_ROLE_EMPLOYEE &&
                    PSApplication.userId() === job.person
            }
        }
        if (booking.lines) {
            const lines = booking.lines
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i]
                await this.executor.executeUpdate(
                    'REPLACE INTO lines(id, seqnum, trandoc, type, total, amount, category, item, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                    line.id,
                    line.seqnum,
                    booking.id,
                    line.type,
                    line.total,
                    line.amount,
                    line.category,
                    line.item,
                    line.last_modified_date,
                    JSON.stringify(line),
                    line.created_date
                )
            }
        }
        if (booking.schedule_instance) await this.storeSchedule(booking.schedule_instance)
        CacheFactory.getInstance().removeRecord(Booking.dataType(), booking)
        await Field.storeFieldData(this, new RecordDataMap(booking), booking.owner, undefined)
        if (booking.confirmedDate && !booking.cancelledDate) {
            if (
                (PSApplication.role() == User.PERM_ROLE_CLIENT &&
                    PSApplication.userId() === booking.client) ||
                (PSApplication.role() <= User.PERM_ROLE_EMPLOYEE && isMine) ||
                (PSApplication.role() > User.PERM_ROLE_EMPLOYEE && !booking.lesson)
            )
                await this.executor.executeUpdate(
                    'REPLACE INTO calendar_keys(id, lastSyncDate) VALUES (?, CURRENT_TIMESTAMP)',
                    booking.id
                )
        }
        else {
            await this.executor.executeUpdate('DELETE from calendar_keys where id = ?', booking.id)
        }
        return loadFks ? this.fetchRecord(booking.id, Booking) : booking.id
    }

    async storeOrder<LoadFKST extends boolean>(
        order: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Order : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO orders(id, type, date, endDate, status, client, vendor, source, gift, total, duration, number, creator, employee, location, latitude, longitude, invoicedDate, paidDate, markPaidDate, cancelledDate, confirmedDate, completedDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                order.id,
                order.type,
                order.date,
                order.end_date,
                order.status,
                order.client,
                order.vendor,
                order.source,
                order.gift,
                order.total,
                order.duration,
                order.number,
                order.creator,
                order.employee,
                order.location,
                order.latitude,
                order.longitude,
                order.invoiced_date,
                order.paid_date,
                order.mark_paid_date,
                order.cancelled_date,
                order.confirmed_date,
                order.completed_date,
                order.last_modified_date,
                JSON.stringify(order),
                order.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, order.id, 'order'),
            this.executor.executeUpdate('DELETE from lines where trandoc = ?', order.id),
        ])
        if (order.lines)
            await Promise.all(
                order.lines.map(async (line: any) =>
                    this.executor.executeUpdate(
                        'REPLACE INTO lines(id, seqnum, trandoc, type, total, amount, category, item, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                        line.id,
                        line.seqnum,
                        order.id,
                        line.type,
                        line.total,
                        line.amount,
                        line.category,
                        line.item,
                        line.last_modified_date,
                        JSON.stringify(line),
                        line.created_date
                    )
                )
            )

        CacheFactory.getInstance().removeRecord(Order.dataType(), order)
        await Field.storeFieldData(this, new RecordDataMap(order), order.owner, undefined)
        return loadFks ? this.fetchRecord(order.id, Order) : order.id
    }

    async storeRoyalty<LoadFKST extends boolean>(
        royalty: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Royalty : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO royalties(id, date, client, owner, total, creator, location, paidDate, markPaidDate, cancelledDate, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                royalty.id,
                royalty.date,
                royalty.client,
                royalty.owner,
                royalty.total,
                royalty.creator,
                royalty.location,
                royalty.paid_date,
                royalty.mark_paid_date,
                royalty.cancelled_date,
                royalty.last_modified_date,
                JSON.stringify(royalty),
                royalty.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, royalty.id, 'royalty'),
            this.executor.executeUpdate('DELETE from lines where trandoc = ?', royalty.id),
        ])

        if (royalty.lines) {
            await Promise.all(
                royalty.lines.map(async (line: any) =>
                    this.executor.executeUpdate(
                        'REPLACE INTO lines(id, seqnum, trandoc, type, total, amount, category, item, lastModifiedDate, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                        line.id,
                        line.seqnum,
                        royalty.id,
                        line.type,
                        line.total,
                        line.amount,
                        line.category,
                        line.item,
                        line.last_modified_date,
                        JSON.stringify(line),
                        line.created_date
                    )
                )
            )
        }
        CacheFactory.getInstance().removeRecord(Royalty.dataType(), royalty)
        return loadFks ? this.fetchRecord(royalty.id, Royalty) : royalty.id
    }

    async storeJob(job: any, loadFks = true): Promise<Job> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO jobs(id, person, owner, type, availableDate, booking, lesson, amount, date, checkoutDate, paidDate, markPaidDate, checkinDate, confirmedDate, jsonData, lastModifiedDate, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                job.id,
                job.person,
                job.owner,
                job.type,
                job.available_date,
                job.booking,
                job.lesson,
                job.amount,
                job.date,
                job.checkout_date,
                job.paid_date,
                job.mark_paid_date,
                job.checkin_date,
                job.confirmed_date,
                JSON.stringify(job),
                job.last_modified_date,
                job.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, job.id, 'job'),
        ])
        CacheFactory.getInstance().removeRecord(Job.dataType(), job)
        return loadFks ? this.fetchRecord(job.id, Job) : new Job(job)
    }

    async storeEvent(event: any, loadFks = true): Promise<PSEvent | string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO events(id, owner, date, endDate, title, device, cancelledDate, importedDate, jsonData, createdDate, lastModifiedDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
                event.id,
                event.owner,
                event.date,
                event.end_date,
                event.title,
                event.device,
                event.cancelled_date,
                event.imported_date,
                JSON.stringify(event),
                event.created_date,
                event.modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, event.id, 'event'),
        ])
        CacheFactory.getInstance().removeRecord(PSEvent.dataType(), event)
        return loadFks ? this.fetchRecord(event.id, PSEvent) : event.id
    }

    async storeContact<LoadFKST extends boolean>(
        contact: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Contact : string> {
        await Promise.all([
            // todo this doesn't look right but android and ios is doing it this way, we should set lastSyncDate here maybe and created date off the json?
            this.executor.executeUpdate(
                'REPLACE INTO contacts(id, name, e164, email, jsonData, createdDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                contact.id,
                contact.name,
                contact.e164,
                contact.email,
                JSON.stringify(contact)
            ),
            this.executor.executeUpdate(KEY_SQL, contact.id, 'contact'),
        ])
        CacheFactory.getInstance().removeRecord(Contact.dataType(), contact)
        return loadFks ? this.fetchRecord(contact.id, Contact) : contact.id
    }

    async storePayee<LoadFKST extends boolean>(
        payee: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Payee : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO payees(id, person, owner, allowPickup, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                payee.id,
                payee.person,
                payee.owner,
                payee.allow_pickup,
                payee.cancelled_date,
                JSON.stringify(payee),
                payee.created_date,
                payee.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, payee.id, 'payee'),
        ])
        CacheFactory.getInstance().removeRecord(Payee.dataType(), payee)
        return loadFks ? this.fetchRecord(payee.id, Payee) : payee.id
    }

    async storeClient<LoadFKST extends boolean>(
        client: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Client : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO clients(id, person, owner, creator, employee, channel, referer, cost, firstPaymentDate, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                client.id,
                client.person,
                client.owner,
                client.creator,
                client.employee === '' ? null : client.employee,
                client.channel,
                client.referer,
                client.cost,
                client.first_payment_date,
                client.cancelled_date,
                JSON.stringify(client),
                client.created_date,
                client.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, client.id, 'client'),
        ])
        CacheFactory.getInstance().removeRecord(Client.dataType(), client)
        return loadFks ? this.fetchRecord(client.id, Client) : client.id
    }

    async storeSubaccount<LoadFKST extends boolean>(
        subaccount: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Subaccount : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO subaccounts(id, client, owner, creator, employee, name, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                subaccount.id,
                subaccount.client,
                subaccount.owner,
                subaccount.creator,
                subaccount.employee,
                subaccount.name,
                subaccount.cancelled_date,
                JSON.stringify(subaccount),
                subaccount.created_date,
                subaccount.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, subaccount.id, 'subaccount'),
        ])
        CacheFactory.getInstance().removeRecord(Subaccount.dataType(), subaccount)
        await Field.storeFieldData(this, new RecordDataMap(subaccount), subaccount.owner, undefined)
        return loadFks ? this.fetchRecord(subaccount.id, Subaccount) : subaccount.id
    }

    async storeBlock<LoadFKST extends boolean>(
        block: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Block : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO blocks(id, owner, person, resource, date, endDate, duration, title, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                block.id,
                block.owner,
                block.person,
                block.resource,
                block.date,
                block.end_date,
                block.duration,
                block.title,
                block.cancelled_date,
                JSON.stringify(block),
                block.created_date,
                block.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, block.id, 'block'),
        ])
        if (block.schedule_instance) await this.storeSchedule(block.schedule_instance)
        CacheFactory.getInstance().removeRecord(Block.dataType(), block)
        return loadFks ? this.fetchRecord(block.id, Block) : block.id
    }

    async storeAdjustment<LoadFKST extends boolean>(
        adjustment: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Adjustment : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO adjustments(id, item, owner, creator, total, paidDate, cancelledDate, jsonData, createdDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                adjustment.id,
                adjustment.item,
                adjustment.owner,
                adjustment.creator,
                adjustment.total,
                adjustment.paid_date,
                adjustment.cancelled_date,
                JSON.stringify(adjustment),
                adjustment.created_date,
                adjustment.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, adjustment.id, 'adjustment'),
        ])
        CacheFactory.getInstance().removeRecord(Adjustment.dataType(), adjustment)
        return loadFks ? this.fetchRecord(adjustment.id, Adjustment) : adjustment.id
    }

    async storeDashboard<LoadFKST extends boolean>(
        dashboard: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Dashboard : string> {
        await this.executor.executeUpdate(
            'REPLACE INTO dashboards(id, title, status, count, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
            dashboard.id,
            dashboard.title,
            dashboard.status,
            dashboard.count,
            JSON.stringify(dashboard),
            dashboard.created_date
        )
        CacheFactory.getInstance().removeRecord(Dashboard.dataType(), dashboard)
        return loadFks ? this.fetchRecord(dashboard.id, Dashboard) : dashboard.id
    }

    async storeCredential<LoadFKST extends boolean>(
        credential: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? PSCredential : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO credentials(id, jsonData, createdDate, cancelledDate, lastSyncDate) VALUES (?, ?, ?, ?, CURRENT_TIMESTAMP)',
                credential.id,
                JSON.stringify(credential),
                credential.created_date,
                credential.cancelled_date
            ),
            this.executor.executeUpdate(KEY_SQL, credential.id, 'credential'),
        ])
        CacheFactory.getInstance().removeRecord(PSCredential.dataType(), credential)
        return loadFks ? this.fetchRecord(credential.id, PSCredential) : credential.id
    }

    async storeCalendar(calendar: any, loadFks = true): Promise<string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO calendars(id, credential, jsonData, createdDate, cancelledDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                calendar.id,
                calendar.credential,
                JSON.stringify(calendar),
                calendar.created_date,
                calendar.cancelled_date
            ),
            this.executor.executeUpdate(KEY_SQL, calendar.id, 'calendar'),
        ])
        CacheFactory.getInstance().removeRecord(Calendar.dataType(), calendar)
        return loadFks ? this.fetchRecord(calendar.id, Calendar) : calendar.id
    }

    async deleteEvent(event: PSEvent): Promise<Result<null>> {
        const url = '/events/cancel'
        try {
            const result = await apiClient.post(url, { event_id: event.id })
            await this.storeEvent(result.data)
            this.postNotification('ScheduleDataSourceChanged', null)
            return okResult(null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteItem(idString: string, notifyAll: boolean): Promise<Result<Item>> {
        const url = `/item/${idString}/delete`
        const postInfo = { notify_all: notifyAll }

        try {
            const result = await apiClient.post(url, postInfo)
            const jsonObject = result.data
            const newItem = await this.storeItem(jsonObject.item, true)
            if (newItem.type === 'class' || (newItem.type === 'reservation' && newItem.startDate)) {
                if (newItem.type === 'class') {
                    if (jsonObject.lessons) {
                        for (const lesson of jsonObject.lessons) {
                            await this.storeClass(lesson)
                        }
                    }
                }
                if (jsonObject.bookings) {
                    for (const booking of jsonObject.bookings) {
                        await this.storeBooking(booking)
                    }
                }
                this.postNotification('ScheduleDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            if (jsonObject.messages) {
                for (const message of jsonObject.messages) {
                    await this.storeMessage(message)
                }
                this.postNotification('MessagesDataSourceChanged', null)
            }
            if (newItem.type === 'product') {
                this.postNotification('DashboardDataSourceChanged', null)
            }
            this.postNotification('ItemDataSourceChanged', null)

            if (newItem.type === 'class') {
                await this.pushBookings([])
            }
            return okResult(newItem)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteCategory(idString: string): Promise<Result<string | Category>> {
        const url = `/category/${idString}/delete`
        try {
            const result = await apiClient.post(url, {})
            const newCategory = await this.storeCategory(result.data)
            this.postNotification('ItemDataSourceChanged', null)
            return okResult(newCategory)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteLink(idString: string): Promise<Result<string | PSLink>> {
        const url = `/link/${idString}/delete`
        try {
            const result = await apiClient.post(url, {})
            const newLink = await this.storeLink(result.data)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newLink)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteNote(idString: string): Promise<Result<string | Note>> {
        const url = `/note/${idString}/delete`

        try {
            const result = await apiClient.post(url, {})
            const newNote = await this.storeNote(result.data)
            this.postNotification('NoteDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newNote)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteNumber(idString: string): Promise<Result<string>> {
        const url = `/number/${idString}/delete`
        try {
            const result = await apiClient.post(url, {})
            const jsonObject = result.data
            const newNumber = await this.storeNumber(jsonObject.number, false)
            if (jsonObject.person) await this.storeUser(jsonObject.person)
            if (jsonObject.subscription) await this.storeSubscription(jsonObject.subscription)
            this.postNotification('NumberDataSourceChanged', null)
            return okResult(newNumber)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteTeam(idString: string): Promise<Result<string | Team>> {
        const url = `/team/${idString}/delete`
        try {
            const result = await apiClient.post(url, {})
            const jsonObject = result.data
            const newTeam = await this.storeTeam(jsonObject)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            return okResult(newTeam)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteTask(idString: string): Promise<Result<string | Task>> {
        const url = `/task/${idString}/delete`
        try {
            const result = await apiClient.post(url, {})
            const jsonObject = result.data
            const newTask = await this.storeTask(jsonObject.task)
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTask)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteType(idString: string): Promise<Result<string | Type>> {
        const url = `/type/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const newType = await this.storeType(result.data)
            this.postNotification('RecordDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newType)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteRecord(idString: string, dontNotify: boolean): Promise<Result<PSRecord>> {
        const url = `/record/${idString}/delete`
        try {
            const result = await apiClient.post(url, { dont_notify: dontNotify })
            const jsonObject = result.data
            const newRecord = await this.storeRecord(jsonObject.record, true)
            if (jsonObject.message && jsonObject.message !== '') {
                const newMessage = await this.storeMessage(jsonObject.message, true)
                this.postNotification('MessagesDataSourceChanged', newMessage)
            }
            this.postNotification('RecordDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newRecord)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteTemplate(idString: string): Promise<Result<string | Template>> {
        const url = `/template/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const jsonObject = JSON.parse(result.data)
            const newTemplate = await this.storeTemplate(jsonObject)
            this.postNotification('TemplateDataSourceChanged', null)
            return okResult(newTemplate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteTrigger(idString: string): Promise<Result<string | Trigger>> {
        const url = `/trigger/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const jsonObject = JSON.parse(result.data)
            const newTrigger = await this.storeTrigger(jsonObject)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTrigger)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async pauseTrigger(idString: string): Promise<Result<string | Trigger>> {
        try {
            const result = await apiClient.post(`/trigger/${idString}/pause`)
            const jsonObject = JSON.parse(result.data)
            const newTrigger = await this.storeTrigger(jsonObject)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTrigger)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async resumeTrigger(idString: string): Promise<Result<string | Trigger>> {
        try {
            const result = await apiClient.post(`/trigger/${idString}/resume`)
            const jsonObject = JSON.parse(result.data)
            const newTrigger = await this.storeTrigger(jsonObject)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTrigger)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteCampaign(idString: string): Promise<Result<string | Campaign>> {
        const url = `/campaign/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const jsonObject = JSON.parse(result.data)
            const newCampaign = await this.storeCampaign(jsonObject)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newCampaign)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteField(idString: string): Promise<Result<Field>> {
        const url = `/field/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const newField = await this.storeField(result.data, true)
            if (newField.record !== undefined)
                this.postNotification('RecordDataSourceChanged', null)
            this.postNotification('FieldDataSourceChanged', null)
            return okResult(newField)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteSource(idString: string): Promise<Result<string | Source>> {
        const url = `/source/${idString}/cancel`
        try {
            const result = await apiClient.post(url)

            const jsonObject = result.data
            const newSource = await this.storeSource(jsonObject.source)

            if (jsonObject.bookings) {
                for (const booking of jsonObject.bookings) await this.storeBooking(booking)
            }

            if (jsonObject.estimates) {
                for (const estimate of jsonObject.estimates) await this.storeEstimate(estimate)
            }

            if (jsonObject.subscriptions) {
                for (const subscription of jsonObject.subscriptions)
                    await this.storeSubscription(subscription)
            }
            this.postNotification('SourceDataSourceChanged', null)
            return okResult(newSource)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async routeClient(idString: string, location: Location): Promise<Result<User | undefined>> {
        const url = `/client/${idString}/route`
        try {
            const result = await apiClient.post(url, { location: location.id })

            const jsonObject = result.data
            const client = await this.storeClient(jsonObject['client'], true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(client.person)
        }
        catch (error) {
            return errorResult(
                "This client can't be routed because they're not in your client list"
            )
        }
    }

    async deleteClient(idString: string, lockOut: boolean): Promise<Result<User | undefined>> {
        const url = `/client/${idString}/delete`
        try {
            const result = await apiClient.post(url, { lock_out: lockOut })

            const jsonObject = result.data
            const client = await this.storeClient(jsonObject['client'], true)
            this.postNotification('PeopleDataSourceChanged', {
                removed: client.person!.id,
            })
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(client.person)
        }
        catch (error) {
            return errorResult(
                "This client can't be deleted because they're not in your client list"
            )
        }
    }

    async deleteSubaccount(idString: string): Promise<Result<Subaccount>> {
        const url = `/subaccount/${idString}/delete`
        try {
            const result = await apiClient.post(url)

            const jsonObject = result.data
            const subaccount = await this.storeSubaccount(jsonObject['subaccount'], true)
            this.postNotification('PeopleDataSourceChanged', {
                removed: subaccount.id,
            })
            this.postNotification('SubaccountDataSourceChanged', null)
            return okResult(subaccount)
        }
        catch (error) {
            return errorResult(
                "This subaccount can't be deleted because they're not in your client list"
            )
        }
    }

    async deletePayee(idString: string): Promise<Result<User | undefined>> {
        const url = `/payee/${idString}/delete`
        try {
            const result = await apiClient.post(url)

            const jsonObject = result.data
            const payee = await this.storePayee(jsonObject['payee'], true)
            this.postNotification('PeopleDataSourceChanged', {
                removed: payee.person!.id,
            })
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(payee.person)
        }
        catch (error) {
            return errorResult("This pro can't be deleted because they're not in your pro list")
        }
    }

    async deleteSubscriber(idString: string): Promise<Result<User | undefined>> {
        const url = `/subscriber/${idString}/delete`
        try {
            const result = await apiClient.post(url)

            const jsonObject = result.data
            const subscriber = await this.storeSubscriber(jsonObject['subscriber'], true)
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(subscriber.person)
        }
        catch (error) {
            return errorResult(
                "This subscriber can't be deleted because they're not in your subscriber list"
            )
        }
    }

    async deleteMessage(idString: string): Promise<Result<Message | null>> {
        const url = `/message/${idString}/delete`
        try {
            const result = await apiClient.post(url)

            const messages: Message[] = []
            const results = result.data
            for (const message of results) messages.push(await this.storeMessage(message, true))
            this.postNotification('MessagesTabDataSourceChanged', null)
            return okResult(messages.length > 0 ? messages[0] : null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteMessages(idString: string): Promise<Result<Message[]>> {
        const url = `/messages/${idString}/delete`
        try {
            const result = await apiClient.post(url)
            const messages: Message[] = []
            const results = result.data
            for (const message of results) messages.push(await this.storeMessage(message, true))
            this.postNotification('MessagesTabDataSourceChanged', null)
            return okResult(messages)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteContract(idString: string): Promise<Result<string | Contract>> {
        const url = `/contract/${idString}/delete`
        try {
            const result = await apiClient.post(url)

            const jsonObject = result.data
            const contract = await this.storeContract(jsonObject)
            this.postNotification('ContractDataSourceChanged', null)
            return okResult(contract)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteSignature(idString: string, dontNotify: boolean): Promise<Result<Signature>> {
        const url = `/signature/${idString}/delete`
        try {
            const result = await apiClient.post(url, { dont_notify: dontNotify })

            const jsonObject = result.data
            const signature = await this.storeSignature(jsonObject['signature'], true)
            if (jsonObject['message']) {
                const message = await this.storeMessage(jsonObject['message'], true)
                this.postNotification('MessagesDataSourceChanged', message)
            }
            this.postNotification('ContractDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(signature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteBlock(idString: string, deleteAll: boolean): Promise<Result<Block>> {
        const jsonData: any = {}
        if (deleteAll) jsonData['delete_all'] = true
        const url = `/block/${idString}/delete`
        try {
            const result = await apiClient.post(url, jsonData)

            const jsonObject = result.data
            const newBlock = await this.storeBlock(jsonObject['block'], true)
            if (jsonObject['schedule']) {
                await this.storeSchedule(jsonObject['schedule'])
                if (jsonObject['blocks']) {
                    for (const block of jsonObject['blocks']) await this.storeBlock(block)
                }
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            return okResult(newBlock)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // todo: ComputeFactory's calendarEnabled and pushBookings
    async pushBookings(bookings: Bookable[]) {
        // if (ComputeFactory.getInstance().calendarEnabled()) {
        //     await ComputeFactory.getInstance().pushBookings(bookings || [], false);
        // }
    }

    async sendBooking(booking: Booking, dontNotify: boolean): Promise<Result<Booking>> {
        const jsonData = booking.jsonObject()
        if (dontNotify) jsonData['dont_notify'] = true
        const url = booking.id ? `/booking/${booking.id}` : '/booking'
        try {
            const result = await apiClient.post(url, jsonData)
            return this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async createCheckout(booking: Booking): Promise<Result<RecordDataMap>> {
        const jsonData: any = booking.jsonObject()
        jsonData['type'] = booking.item ? booking.item.type : 'service'
        if (booking.lines?.length) {
            const lines: any[] = []
            for (const line of booking.lines) {
                lines.push(line.jsonObject())
            }
            jsonData['lines'] = lines
        }
        if (booking.lessons?.length) {
            const lessons: string[] = []
            for (const lesson of booking.lessons) {
                lessons.push(lesson.id!)
            }
            jsonData['lessons'] = lessons
        }
        if (booking.dates?.length) {
            jsonData['dates'] = booking.dates.map((date) => formatTimeStamp(date))
        }
        try {
            const result = await apiClient.post(`/booking/checkout/${booking.owner!.id}`, jsonData)
            const start = moment()
            const checkoutResults = new RecordDataMap()
            const results = result.data
            checkoutResults.put('checkout', results['checkout'])
            const productData = results['products'] || []
            const products: Item[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const product of productData) {
                    products.push(await txManager.recordFactory.storeItem(product, true))
                }
                checkoutResults.put('products', products)
                console.debug(
                    `CHECKOUT: createCheckout ${productData.length} products (${moment().diff(start)}ms)`
                )
            })
            return okResult(checkoutResults)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkout(booking: Booking, image?: File): Promise<Result<Booking>> {
        const url = '/booking/schedule'
        try {
            const result = await apiClient.post(url, addImage(booking.jsonObject(), image))
            return this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    private async handleBookingResult(result: any): Promise<Result<Booking>> {
        const jsonObject = result.data
        let message: Message | null = null
        const newBooking = await this.storeBooking(jsonObject['booking'], true)
        const newBookings: Bookable[] = [newBooking]
        if (jsonObject['message']) {
            message = await this.storeMessage(jsonObject['message'], true)
        }
        await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
            if (jsonObject['client']) {
                await txManager.recordFactory.storeClient(jsonObject['client'])
            }
            if (jsonObject['subaccount']) {
                await txManager.recordFactory.storeSubaccount(jsonObject['subaccount'])
            }
            if (jsonObject['payee']) {
                await txManager.recordFactory.storePayee(jsonObject['payee'])
            }
            if (jsonObject['estimate']) {
                await txManager.recordFactory.storeEstimate(jsonObject['estimate'])
            }
            if (jsonObject['payment']) {
                await txManager.recordFactory.storePayment(jsonObject['payment'])
            }
            if (jsonObject['schedule']) {
                await txManager.recordFactory.storeSchedule(jsonObject['schedule'])
            }
            if (jsonObject['bookings']) {
                for (const booking of jsonObject['bookings']) {
                    newBookings.push(await txManager.recordFactory.storeBooking(booking, true))
                }
            }
            if (jsonObject['gift']) {
                await txManager.recordFactory.storeGift(jsonObject['gift'])
            }
            if (jsonObject['package']) {
                await txManager.recordFactory.storePackage(jsonObject['package'])
            }
            if (jsonObject['event']) {
                await txManager.recordFactory.storeEvent(jsonObject['event'])
            }
            if (jsonObject['employee']) {
                await txManager.recordFactory.storeUser(jsonObject['employee'])
            }
            if (jsonObject['form']) {
                await txManager.recordFactory.storeType(jsonObject['form'])
            }
            if (jsonObject['contract']) {
                await txManager.recordFactory.storeContract(jsonObject['contract'])
            }
            if (jsonObject['messages']) {
                for (const message of jsonObject['messages']) {
                    await txManager.recordFactory.storeMessage(message)
                }
            }
        })

        this.postNotification('ScheduleDataSourceChanged', newBooking)
        this.postNotification('DashboardDataSourceChanged', newBooking)
        if (message) {
            this.postNotification('MessagesDataSourceChanged', message)
        }

        await this.pushBookings(newBookings)
        return okResult(newBooking)
    }

    async sendMessage(message: Message, image?: File): Promise<Result<Message>> {
        const url = image ? '/message' : message.id ? `/message/${message.id}` : '/message'
        try {
            const result = await apiClient.post(url, addImage(message.jsonObject(), image))

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData, true)

            this.postNotification('MessagesDataSourceChanged', newMessage)
            this.postNotification('MessagesTabDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newMessage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendBroadcast(message: Message, team: Team, image?: File): Promise<Result<Message>> {
        const url = '/broadcast'
        const payload = message.jsonObject()
        if (!team.id) {
            const members: string[] = []
            for (const member of team.members) {
                members.push(member.person!.id!)
            }
            payload['members'] = members
        }
        try {
            const result = await apiClient.post(url, addImage(payload, image))

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData['message'], true)

            await this.storeTeam(resultData['team'])
            this.postNotification('MessagesDataSourceChanged', newMessage)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newMessage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOrder(order: Order, dontNotify: boolean): Promise<Result<Payment | Order>> {
        const jsonData: any = order.jsonObject()
        if (dontNotify) {
            jsonData['dont_notify'] = true
        }
        const url = order.id ? `/order/${order.id}` : '/order'
        try {
            const result = await apiClient.post(url, jsonData)
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async showOrderType(order: Order): Promise<Result<RecordDataMap[]>> {
        const postData: any = {}
        postData['owner'] = order.owner!.id
        if (order.total && order.total > 0) {
            postData['total'] = order.total
        }
        const lines: any[] = []
        for (const line of order.lines) {
            lines.push(line.jsonObject())
        }
        postData['lines'] = lines

        try {
            const result = await apiClient.post('/order/type', postData)
            const results: RecordDataMap[] = []
            const resultData = result.data
            const al = resultData['results']
            for (const a of al) {
                results.push(new RecordDataMap(a))
            }
            return okResult(results)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async createOrderCheckout(order: Order): Promise<Result<any>> {
        const jsonData = order.jsonObject()
        jsonData['type'] = 'product'

        try {
            const result = await apiClient.post(`/order/checkout/${order.owner!.id!}`, jsonData)
            const checkout = result.data['checkout']
            return okResult(checkout)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutOrder(
        order: Order,
        intent?: string,
        image?: File
    ): Promise<Result<Payment | Order>> {
        const json = order.jsonObject()
        if (intent) {
            json['intent'] = intent
        }
        const url = '/order/place'
        try {
            const result = await apiClient.post(url, addImage(json, image))
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async confirmOrder(
        order: Order,
        pos: boolean,
        intent: string
    ): Promise<Result<Payment | Order>> {
        const url = `/order/${order.id}/confirm`
        const postInfo: any = {}
        if (pos) {
            postInfo['is_pos'] = true
            if (intent) {
                postInfo['intent'] = intent
            }
        }
        try {
            const result = await apiClient.post(url, postInfo)
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async chargeOrder(
        order: Order,
        pos: boolean,
        source: Source | undefined,
        intent: string | undefined
    ): Promise<Result<Payment | Order>> {
        const postInfo: any = {}
        postInfo['rate'] = order.rate ? order.rate : '0'
        if (pos) {
            postInfo['is_pos'] = true
            if (intent) {
                postInfo['intent'] = intent
            }
            if (source) {
                postInfo['source'] = source.id
            }
        }
        if (order.lines) {
            const jsonLines = order.lines.map((line: Line) => line.jsonObject())
            postInfo['lines'] = jsonLines
        }
        if (order.ship) {
            postInfo['ship'] = order.ship
        }
        postInfo['ship_taxable'] = order.shipTaxable
        if (order.carrier) {
            postInfo['carrier'] = order.carrier
        }
        if (order.delivery) {
            postInfo['delivery'] = order.delivery
        }
        if (order.order) {
            postInfo['order'] = order.order
        }
        if (order.shipPricing) {
            postInfo['ship_pricing'] = order.shipPricing
        }
        postInfo['allow_surcharge'] = order.allowSurcharge
        if (order.allowSalesTax) {
            postInfo['taxcode'] = order.taxcode ? order.taxcode.id : ''
            postInfo['tax'] = order.tax && order.tax !== 0.0 ? order.tax : ''
            postInfo['nexus'] = order.nexus ? order.nexus : ''
            postInfo['line1'] = order.line1 ? order.line1 : ''
            postInfo['line2'] = order.line2 ? order.line2 : ''
            postInfo['city'] = order.city ? order.city : ''
            postInfo['state'] = order.state ? order.state : ''
            postInfo['postal_code'] = order.postalCode ? order.postalCode : ''
            postInfo['country_code'] = order.countryCode ? order.countryCode : ''
        }
        else {
            postInfo['taxcode'] = ''
            postInfo['tax'] = ''
            postInfo['nexus'] = ''
        }
        if (order.discount && order.discount > 0) {
            postInfo['discount'] = order.discount
            postInfo['discount_type'] = order.discountType
        }
        else {
            postInfo['discount'] = ''
            postInfo['discount_type'] = ''
        }
        const url = `/order/${order.id}/charge`
        try {
            const result = await apiClient.post(url, postInfo)
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markOrder(
        order: Order,
        refnum: string | undefined,
        paymeth: string | undefined,
        markPaidDate: moment.Moment,
        receipt: boolean
    ): Promise<Result<Order>> {
        const postInfo: any = {}
        postInfo['rate'] = order.rate || '0'
        if (order.lines) {
            const jsonLines: any[] = order.lines.map((line) => line.jsonObject())
            postInfo['lines'] = jsonLines
        }
        if (order.ship) postInfo['ship'] = order.ship
        postInfo['ship_taxable'] = order.shipTaxable
        if (order.carrier) postInfo['carrier'] = order.carrier
        if (order.delivery) postInfo['delivery'] = order.delivery
        if (order.order) postInfo['order'] = order.order
        if (order.shipPricing) postInfo['ship_pricing'] = order.shipPricing
        postInfo['allow_surcharge'] = order.allowSurcharge
        if (order.allowSalesTax) {
            postInfo['taxcode'] = order.taxcode ? order.taxcode.id : ''
            postInfo['tax'] = order.tax && order.tax !== 0.0 ? order.tax : ''
            postInfo['nexus'] = order.nexus || ''
            postInfo['line1'] = order.line1 || ''
            postInfo['line2'] = order.line2 || ''
            postInfo['city'] = order.city || ''
            postInfo['state'] = order.state || ''
            postInfo['postal_code'] = order.postalCode || ''
            postInfo['country_code'] = order.countryCode || ''
        }
        else {
            postInfo['taxcode'] = ''
            postInfo['tax'] = ''
            postInfo['nexus'] = ''
        }
        if (order.discount && order.discount > 0) {
            postInfo['discount'] = order.discount
            postInfo['discount_type'] = order.discountType
        }
        else {
            postInfo['discount'] = ''
            postInfo['discount_type'] = ''
        }
        const url = `/order/${order.id}/mark`

        try {
            const result = await apiClient.post(url, postInfo)

            const resultData = result.data
            const newOrder = await this.storeOrder(resultData, true)
            this.postNotification('OrderDataSourceChanged', null)
            return okResult(newOrder)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeOrder(order: Order, dontNotify: boolean): Promise<Result<Payment | Order>> {
        const postInfo: any = {}
        if (order.pickupCode) {
            postInfo['pickup_code'] = order.pickupCode
        }
        if (order.trackingCode) {
            postInfo['tracking_code'] = order.trackingCode
        }
        postInfo['dont_notify'] = dontNotify
        const url = `/order/${order.id}/complete`
        try {
            const result = await apiClient.post(url, postInfo)
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelOrder(
        order: Order,
        dontNotify: boolean,
        cancellationType: string,
        cancellationMemo: string,
        enforce: boolean
    ): Promise<Result<Payment | Order>> {
        const postInfo: any = {}
        if (dontNotify) {
            postInfo['dont_notify'] = true
        }
        if (cancellationType) {
            postInfo['cancellation_type'] = cancellationType
        }
        if (cancellationMemo) {
            postInfo['cancellation_memo'] = cancellationMemo
        }
        if (enforce) {
            postInfo['enforce'] = true
        }
        const url = `/order/${order.id}/cancel`
        try {
            const result = await apiClient.post(url, postInfo)
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async changeOrderSource(order: Order, source: Source): Promise<Result<Payment | Order>> {
        const url = `/order/${order.id}/source`
        try {
            const result = await apiClient.post(url, { source: source.id })
            return this.handleOrderResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    private async handleOrderResult(result: any): Promise<Result<Payment | Order>> {
        try {
            const jsonObject = result.data
            let message = null
            const newPayments: Payment[] = []
            const newOrder = await this.storeOrder(jsonObject.order, true)
            if (jsonObject.message) message = await this.storeMessage(jsonObject.message, true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (jsonObject.client) await txManager.recordFactory.storeClient(jsonObject.client)
                if (jsonObject.payee) await txManager.recordFactory.storePayee(jsonObject.payee)
                if (jsonObject.estimate)
                    await txManager.recordFactory.storeEstimate(jsonObject.estimate)
                if (jsonObject.payment)
                    newPayments.push(
                        await txManager.recordFactory.storePayment(jsonObject.payment, true)
                    )
                if (jsonObject.gift) await txManager.recordFactory.storeGift(jsonObject.gift)
                if (jsonObject.employee)
                    await txManager.recordFactory.storeUser(jsonObject.employee)
                if (jsonObject.messages) {
                    for (const message of jsonObject.messages)
                        await txManager.recordFactory.storeMessage(message)
                }
            })

            this.postNotification('ScheduleDataSourceChanged', newOrder)
            this.postNotification('DashboardDataSourceChanged', newOrder)
            if (message) this.postNotification('MessagesDataSourceChanged', message)
            return okResult(newPayments.length > 0 ? newPayments[0] : newOrder)
        }
        catch (error: any) {
            return errorResult(error)
        }
    }

    // todo: implement
    // public void sendRoyalty(final Royalty royalty, boolean dontNotify, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> handleRoyaltyResult(result, onload);
    //     HashMap<String, Object> jsonData = royalty.jsonObject();
    //     if (dontNotify)
    //         jsonData.put("dont_notify", true);
    //     String url = StringUtil.isNotEmpty(royalty.id) ? String.format("/royalty/%s", royalty.id) : "/royalty";
    //     ServerFactory.getInstance().post(url, jsonData, serverCallBack, result -> {
    //         if (onfail != null)
    //             onfail.execute(result);
    //     });
    // }

    // public void chargeRoyalty(Royalty royalty, boolean pos, Source source, String intent, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> handleRoyaltyResult(result, onload);

    //     RecordDataMap postInfo = new RecordDataMap();
    //     postInfo.put("is_pos", pos);
    //     if (StringUtil.length(intent) > 0)
    //         postInfo.put("intent", intent);
    //     if (source != null)
    //         postInfo.put("source", source.id);
    //     String url = String.format("/royalty/%s/charge", royalty.getId());
    //     ServerFactory.getInstance().post(url, postInfo, serverCallBack, result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    // public void markRoyalty(Royalty royalty, String refnum, String paymeth, Date markPaidDate, boolean receipt, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> handleRoyaltyResult(result, onload);

    //     RecordDataMap postInfo = new RecordDataMap();
    //     postInfo.put("refnum", StringUtil.isNotEmpty(refnum) ? refnum : "");
    //     postInfo.put("paymeth", StringUtil.isNotEmpty(paymeth) ? paymeth : "");
    //     postInfo.put("mark_paid_date", FormatLogic.formatTimestamp(markPaidDate));
    //     if (receipt)
    //         postInfo.put("send_receipt", true);
    //     String url = String.format("/royalty/%s/mark", royalty.getId());
    //     ServerFactory.getInstance().post(url, postInfo, serverCallBack, result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    // public void cancelRoyalty(Royalty royalty, boolean dontNotify, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> handleRoyaltyResult(result, onload);

    //     RecordDataMap postInfo = new RecordDataMap();
    //     if (dontNotify)
    //         postInfo.put("dont_notify", true);
    //     String url = String.format("/royalty/%s/cancel", royalty.id);
    //     ServerFactory.getInstance().post(url, postInfo, serverCallBack, result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    // public void changeRoyaltySource(Royalty royalty, Source source, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> handleRoyaltyResult(result, onload);

    //     String url = String.format("/royalty/%s/source", royalty.getId());
    //     ServerFactory.getInstance().post(url, CollectionUtil.asMap("source", source.getId()), serverCallBack, result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    // private void handleRoyaltyResult(Object result, ServerCallBack onload) {
    //     try {
    //         Payment payment = null;
    //         final JSONObject jsonObject = new JSONObject(result.toString());
    //         Royalty newRoyalty = storeRoyalty(jsonObject.getJSONObject("royalty"));
    //         if (jsonObject.has("payment"))
    //             payment = storePayment(jsonObject.getJSONObject("payment"));
    //         SQLiteFactory.getInstance().executeTransaction(() -> {
    //             try {
    //                 if (jsonObject.has("client"))
    //                     storeClient(jsonObject.getJSONObject("client"));
    //                 if (jsonObject.has("payee"))
    //                     storePayee(jsonObject.getJSONObject("payee"));
    //                 if (jsonObject.has("location"))
    //                     storeLocation(jsonObject.getJSONObject("location"));
    //                 if (jsonObject.has("message"))
    //                     storeMessage(jsonObject.getJSONObject("message"));
    //                 if (jsonObject.has("messages")) {
    //                     JSONArray messagesArray = jsonObject.getJSONArray("messages");
    //                     for (int i = 0; i < messagesArray.length(); i++)
    //                         storeMessage(messagesArray.getJSONObject(i));
    //                 }
    //             }
    //             catch (Exception e) {
    //                 ErrorLogic.log(e);
    //             }
    //         });

    //         this.postNotification(PSApplication.getAppContext(), DashboardDataSourceChanged, null);
    //         if (jsonObject.has("messages") || jsonObject.has("message"))
    //             this.postNotification(PSApplication.getAppContext(), MessagesDataSourceChanged, null);
    //         if (jsonObject.has("location"))
    //             this.postNotification(PSApplication.getAppContext(), LocationDataSourceChanged, null);
    //         if (onload != null)
    //             onload.execute(payment != null ? payment : newRoyalty);
    //     }
    //     catch (JSONException e) {
    //         ErrorLogic.log(e);
    //     }
    // }

    async sendEstimate(estimate: Estimate, dontNotify: boolean): Promise<Result<Estimate>> {
        const jsonData = estimate.jsonObject()
        if (dontNotify) jsonData['dont_notify'] = true
        const url = estimate.id ? `/estimate/${estimate.id}` : '/estimate'
        try {
            const result = await apiClient.post(url, jsonData)
            const resultData = result.data

            if (resultData.message) {
                const newMessage = await this.storeMessage(resultData.message, true)
                this.postNotification('MessagesDataSourceChanged', newMessage)
            }

            const newEstimate = await this.storeEstimate(resultData.estimate, true)
            this.postNotification('DashboardDataSourceChanged', newEstimate)

            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.payee) await this.storePayee(resultData.payee)
            if (resultData.subaccount) await this.storeSubaccount(resultData.subaccount)

            return okResult(newEstimate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendInvoice(invoice: Invoice, dontNotify: boolean): Promise<Result<Invoice>> {
        const jsonData = invoice.jsonObject()
        if (dontNotify) jsonData['dont_notify'] = true
        const url = invoice.id ? `/invoice/${invoice.id}` : '/invoice'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newInvoice = await this.storeInvoice(resultData.invoice, true)
            if (resultData.message) await this.storeMessage(resultData.message, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.payee) await this.storePayee(resultData.payee)
            if (resultData.subaccount) await this.storeSubaccount(resultData.subaccount)
            if (resultData.bookings) {
                for (const booking of resultData.bookings) await this.storeBooking(booking)
                this.postNotification('ScheduleDataSourceChanged', null)
            }
            if (resultData.event) await this.storeEvent(resultData.event)
            if (resultData.estimate) await this.storeEstimate(resultData.estimate)
            this.postNotification('DashboardDataSourceChanged', newInvoice)
            if (resultData.message)
                console.debug(`MessagesDataSourceChanged: ${resultData.message}`)
            return okResult(newInvoice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendPayment(
        transaction: Transaction | undefined,
        payment: Payment
    ): Promise<Result<Payment>> {
        const url =
            transaction && transaction.id
                ? payment.gift
                    ? `/gift/${transaction.id}/pay`
                    : payment.aPackage
                      ? `/package/${transaction.id}/pay`
                      : `/invoice/${transaction.id}/pay`
                : '/pay'
        try {
            const result = await apiClient.post(url, payment.jsonObject())

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData.message, true)
            const newPayment = await this.storePayment(resultData.payment, true)
            if (resultData.invoice) await this.storeInvoice(resultData.invoice)
            if (resultData.package) await this.storePackage(resultData.package)
            if (resultData.gift) await this.storeGift(resultData.gift)
            if (resultData.jobs) {
                for (const job of resultData.jobs) await this.storeJob(job)
            }
            if (resultData.payee) await this.storePayee(resultData.payee)
            if (resultData.estimate) await this.storeEstimate(resultData.estimate)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('MessagesDataSourceChanged', newMessage)
            return okResult(newPayment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendCharge(payment: Payment): Promise<Result<Payment>> {
        try {
            const result = await apiClient.post('/payment', payment.jsonObject())

            const resultData = result.data
            let newMessage
            if (resultData.newMessage)
                newMessage = await this.storeMessage(resultData.message, true)
            const newPayment = await this.storePayment(resultData.payment, true)
            if (resultData.invoice) await this.storeInvoice(resultData.invoice)
            if (resultData.payer) await this.storeUser(resultData.payer)
            if (resultData.client) await this.storeClient(resultData.client)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('MessagesDataSourceChanged', newMessage)
            return okResult(newPayment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendType(type: Type, image?: File): Promise<Result<string | Type>> {
        const url = type.id ? `/type/${type.id}` : '/type'
        try {
            const result = await apiClient.post(url, addImage(type.jsonObject(), image))

            const resultData = result.data
            const newType = await this.storeType(resultData.form)
            if (resultData.fields) {
                await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                    for (const field of resultData.fields)
                        await txManager.recordFactory.storeField(field)
                })
            }
            this.postNotification('RecordDataSourceChanged', null)
            return okResult(newType)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendRecord(
        record: PSRecord,
        sendOnly: boolean,
        dontNotify: boolean
    ): Promise<Result<PSRecord>> {
        const url = record.id ? `/record/${record.id}` : '/record'
        const jsonData: any = record.jsonObject()
        if (sendOnly) {
            let channel = record.channel
            if (!channel) {
                const person = record.person!
                channel = person.e164 ? 'sms' : 'email'
                const client = await this.loadClient(person.id!) // client can be null in case of selfie
                if (client && client.notifyType) {
                    channel = client.notifyType
                }
                jsonData['channel'] = channel
            }
        }
        if (dontNotify) {
            jsonData['dont_notify'] = true
        }
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newRecord = await this.storeRecord(resultData['record'], true)
            const message = await this.store('message', resultData['message'])
            await this.store('contract', resultData['contract'])
            await this.store('signature', resultData['signature'])
            if (resultData['messages']) {
                for (const message of resultData['messages']) {
                    await this.storeMessage(message)
                }
            }
            this.postNotification('MessagesDataSourceChanged', message)
            this.postNotification('RecordDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newRecord)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async captureTransaction(
        transaction: Transaction,
        record: PSRecord
    ): Promise<Result<string | PSRecord>> {
        const url = `/transaction/${transaction.id}/capture`
        try {
            const result = await apiClient.post(url, record.jsonObject())

            const resultData = result.data
            const newRecord = await this.storeRecord(resultData['record'])
            await this.store('contract', resultData['contract'])
            await this.store('signature', resultData['signature'])
            await this.store('message', resultData['message'])
            if (resultData['invoice']) await this.storeInvoice(resultData['invoice'])
            if (resultData['estimate']) await this.storeEstimate(resultData['estimate'])
            if (resultData['booking']) await this.storeBooking(resultData['booking'])
            if (resultData['package']) await this.storePackage(resultData['package'])
            if (resultData['gift']) await this.storeGift(resultData['gift'])
            if (resultData['order']) await this.storeOrder(resultData['order'])
            if (resultData['subscription']) await this.storeSubscription(resultData['subscription'])
            if (resultData['messages']) {
                for (const message of resultData['messages']) {
                    await this.storeMessage(message)
                }
            }
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('ContractDataSourceChanged', null)
            return okResult(newRecord)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTemplate(template: Template): Promise<Result<string | Template>> {
        const url = template.id ? `/template/${template.id}` : '/template'
        try {
            const result = await apiClient.post(url, template.jsonObject())

            const newTemplate = await this.storeTemplate(result.data)
            this.postNotification('TemplateDataSourceChanged', null)
            return okResult(newTemplate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTrigger(trigger: Trigger): Promise<Result<string | Trigger>> {
        const url = trigger.id ? `/trigger/${trigger.id}` : '/trigger'
        try {
            const result = await apiClient.post(url, trigger.jsonObject())

            const newTrigger = await this.storeTrigger(result.data)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTrigger)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendCampaign(campaign: Campaign): Promise<Result<string | Campaign>> {
        const team = campaign.team

        const jsonObject = campaign.jsonObject()
        if (team && !team.id) {
            const members: string[] = []
            for (const member of team.members) {
                members.push(member.person!.id!)
            }
            jsonObject['members'] = members
        }
        const url = campaign.id ? `/campaign/${campaign.id}` : '/campaign'
        try {
            const result = await apiClient.post(url, jsonObject)

            const resultData = result.data
            const newCampaign = await this.storeCampaign(resultData['campaign'])
            if (resultData['team']) await this.storeTeam(resultData['team'])
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newCampaign)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendField(field: Field): Promise<Result<string | Field>> {
        const url = field.id ? `/field/${field.id}` : '/field'
        try {
            const result = await apiClient.post(url, field.jsonObject())

            const newField = await this.storeField(result.data)
            this.postNotification('FieldDataSourceChanged', null)
            return okResult(newField)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // todo: how to translate ServerFactory.getInstance().postData
    // async sendUpload(field: Field, data: Buffer, name: string, onLoad?: (result: any) => void, onFail?: (result: any) => void) {
    //     const callBack = async (results: any) => {
    //         try {
    //             const resultData = results.data;
    //             const result = await this.storeImage(new RecordDataMap(resultData['image']));
    //             if (onLoad)
    //                 onLoad(result);
    //         } catch (error: any) { return errorResult(error); }
    //     };
    //
    //     const url = `/field/${field.id}/upload`;
    //     const jsonData: any = {};
    //     jsonData['field'] = field.id;
    //     await apiClient.post(url, addImage(jsonData, data, name))
    //         .then(callBack)
    //         .catch((result: any) => {
    //             if (onFail)
    //                 onFail(result);
    //         });
    // }

    async sendExpiration(
        field: Field,
        image: Image,
        date: moment.Moment
    ): Promise<Result<string | Image>> {
        const callBack = async (results: any) => {}

        const url = `/field/${field.id}/expire`
        const jsonData: any = {}
        jsonData['image'] = image.id
        jsonData['date'] = formatTimeStamp(date)
        try {
            const results = await apiClient.post(url, jsonData)

            const resultData = results.data
            const result = await this.storeImage(new RecordDataMap(resultData['image']))
            return okResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendSource(source: Source): Promise<Result<Source>> {
        const url = source.id ? `/source/${source.id}` : '/source'
        try {
            const result = await apiClient.post(url, source.jsonObject())

            const resultData = result.data
            const newSource = await this.storeSource(resultData, true)
            this.postNotification('SourceDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('MessagesTabDataSourceChanged', null)
            return okResult(newSource)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendItem(item: Item, image?: File): Promise<Result<Item>> {
        const url = item.id ? `/item/${item.id}` : '/item'
        try {
            const result = await apiClient.post(url, addImage(item.jsonObject(), image))

            const resultData = result.data
            const newItem = await this.storeItem(resultData['item'], true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (resultData['message'])
                    await txManager.recordFactory.store('message', resultData['message'])
                if (resultData['lessons']) {
                    for (const lesson of resultData['lessons'])
                        await txManager.recordFactory.storeClass(lesson)
                }
                if (resultData['package'])
                    await txManager.recordFactory.storeItem(resultData['package'])
                if (resultData['adjustment'])
                    await txManager.recordFactory.storeAdjustment(resultData['adjustment'])
            })
            if (resultData['message']) this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('ItemDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            this.postNotification('ScheduleDataSourceChanged', null)
            return okResult(newItem)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendCategory(category: Category, image?: File): Promise<Result<string | Category>> {
        const url = category.id ? `/category/${category.id}` : '/category'
        try {
            const result = await apiClient.post(url, addImage(category.jsonObject(), image))

            const newCategory = await this.storeCategory(result.data)
            this.postNotification('ItemDataSourceChanged', null)
            return okResult(newCategory)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendClass(
        lesson: Lesson,
        schedule: Schedule | undefined,
        item: Item | undefined,
        notifyAll: boolean,
        editAll: boolean
    ): Promise<Result<Lesson | null>> {
        const url = lesson && lesson.id ? `/class/${lesson.id}` : '/class'
        const jsonData: any = lesson ? lesson.jsonObject() : {}
        jsonData['notify_all'] = notifyAll
        jsonData['edit_all'] = editAll
        if (item) jsonData['item'] = item.id
        if (schedule) jsonData['schedule'] = schedule.jsonObject()

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            let newLesson = resultData['lesson']
                ? await this.storeClass(resultData['lesson'], true)
                : null
            const bookings: Bookable[] = []
            const lessons: Lesson[] = []
            if (newLesson) lessons.push(newLesson)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (resultData['item'])
                    await txManager.recordFactory.store('item', resultData['item'])
                if (resultData['bookings']) {
                    for (const booking of resultData['bookings'])
                        bookings.push(await txManager.recordFactory.storeBooking(booking, true))
                }
                if (resultData['lessons']) {
                    for (const lesson of resultData['lessons'])
                        lessons.push(await txManager.recordFactory.storeClass(lesson, true))
                }
                if (resultData['messages']) {
                    for (const message of resultData['messages'])
                        await txManager.recordFactory.storeMessage(message)
                }
            })
            if (resultData['messages']) this.postNotification('MessagesDataSourceChanged', null)
            if (resultData['lessons'] || resultData['bookings']) {
                this.postNotification('ScheduleDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            this.postNotification('ItemDataSourceChanged', null)
            if (!newLesson && lessons.length > 0) newLesson = lessons[0]
            if (lessons?.length) await this.pushBookings(lessons)
            return okResult(newLesson)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendLink(link: PSLink): Promise<Result<string | PSLink>> {
        const url = link.id ? `/link/${link.id}` : '/link'
        try {
            const result = await apiClient.post(url, link.jsonObject())

            const newLink = await this.storeLink(result.data)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newLink)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendNote(note: Note, image?: File): Promise<Result<string | Note>> {
        const url = note.id ? `/note/${note.id}` : '/note'
        try {
            const result = await apiClient.post(url, addImage(note.jsonObject(), image))

            const resultData = result.data
            const newNote = await this.storeNote(resultData)
            this.postNotification('NoteDataSourceChanged', null)
            return okResult(newNote)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // todo: implement
    // public void sendLocation(Location location, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack callBack = result -> {
    //         try {
    //             JSONObject resultData = new JSONObject(result.toString());
    //             Location newLocation = storeLocation(resultData.getJSONObject("location"));
    //             this.postNotification(PSApplication.getAppContext(), LocationDataSourceChanged, null);
    //             if (onload != null)
    //                 onload.execute(newLocation);
    //         }
    //         catch (JSONException e) {
    //             ErrorLogic.log(e);
    //         }
    //     };

    //     String url = StringUtil.isNotEmpty(location.id) ? String.format("/location/%s", location.id) : "/location";
    //     ServerFactory.getInstance().post(url, location.jsonObject(), callBack, result -> {
    //         if (onfail != null)
    //             onfail.execute(result);
    //     });
    // }

    // public void changeLocationSource(Location location, Source source, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack serverCallBack = result -> {
    //         try {
    //             JSONObject resultData = new JSONObject(result.toString());
    //             Location newLocation = storeLocation(resultData.getJSONObject("location"));
    //             this.postNotification(PSApplication.getAppContext(), LocationDataSourceChanged, null);
    //             if (onload != null)
    //                 onload.execute(newLocation);
    //         }
    //         catch (JSONException e) {
    //             ErrorLogic.log(e);
    //         }
    //     };

    //     String url = String.format("/location/%s/source", location.getId());
    //     RecordDataMap json = new RecordDataMap();
    //     if (source != null)
    //         json.put("source", source.getId());
    //     ServerFactory.getInstance().post(url, json, serverCallBack, result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    async sendTask(task: Task): Promise<Result<string | Task>> {
        const url = task.id ? `/task/${task.id}` : '/task'
        try {
            const result = await apiClient.post(url, task.jsonObject())

            const resultData = result.data
            const newTask = await this.storeTask(resultData.task)
            if (resultData.hasOwnProperty('message')) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            return okResult(newTask)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async respondGoogleReview(review: GoogleReview): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['response'] = review.response
        jsonData['review_id'] = review.reviewId

        try {
            await apiClient.post('/google-review/respond', jsonData)
            this.postNotification('DashboardDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async respondReview(review: Review): Promise<Result<string | Review>> {
        const jsonData: any = {}
        jsonData['response'] = review.response

        try {
            const result = await apiClient.post(`/review/${review.id}/respond`, jsonData)

            const resultData = result.data
            const newReview = await this.storeReview(resultData.review)

            await this.storeMessage(resultData.message)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)

            return okResult(newReview)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendReview(review: Review): Promise<Result<string | Review>> {
        const url = review.id ? `/review/${review.id}` : '/review'
        try {
            const result = await apiClient.post(url, review.jsonObject())

            const resultData = result.data
            const newReview = await this.storeReview(resultData.review)
            if (resultData.hasOwnProperty('message')) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            return okResult(newReview)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendGoogleLocation(location: GoogleLocation, credentialId: string): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['credential_id'] = credentialId
        jsonData['location_id'] = location.locationId
        jsonData['account_id'] = location.accountId

        try {
            const result = await apiClient.post('/externallocation', jsonData)

            const resultData = result.data
            await this.storeCredential(resultData.credential)
            await this.storeUser(resultData.user)

            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendCredential(credential: PSCredential): Promise<Result<PSCredential>> {
        try {
            const result = await apiClient.post(
                `/credential/${credential.id}`,
                credential.jsonObject()
            )

            const resultData = result.data
            const newCredential = await this.storeCredential(resultData.credential, true)

            return okResult(newCredential)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTaxForm(taxYears: string[]): Promise<Result<string>> {
        try {
            const jsonData: any = {}
            jsonData['tax_years'] = taxYears
            const result = await apiClient.post('/user/send-tax', jsonData)
            const resultData = result.data
            return okResult(resultData.alert)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendUser(user: User, image?: File): Promise<Result<string | User>> {
        const url = user.id ? `/user/${user.id}` : '/user'
        try {
            const result = await apiClient.post(url, addImage(user.jsonObject(), image))

            const resultData = result.data
            const newUser = await this.storeUser(resultData.user)
            if (resultData.hasOwnProperty('taxcode')) {
                await this.storeTaxcode(resultData.taxcode)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingState(state: string): Promise<Result<string | User>> {
        const jsonData: any = {}
        jsonData['state'] = state
        jsonData['dashboard'] = 'setup_onboarding'

        try {
            const result = await apiClient.post('/onboarding', jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData.user)
            this.postNotification('PeopleDataSourceChanged', null)
            if (state.endsWith('_complete')) {
                this.postNotification('DashboardDataSourceChanged', null)
            }
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingStateByDashboard(
        state: string,
        dashboard: Dashboard
    ): Promise<Result<RecordDataMap>> {
        const jsonData: any = {}
        jsonData['state'] = state
        jsonData['dashboard'] = dashboard.id

        try {
            const result = await apiClient.post('/onboarding', jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData.user)
            const newDashboard = await this.storeDashboard(resultData.dashboard)
            // if (state.endsWith("_complete"))  // Force a dashboard reload to clear GL dashboard
            //     this.postNotification('DashboardDataSourceChanged', null);
            return okResult(new RecordDataMap({ user: newUser, dashboard: newDashboard }))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchOnboardingState(
        state: string,
        dashboard?: Dashboard
    ): Promise<Result<RecordDataMap>> {
        const start = moment()

        const jsonData: any = {}
        jsonData['state'] = state
        if (dashboard) jsonData['dashboard'] = dashboard.id

        try {
            const result = await apiClient.post('/onboarding/sync', jsonData)

            const results = result.data
            const typeData = results['types']
            const resultData = results['results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results.hasOwnProperty('dashboard'))
                    await txManager.recordFactory.storeDashboard(results.dashboard)
                for (let i = 0; i < typeData.length; i++)
                    await txManager.recordFactory.store(
                        RecordFactory.stringForArrayEntry(typeData, i),
                        RecordFactory.valueForArrayEntry(resultData, i)
                    )
                if (resultData?.length) console.debug(`FETCH: Fetched ${resultData.length} results`)
                console.debug(`FETCH: fetchOnboardingState Fetch Time: ${moment().diff(start)}ms`)
            })
            return okResult(new RecordDataMap())
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingCategories(
        categories: Category[],
        edition: string,
        forOnboarding: boolean
    ): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['categories'] = categories.map((category) => category.jsonObject())
        jsonData['edition'] = edition
        jsonData['forOnboarding'] = forOnboarding

        try {
            const result = await apiClient.post('/onboarding/setup/categories', jsonData)

            const resultData = result.data
            const categoryData = resultData['categories']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    categoryData,
                    txManager.recordFactory.storeCategory
                )
                if (resultData.hasOwnProperty('user'))
                    await txManager.recordFactory.storeUser(resultData.user)
            })
            this.postNotification('ItemDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingItemsSetup(
        type: string,
        items: Item[],
        edition: string,
        forOnboarding: boolean
    ): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['items'] = items.map((item) => item.jsonObject())
        jsonData['edition'] = edition
        jsonData['forOnboarding'] = forOnboarding

        let url = `/onboarding/setup/${type}s`
        if (type === 'class') url = '/onboarding/setup/classes'

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const itemData = resultData['items']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(itemData, txManager.recordFactory.storeItem)
                if (resultData.hasOwnProperty('user'))
                    await txManager.recordFactory.storeUser(resultData.user)
            })
            this.postNotification('ItemDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingCopyData(
        targetUserID: string,
        settingsIDs: string[]
    ): Promise<Result<User>> {
        const response = await apiClient.post('/onboarding/setup/copy', {
            login: targetUserID,
            settings: settingsIDs,
        })

        const newUser = response.data.user
        return okResult(newUser)
    }

    async sendOnboardingTypes(
        types: Type[],
        edition: string,
        forOnboarding: boolean
    ): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['types'] = types.map((type) => type.jsonObject())
        jsonData['edition'] = edition
        jsonData['forOnboarding'] = forOnboarding

        try {
            const result = await apiClient.post('/onboarding/setup/types', jsonData)

            const resultData = result.data
            const typeData = resultData['types']
            const fields = resultData['fields']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(typeData, txManager.recordFactory.storeType)
                if (fields) {
                    await Promise.all(
                        fields.map((field: any) => txManager.recordFactory.storeField(field, false))
                    )
                }
                if (resultData.hasOwnProperty('user'))
                    await txManager.recordFactory.storeUser(resultData.user)
            })
            this.postNotification('RecordDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingIntake(fields: Field[]): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['fields'] = fields.map((field) => field.jsonObject())

        try {
            const result = await apiClient.post('/onboarding/setup/intake', jsonData)

            const resultData = result.data
            const fieldData = resultData['fields']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    fieldData,
                    txManager.recordFactory.storeField
                )
                if (resultData.hasOwnProperty('user'))
                    await txManager.recordFactory.storeUser(resultData.user)
            })
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('FieldDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendOnboardingContracts(
        contracts: Contract[],
        edition: string,
        forOnboarding: boolean
    ): Promise<VoidResult> {
        const jsonData: any = {}
        jsonData['contracts'] = contracts.map((contract) => contract.jsonObject())
        jsonData['edition'] = edition
        jsonData['forOnboarding'] = forOnboarding

        try {
            const result = await apiClient.post('/onboarding/setup/contracts', jsonData)

            const resultData = result.data
            const contractData = resultData['contracts']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    contractData,
                    txManager.recordFactory.storeContract
                )
                if (resultData.hasOwnProperty('user'))
                    await txManager.recordFactory.storeUser(resultData.user)
                if (resultData.hasOwnProperty('items')) {
                    const itemsData = resultData['items']
                    await txManager.recordFactory.storeAll(
                        itemsData,
                        txManager.recordFactory.storeItem
                    )
                }
            })
            this.postNotification('ContractDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTCR(
        tcr: TCR,
        state: string,
        dashboard: Dashboard,
        image?: File
    ): Promise<Result<string | TCR>> {
        const url = '/tcr/setup'
        const jsonData: any = {}
        jsonData.tcr = tcr.jsonObject()
        jsonData.state = state
        jsonData.dashboard = dashboard.id

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = JSON.parse(result.data)
            const updatedTCR = await this.storeTCR(resultData.tcr)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(updatedTCR)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTCROTP(tcr: TCR): Promise<Result<string | TCR>> {
        const url = '/tcr/otp'
        const jsonData: any = {}
        jsonData.tcr = tcr.jsonObject()
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = JSON.parse(result.data)
            const updatedTCR = await this.storeTCR(resultData.tcr)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(updatedTCR)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendNumber(number: PhoneNumber): Promise<Result<any>> {
        const url = !!number.id ? `/number/${number.id}` : '/number'
        try {
            const result = await apiClient.post(url, number.jsonObject())

            const resultData = JSON.parse(result.data)
            const results: any = {}
            results.number = await this.storeNumber(resultData.number)
            if (resultData.price) results.price = resultData.price
            if (resultData.person) await this.storeUser(resultData.person)
            if (resultData.subscription)
                results.subscription = await this.storeSubscription(resultData.subscription)
            if (resultData.payment) await this.storePayment(resultData.payment)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('NumberDataSourceChanged', null)
            return okResult(results)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTeam(team: Team, image?: File): Promise<Result<Team>> {
        const url = !!team.id ? `/team/${team.id}` : '/team'
        try {
            const result = await apiClient.post(url, addImage(team.jsonObject(), image))

            const newTeam = await this.storeTeam(result.data, true)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            return okResult(newTeam)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async addMember(team: Team, member: User): Promise<Result<Team>> {
        const url = `/member/${team.id}/add`
        try {
            const result = await apiClient.post(url, { member: member.id })

            try {
                const newTeam = await this.storeTeam(result.data, true)
                this.postNotification('MessagesDataSourceChanged', null)
                this.postNotification('PeopleDataSourceChanged', null)
                return okResult(newTeam)
            }
            catch (e) {
                return errorResult(e)
            }
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteMember(team: Team, member: User): Promise<Result<Team>> {
        const url = `/member/${team.id}/delete`
        try {
            const result = await apiClient.post(url, { member: member.id })

            const newTeam = await this.storeTeam(result.data, true)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            return okResult(newTeam)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async queryTaxcode(location: string): Promise<Result<Taxcode | null>> {
        try {
            const result = await apiClient.post('/taxcode', { location: location })

            const taxcode = result.data['id'] ? await this.storeTaxcode(result.data, true) : null
            return okResult(taxcode)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async queryShipment(
        order: Order,
        charge: Payment | undefined,
        calcSalesTax: boolean
    ): Promise<Result<any>> {
        const url = '/orders'
        const jsonData: any = {}
        jsonData['owner'] = order.owner?.id
        jsonData['client'] = order.client?.id
        jsonData['line1'] = order.line1
        jsonData['line2'] = order.line2 ? order.line2 : ''
        jsonData['city'] = order.city
        jsonData['state'] = order.state
        jsonData['postal_code'] = order.postalCode
        jsonData['calculate_tax'] = calcSalesTax
        jsonData['nexus'] = charge ? charge.nexus : order.nexus
        jsonData['parcel'] = charge ? charge.parcel : order.parcel
        jsonData['carrier'] = charge ? charge.carrier : order.carrier
        jsonData['delivery'] = charge ? charge.delivery : order.delivery
        jsonData['lines'] = charge ? charge.jsonObject().lines : order.jsonObject().lines

        try {
            const result = await apiClient.post(url, jsonData)
            const results = result.data
            const searchResults: any = {}
            if (results['rates']) {
                const shippingRates: any[] = []
                const rateData = results['rates']
                for (const rate of rateData) shippingRates.push(rate)
                searchResults['rates'] = shippingRates
            }
            if (results['rate']) searchResults['rate'] = results['rate']
            if (results['order']) searchResults['order'] = results['order']
            if (results['parcel']) searchResults['parcel'] = results['parcel']
            if (results['parcel_count']) searchResults['parcel_count'] = results['parcel_count']
            if (results['taxcode'])
                searchResults['taxcode'] = await this.storeTaxcode(results['taxcode'])
            return okResult(searchResults)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendTracking(order: Order): Promise<Result<Message>> {
        const url = `/order/${order.id}/tracking`
        try {
            const result = await apiClient.post(url, null)

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData, true)
            this.postNotification('MessagesDataSourceChanged', null)
            return okResult(newMessage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitLoginSettings(
        device: Device,
        ttl: number | null,
        name: string,
        password?: string
    ): Promise<Result<Device>> {
        const url = device.id ? `/device/${device.id}` : '/device'
        const d: any = {}
        d.ttl = ttl !== null ? ttl : ''
        if (!password || password !== 'xxxx') d.password = password
        if (name.length > 0) d.name = name
        try {
            const result = await apiClient.post(url, d)

            const resultData = result.data
            const newDevice = await this.storeDevice(resultData.device, true)
            if (resultData.user) await this.storeUser(resultData.user)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newDevice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitCalendarSettings(
        device: Device,
        calendar: string,
        availability: string,
        hipaa: boolean | null
    ): Promise<Result<Device>> {
        const url = device.id ? `/device/${device.id}` : '/device'
        const d: any = {}
        if (hipaa !== null) d.hipaa = hipaa
        d.calendar = calendar !== null ? calendar : ''
        d.availability = availability !== null ? availability : ''
        try {
            const result = await apiClient.post(url, d)

            const resultData = result.data
            const newDevice = await this.storeDevice(resultData.device, true)
            if (resultData.user) await this.storeUser(resultData.user)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newDevice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendCalendars(calendars: Calendar[]): Promise<VoidResult> {
        const jsonCalendars = calendars.map((calendar) => calendar.jsonObject())
        const jsonData = { calendars: jsonCalendars }

        const url = '/externalcalendars'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const calendarData = resultData.calendars
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    calendarData,
                    txManager.recordFactory.storeCalendar
                )
            })
            this.postNotification('DashboardDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendClient(person: User, client: Client, image?: File): Promise<Result<User>> {
        const jsonData: any = {}
        if (!person.signupDate) {
            jsonData.name = person.name
            jsonData.phone = person.phone
            jsonData.notes = person.notes
            jsonData.email = person.email
            jsonData.timezone = person.timezone
            jsonData.postal_code = person.postalCode
            jsonData.entity = person.entity
            jsonData.line1 = person.line1
            jsonData.line2 = person.line2
            jsonData.city = person.city
            jsonData.state = person.state
            if (person.address) {
                jsonData.address = person.address
            }
        }

        jsonData.cost = client.cost ? client.cost : ''
        jsonData.channel = client.channel ? client.channel.id : ''
        jsonData.referer = client.referer ? client.referer.id : ''
        jsonData.notify = client.notifyType ? client.notifyType : ''
        jsonData.reminder = client.reminder ? client.reminder : ''
        jsonData.discount = client.discount && client.discount !== 0 ? client.discount : ''
        jsonData.discount_type = client.discountType ? client.discountType : ''

        if (client.fieldValues && Object.keys(client.fieldValues).length > 0) {
            jsonData.field_values = Field.serializeFieldData(
                client.fieldValues,
                client.owner?.id,
                undefined
            )
        }
        if (person.id) {
            jsonData.person = person.id
        }
        jsonData.employee = client.employee ? client.employee.id : ''
        jsonData.location = client.location ? client.location.id : ''
        jsonData.allow_trigger = client.allowTrigger
        jsonData.birthday = client.birthDay
            ? moment(client.birthDay).format('YYYY-MM-DD')
            : undefined

        const url = client.id ? `/client/${client.id}` : '/client'
        try {
            const result = await apiClient.post(url, addImage(jsonData, image))

            const resultData = result.data
            const userData = await this.storeUser(resultData.person, true)
            const messageData = resultData.messages
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeClient(resultData.client)
                if (resultData.note) {
                    await txManager.recordFactory.storeNote(resultData.note)
                }
                await txManager.recordFactory.storeAll(
                    messageData,
                    txManager.recordFactory.storeMessage
                )
            })
            const newUser = await this.fetchRecord(userData.id!, User)
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async importClients(contacts: Person[]): Promise<Result<User[]>> {
        const submitContacts: any[] = []
        for (const person of contacts) {
            const jsonData: any = {}
            if (!person.name || (!person.email && !person.phone)) {
                continue
            }
            jsonData.name = person.name
            jsonData.phone = person.phone
            jsonData.notes = person.notes
            jsonData.email = person.email
            jsonData.postal_code = person.postalCode
            jsonData.entity = person.entity
            jsonData.line1 = person.line1
            jsonData.line2 = person.line2
            jsonData.city = person.city
            jsonData.state = person.state
            if (person.address) {
                jsonData.address = person.address
            }
            submitContacts.push(jsonData)
        }

        try {
            const result = await apiClient.post('/client/import', { contacts: submitContacts })

            const results = result.data
            const newUsers: User[] = []
            for (const resultData of results) {
                const newUser = await this.storeUser(resultData.person, true)
                await this.storeClient(resultData.client)
                if (resultData.note) {
                    await this.storeNote(resultData.note)
                }
                newUsers.push(newUser)
            }
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newUsers)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendSubaccount(subaccount: Subaccount, image?: File): Promise<Result<Subaccount>> {
        const jsonData = subaccount.jsonObject()
        const url = subaccount.id ? `/subaccount/${subaccount.id}` : '/subaccount'
        try {
            const result = await apiClient.post(url, addImage(jsonData, image))

            const resultData = result.data
            const newSubaccount = await this.storeSubaccount(resultData.subaccount, true)
            if (resultData.note) {
                await this.storeNote(resultData.note)
            }
            this.postNotification('SubaccountDataSourceChanged', null)
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('ProfileDataSourceChanged', null)
            return okResult(newSubaccount)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendPayee(person: User, payee: Payee, image?: File): Promise<Result<any>> {
        const jsonData: any = {}
        jsonData.rate = payee.rate ? payee.rate : ''
        jsonData.class_rate = payee.classRate ? payee.classRate : ''
        jsonData.invoice_rate = payee.invoiceRate ? payee.invoiceRate : ''
        jsonData.package_rate = payee.packageRate ? payee.packageRate : ''
        jsonData.gratuity_rate = payee.gratuityRate ? payee.gratuityRate : ''
        jsonData.estimate_rate = payee.estimateRate ? payee.estimateRate : ''
        jsonData.reservation_rate = payee.reservationRate ? payee.reservationRate : ''
        jsonData.subscription_rate = payee.subscriptionRate ? payee.subscriptionRate : ''
        jsonData.rate_type = payee.rateType ? payee.rateType : ''
        jsonData.class_rate_type = payee.classRateType ? payee.classRateType : ''
        jsonData.invoice_rate_type = payee.invoiceRateType ? payee.invoiceRateType : ''
        jsonData.package_rate_type = payee.packageRateType ? payee.packageRateType : ''
        jsonData.gratuity_rate_type = payee.gratuityRateType ? payee.gratuityRateType : ''
        jsonData.estimate_rate_type = payee.estimateRateType ? payee.estimateRateType : ''
        jsonData.reservation_rate_type = payee.reservationRateType ? payee.reservationRateType : ''
        jsonData.subscription_rate_type = payee.subscriptionRateType
            ? payee.subscriptionRateType
            : ''
        jsonData.notify = payee.notifyType ? payee.notifyType : ''
        jsonData.reminder = payee.reminder ? payee.reminder : ''
        jsonData.checkin_reminder = payee.checkinReminder ? payee.checkinReminder : ''
        jsonData.send_summary = payee.sendSummary
        jsonData.allow_review_reply = payee.allowReviewReply
        jsonData.show_income = payee.showIncome
        jsonData.show_inbox = payee.showInbox
        jsonData.show_clients = payee.showClients
        jsonData.show_online = payee.showOnline
        jsonData.show_checkin = payee.showCheckin
        jsonData.send_checkin = payee.sendCheckin
        jsonData.show_selfie = payee.showSelfie
        jsonData.allow_pickup = payee.allowPickup
        jsonData.skills = payee.skills && payee.skills.length > 0 ? payee.skills : ''
        jsonData.color = payee.color ? payee.color : ''

        const me = PSApplication.me()
        if (
            !person.signupDate ||
            (person.type === 'payee' && person.employer && me.id === person.employer.id)
        ) {
            jsonData.name = person.name
            jsonData.role = person.role
            jsonData.phone = person.phone
            jsonData.bio = person.bio
            jsonData.notes = person.notes
            jsonData.email = person.email
            jsonData.postal_code = person.postalCode
            jsonData.entity = person.entity
            jsonData.line1 = person.line1
            jsonData.line2 = person.line2
            jsonData.city = person.city
            jsonData.state = person.state
            if (person.address) {
                jsonData.address = person.address
            }
            jsonData.capacity = person.capacity && person.capacity > 0 ? person.capacity : 1
            jsonData.reservation_capacity =
                person.reservationCapacity && person.reservationCapacity > 0
                    ? person.reservationCapacity
                    : 1
            jsonData.timezone = person.timezone ? person.timezone : ''
            jsonData.schedule = person.schedule ? person.schedule : ''
            jsonData.mon_start_hour = person.monStartHour ? person.monStartHour : ''
            jsonData.mon_end_hour = person.monEndHour ? person.monEndHour : ''
            jsonData.tue_start_hour = person.tueStartHour ? person.tueStartHour : ''
            jsonData.tue_end_hour = person.tueEndHour ? person.tueEndHour : ''
            jsonData.wed_start_hour = person.wedStartHour ? person.wedStartHour : ''
            jsonData.wed_end_hour = person.wedEndHour ? person.wedEndHour : ''
            jsonData.thu_start_hour = person.thuStartHour ? person.thuStartHour : ''
            jsonData.thu_end_hour = person.thuEndHour ? person.thuEndHour : ''
            jsonData.fri_start_hour = person.friStartHour ? person.friStartHour : ''
            jsonData.fri_end_hour = person.friEndHour ? person.friEndHour : ''
            jsonData.sat_start_hour = person.satStartHour ? person.satStartHour : ''
            jsonData.sat_end_hour = person.satEndHour ? person.satEndHour : ''
            jsonData.sun_start_hour = person.sunStartHour ? person.sunStartHour : ''
            jsonData.sun_end_hour = person.sunEndHour ? person.sunEndHour : ''
        }

        if (person.id) {
            jsonData.person = person.id
        }

        const url = payee.id ? `/payee/${payee.id}` : '/payee'
        try {
            const result = await apiClient.post(url, addImage(jsonData, image))

            const resultData = result.data
            const userData = await this.storeUser(resultData.person, true)
            await this.storePayee(resultData.payee)
            if (resultData.note) {
                await this.storeNote(resultData.note)
            }
            if (resultData.client) {
                await this.storeClient(resultData.client)
            }
            if (resultData.message) {
                await this.storeMessage(resultData.message)
            }
            if (resultData.subscription) {
                await this.storeSubscription(resultData.subscription)
            }
            const newUser = await this.fetchRecord(userData.id!, User)
            this.postNotification('PeopleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendInvite(person: User, message: string): Promise<Result<null>> {
        const url = `/payee/${person.id}/invite`
        try {
            const result = await apiClient.post(url, { message: message })

            return okResult(null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async provisionNumber(
        areaCode: string,
        backupAreaCode: string,
        portRequest: boolean
    ): Promise<Result<any>> {
        const jsonData: any = {}
        jsonData.sms = areaCode
        if (backupAreaCode) jsonData.area_code_backup = backupAreaCode
        if (portRequest) jsonData.port_in = true
        jsonData.person = PSApplication.userId()

        try {
            const result = await apiClient.post('/number', jsonData)

            const resultData = result.data
            if (resultData.number) await this.storeNumber(resultData.number)
            if (resultData.person) await this.storeUser(resultData.person)
            this.postNotification('NumberDataSourceChanged', null)
            return okResult(resultData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutPackage(booking: Booking, intent?: string): Promise<Result<Package>> {
        const json = booking.jsonObject()
        if (intent) json.intent = intent
        try {
            const result = await apiClient.post('/package/book', json)

            const resultData = result.data
            const newPackage = await this.storePackage(resultData.package, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.payee) await this.storePayee(resultData.payee)
            if (resultData.gift) await this.storeGift(resultData.gift)
            if (resultData.payment) await this.storePayment(resultData.payment)
            if (resultData.bookings) {
                for (const booking of resultData.bookings) await this.storeBooking(booking)
            }
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPackage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutGift(booking: Booking): Promise<Result<Gift>> {
        try {
            const result = await apiClient.post('/gift/book', booking.jsonObject())

            const resultData = result.data
            const newGift = await this.storeGift(resultData.gift, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.payee) await this.storePayee(resultData.payee)
            if (resultData.payment) await this.storePayment(resultData.payment)
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newGift)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutSubscription(booking: Booking): Promise<Result<Subscription>> {
        const jsonData: any = booking.jsonObject()
        if (booking.item?.deposit && booking.item.deposit > 0)
            jsonData.deposit = booking.item.deposit

        try {
            const result = await apiClient.post('/subscription/book', jsonData)

            const resultData = result.data
            const newSubscription = await this.storeSubscription(resultData.subscription, true)
            if (resultData.payment) await this.storePayment(resultData.payment)
            if (resultData.gift) await this.storeGift(resultData.gift)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutClass(booking: Booking, dontNotify: boolean): Promise<Result<Booking | null>> {
        const postInfo: any = booking.jsonObject()
        const lessons: string[] = []
        if (booking.lessons) {
            for (const lesson of booking.lessons) lessons.push(lesson.id!)
        }
        postInfo.lessons = lessons

        const resources = []
        if (booking.resources?.length) {
            for (const list of booking.resources) {
                resources.push(list.map((t) => t.jsonObject()))
            }
        }
        if (booking.employees && booking.employees.length > 0) {
            const l: string[][] = []
            for (const list of booking.employees) l.push(list.map((t) => t.id!))
            postInfo.employees = l
        }
        if (dontNotify) postInfo.dont_notify = true

        const url = `/class/${booking.item!.id}/schedule`
        try {
            const result = await apiClient.post(url, postInfo)

            const resultData = result.data
            let newBooking: Booking | null = null
            if (resultData.booking) newBooking = await this.storeBooking(resultData.booking, true)
            if (resultData.bookings) {
                for (const booking of resultData.bookings) await this.storeBooking(booking)
            }
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutBookings(booking: Booking, dontNotify: boolean): Promise<Result<Booking>> {
        const postInfo: any = booking.jsonObject()
        postInfo.dates = booking.dates.map((t) => formatTimeStamp(t))
        if (booking.employees?.length) {
            const l: string[][] = []
            for (const list of booking.employees) l.push(list.map((t) => t.id!))
            postInfo.employees = l
        }
        const resources = []
        if (booking.resources?.length) {
            for (const list of booking.resources) {
                resources.push(list.map((t) => t.jsonObject()))
            }
        }
        postInfo.resources = resources

        if (dontNotify) postInfo.dont_notify = true

        try {
            const result = await apiClient.post('/booking/schedule', postInfo)

            const resultData = result.data
            const newBooking = await this.storeBooking(resultData.booking, true)
            if (resultData.bookings) {
                for (const booking of resultData.bookings) await this.storeBooking(booking)
            }
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    private async store(
        type: string | undefined,
        resultData: any
    ): Promise<BaseRecord | string | undefined> {
        if (!type || !resultData) return undefined
        if (type.toLowerCase() === 'client') return this.storeClient(resultData)
        else if (type.toLowerCase() === 'user') return this.storeUser(resultData)
        else if (type.toLowerCase() === 'booking') return this.storeBooking(resultData)
        else if (type.toLowerCase() === 'invoice') return this.storeInvoice(resultData)
        else if (type.toLowerCase() === 'order') return this.storeOrder(resultData)
        else if (type.toLowerCase() === 'royalty') return this.storeRoyalty(resultData)
        else if (type.toLowerCase() === 'change') return this.storeChange(resultData)
        else if (type.toLowerCase() === 'package') return this.storePackage(resultData)
        // else if (type.toLowerCase() === 'refund') return this.storeRefund(resultData)
        else if (type.toLowerCase() === 'payment') return this.storePayment(resultData)
        else if (type.toLowerCase() === 'charge') return this.storeCharge(resultData)
        else if (type.toLowerCase() === 'event') return this.storeEvent(resultData)
        else if (type.toLowerCase() === 'team') return this.storeTeam(resultData)
        else if (type.toLowerCase() === 'gift') return this.storeGift(resultData)
        else if (type.toLowerCase() === 'message') return this.storeMessage(resultData)
        else if (type.toLowerCase() === 'campaign') return this.storeCampaign(resultData)
        else if (type.toLowerCase() === 'lesson') return this.storeClass(resultData)
        else if (type.toLowerCase() === 'contract') return this.storeContract(resultData)
        else if (type.toLowerCase() === 'subscription') return this.storeSubscription(resultData)
        else if (type.toLowerCase() === 'signature') return this.storeSignature(resultData)
        else if (type.toLowerCase() === 'subaccount') return this.storeSubaccount(resultData)
        else if (type.toLowerCase() === 'job') return this.storeJob(resultData)
        else if (type.toLowerCase() === 'note') return this.storeNote(resultData)
        else if (type.toLowerCase() === 'item') return this.storeItem(resultData)
        else if (type.toLowerCase() === 'record') return this.storeRecord(resultData)
        else if (type.toLowerCase() === 'payee') return this.storePayee(resultData)
        else if (type.toLowerCase() === 'estimate') return this.storeEstimate(resultData)
        else if (type.toLowerCase() === 'task') return this.storeTask(resultData)
        else if (type.toLowerCase() === 'type') return this.storeType(resultData)
        else if (type.toLowerCase() === 'field') return this.storeField(resultData)
        else if (type.toLowerCase() === 'image')
            return this.storeImage(new RecordDataMap(resultData))
        else if (type.toLowerCase() === 'deposit') return this.storeDeposit(resultData)
        else if (type.toLowerCase() === 'event') return this.storeEvent(resultData)
        else if (type.toLowerCase() === 'block') return this.storeBlock(resultData)
        else if (type.toLowerCase() === 'source') return this.storeSource(resultData)
        else if (type.toLowerCase() === 'taxcode') return this.storeTaxcode(resultData)
        else if (type.toLowerCase() === 'conference') return this.storeConference(resultData)
        else if (type.toLowerCase() === 'feed') return this.storeFeed(resultData)
        else if (type.toLowerCase() === 'post') return this.storePost(resultData)
        else if (type.toLowerCase() === 'edition') return this.storeEdition(resultData)
        else if (type.toLowerCase() === 'contract') return this.storeContract(resultData)
        else if (type.toLowerCase() === 'channel') return this.storeChannel(resultData)
        else if (type.toLowerCase() === 'trigger') return this.storeTrigger(resultData)
        else if (type.toLowerCase() === 'location') return this.storeLocation(resultData)
        else if (type.toLowerCase() === 'subscriber') return this.storeSubscriber(resultData)
        else if (type.toLowerCase() === 'tcr') return this.storeTCR(resultData)
        else if (type.toLowerCase() === 'review') return this.storeReview(resultData)
        else if (type.toLowerCase() === 'google_review')
            // do not store google review, just return in memory value for dashboard
            return new GoogleReview(resultData)
        else if (type.toLowerCase() === 'resource') return this.storeResource(resultData)
        else if (type.toLowerCase() === 'requirement') return this.storeRequirement(resultData)
        return undefined
    }

    async transferGift(gift: Gift, person: User, message: string): Promise<Result<Gift>> {
        const d: any = {}
        d.customer = person.id
        if (message) d.message = message

        try {
            const result = await apiClient.post(`/gift/${gift.id}/transfer`, d)

            const resultData = result.data
            const newGift = await this.storeGift(resultData.gift, true)
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newGift)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendGift(gift: Gift, dontNotify: boolean): Promise<Result<Gift>> {
        const jsonData = gift.jsonObject()
        if (dontNotify) jsonData.dont_notify = true
        const url = gift.id ? `/gift/${gift.id}` : '/gift'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newGift = await this.storeGift(resultData.gift, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newGift)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendAdjustment(adjustment: Adjustment): Promise<Result<Adjustment>> {
        const url = adjustment.id ? `/adjustment/${adjustment.id}` : '/adjustment'
        try {
            const result = await apiClient.post(url, adjustment.jsonObject())

            const resultData = result.data
            const newAdjustment = await this.storeAdjustment(resultData.adjustment, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newAdjustment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async transferPackage(
        aPackage: Package,
        person: User,
        message: string
    ): Promise<Result<Package>> {
        const d: any = {}
        d.customer = person.id
        if (message) d.message = message
        try {
            const result = await apiClient.post(`/package/${aPackage.id}/transfer`, d)

            const resultData = result.data
            const newPackage = await this.storePackage(resultData.package, true)
            if (resultData.messages) {
                for (const message of resultData.messages) await this.storeMessage(message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPackage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendPackage(aPackage: Package, dontNotify: boolean): Promise<Result<Package>> {
        const jsonData = aPackage.jsonObject()
        if (dontNotify) jsonData.dont_notify = true
        const url = aPackage.id ? `/package/${aPackage.id}` : '/package'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newPackage = await this.storePackage(resultData.package, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.subaccount) await this.storeSubaccount(resultData.subaccount)
            if (resultData.bookings) {
                for (const booking of resultData.bookings) await this.storeBooking(booking)
            }
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPackage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendSubscription(
        subscription: Subscription,
        dontNotify: boolean
    ): Promise<Result<Subscription>> {
        const jsonData = subscription.jsonObject()
        if (dontNotify) jsonData.dont_notify = true
        const url = subscription.id ? `/subscription/${subscription.id}` : '/subscription'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSubscription = await this.storeSubscription(resultData.subscription, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.subaccount) await this.storeSubaccount(resultData.subaccount)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendContract(contract: Contract): Promise<Result<Contract | null>> {
        const url = contract.id ? `/contract/${contract.id}` : '/contract'
        try {
            const result = await apiClient.post(url, contract.jsonObject())

            const resultData = result.data
            const newContract = resultData.contract
                ? await this.storeContract(resultData.contract, true)
                : null
            this.postNotification('ContractDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newContract)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendSignature(signature: Signature, sendOnly: boolean): Promise<Result<Signature>> {
        const url = signature.id ? `/signature/${signature.id}` : '/signature'
        let d = signature.jsonObject()
        if (sendOnly) {
            let channel = signature.channel
            if (!channel) {
                const person = signature.person!
                channel = person.e164 ? 'sms' : 'email'
                const client = await this.loadClient(person.id!) // client can be null in case of selfie
                if (client && client.notifyType) channel = client.notifyType
            }
            d = { id: signature.id, channel: channel }
        }
        try {
            const result = await apiClient.post(url, d)

            const resultData = result.data
            const newSignature = await this.storeSignature(resultData['signature'], true)
            const message = resultData['message']
                ? await this.storeMessage(resultData['message'])
                : null
            if (resultData['messages']) {
                for (const message of resultData['messages']) await this.storeMessage(message)
            }
            this.postNotification('MessagesDataSourceChanged', message)
            this.postNotification('ContractDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newSignature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendBlock(block: Block, changeAll: boolean): Promise<Result<Block>> {
        const jsonData = block.jsonObject()
        jsonData['change_all'] = changeAll
        if (changeAll) {
            const schedule = block.schedule!
            jsonData['instances'] = schedule.instances ? schedule.instances : ''
            if (schedule.interval && schedule.interval > 0) {
                jsonData['dow'] = schedule.dow
                jsonData['interval'] = schedule.interval
                jsonData['frequency'] = schedule.frequency
                if (schedule.wom && schedule.wom > 0) jsonData['wom'] = schedule.wom
            }
        }
        const url = block.id ? `/block/${block.id}` : '/block'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newBlock = await this.storeBlock(resultData['block'], true)
            if (resultData['schedules']) {
                for (const schedule of resultData['schedules']) await this.storeSchedule(schedule)
            }
            if (resultData['blocks']) {
                for (const blockData of resultData['blocks']) await this.storeBlock(blockData)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            return okResult(newBlock)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async confirmBooking(
        booking: Booking,
        confirmAll: boolean,
        pos: boolean,
        intent: string | undefined
    ): Promise<Result<Payment | Booking>> {
        const url = `/booking/${booking.id}/confirm`
        const postInfo: any = {}
        if (pos) postInfo['is_pos'] = true
        if (intent) postInfo['intent'] = intent
        if (confirmAll) postInfo['confirm_all'] = true
        if (booking.source) postInfo['source'] = booking.source.id
        if (booking.gratuity) postInfo['gratuity'] = booking.gratuity
        if (booking.gratuity && booking.gratuityType)
            postInfo['gratuity_type'] = booking.gratuityType
        try {
            const result = await apiClient.post(url, postInfo)

            const confirmData = result.data
            let newPayment: Payment | null = null
            const newBooking = await this.storeBooking(confirmData['booking'], true)
            const newBookings: Bookable[] = [newBooking]
            if (confirmData['payment'])
                newPayment = await this.storePayment(confirmData['payment'], true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (confirmData['client'])
                    await txManager.recordFactory.storeClient(confirmData['client'])
                if (confirmData['payee'])
                    await txManager.recordFactory.storePayee(confirmData['payee'])
                if (confirmData['invoice'])
                    await txManager.recordFactory.storeInvoice(confirmData['invoice'])
                if (confirmData['messages']) {
                    for (const message of confirmData['messages'])
                        await txManager.recordFactory.storeMessage(message)
                }
                if (confirmData['classes'] && confirmData['classes']['bookings']) {
                    for (const bookingData of confirmData['classes']['bookings'])
                        await txManager.recordFactory.storeBooking(bookingData)
                }
                if (confirmData['package'])
                    await txManager.recordFactory.storePackage(confirmData['package'])
                if (confirmData['schedule'])
                    await txManager.recordFactory.storeSchedule(confirmData['schedule'])
                if (confirmData['bookings']) {
                    for (const bookingData of confirmData['bookings'])
                        newBookings.push(
                            await txManager.recordFactory.storeBooking(bookingData, true)
                        )
                }
            })

            this.postNotification('ScheduleDataSourceChanged', newBooking)
            this.postNotification('DashboardDataSourceChanged', newBooking)

            await this.pushBookings(newBookings)
            return okResult(newPayment ? newPayment : newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async changeBooking(
        booking: Booking,
        dontNotify: boolean,
        changeAll: boolean
    ): Promise<Result<Booking>> {
        const vendor = booking.vendor!
        const url = `/booking/${booking.id}`
        const jsonData: any = {}
        const jsonObject = booking.jsonObject()
        jsonData.date = formatTimeStamp(booking.date)
        if (booking.item && booking.item.type === 'reservation') {
            jsonData.end_date = formatTimeStamp(booking.endDate)
            // TODO: port Reservations 2.0
            // jsonData.reserve_charge_by = booking.reserveChargeBy
            // jsonData.reserve_checkin_type = booking.reserveCheckinType
            // jsonData.reserve_checkout_type = booking.reserveCheckoutType
            // jsonData.reserve_checkin_duration = booking.reserveCheckinDuration
            // jsonData.reserve_checkout_duration = booking.reserveCheckoutDuration
        }
        jsonData.vendor = vendor.id
        jsonData.rate = booking.rate || ''
        jsonData.item = booking.item ? booking.item.id : ''
        if (vendor.allowDiscount) {
            if (vendor.allowGift) jsonData.gift = booking.gift ? booking.gift.id : ''
            jsonData.discount = booking.discount || ''
            jsonData.discount_type = booking.discountType || ''
        }
        if (booking.allowSalesTax) {
            jsonData.taxcode = booking.taxcode ? booking.taxcode.id : ''
            jsonData.tax = booking.tax || ''
            jsonData.nexus = booking.nexus || ''
        }
        else {
            jsonData.taxcode = ''
            jsonData.tax = ''
            jsonData.nexus = ''
        }
        if (dontNotify) jsonData.dont_notify = true
        jsonData.form = booking.form ? booking.form.id : ''
        // jsonData.project = booking.project ? booking.project.id : ''
        jsonData.contract = booking.contract ? booking.contract.id : ''
        jsonData.duration = booking.duration || ''
        jsonData.employee = booking.employee ? booking.employee.id : ''
        if (vendor.allowSurcharge) jsonData.allow_surcharge = booking.allowSurcharge
        jsonData.allow_conference = booking.allowConference
        jsonData.conference_type = booking.conferenceType
        jsonData.jobs = jsonObject.jobs
        jsonData.lines = jsonObject.lines
        jsonData.allocations = jsonObject.allocations
        if (booking.paidDepositDate === undefined) {
            jsonData.deposit = booking.deposit || ''
            jsonData.package = booking.package ? booking.package.id : ''
        }
        jsonData.location = booking.location || ''
        jsonData.line1 = booking.line1 || ''
        jsonData.line2 = booking.line2 || ''
        jsonData.city = booking.city || ''
        jsonData.state = booking.state || ''
        jsonData.postal_code = booking.postalCode || ''
        jsonData.country_code = booking.countryCode || ''
        jsonData.memo = booking.memo || ''
        const schedule = changeAll ? booking.schedule : undefined
        if (schedule) {
            if (schedule.interval && schedule.interval !== 0) {
                jsonData.interval = schedule.interval
                jsonData.dow = schedule.dow !== 0 ? schedule.dow : ''
                jsonData.frequency = schedule.frequency
                if (schedule.wom && schedule.wom !== 0) jsonData.wom = schedule.wom
            }
            else if (booking.dates.length > 0 && booking.forSchedule) {
                jsonData.interval = ''
                jsonData.date_values = booking.dates.map((date) => formatTimeStamp(date))
                // jsonData.end_date_values = booking.endDates.map((endDate) => formatTimeStamp(endDate))
                if (booking.employees?.length) {
                    const l: string[][] = []
                    for (const list of booking.employees) l.push(list.map((user) => user.id!))
                    jsonData.employees = l
                }
                if (booking.resources?.length) {
                    const resources = []
                    for (const list of booking.resources) {
                        resources.push(list.map((resource) => resource.jsonObject()))
                    }
                    jsonData.resources = resources
                }
            }
            jsonData.change_all = true
            jsonData.instances = schedule.instances || ''
        }
        jsonData.field_values = Field.serializeFieldData(
            booking.fieldValues,
            booking.owner!.id,
            undefined
        )
        if (booking.status === 'sentQuote') jsonData.status = booking.status
        jsonData.confirm_status = booking.confirmStatus || ''
        jsonData.rebook = booking.rebook || ''
        jsonData.channel = booking.channel || ''
        jsonData.reminder = booking.reminder || ''

        try {
            const result = await apiClient.post(url, jsonData)

            const changeData = result.data
            const messagesArray: Message[] = []
            const newBooking = await this.storeBooking(changeData.booking, true)
            const newBookings: Booking[] = [newBooking]
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (changeData.message)
                    messagesArray.push(
                        await txManager.recordFactory.storeMessage(changeData.message, true)
                    )
                if (changeData.schedules) {
                    for (const schedule of changeData.schedules)
                        await txManager.recordFactory.storeSchedule(schedule)
                }
                if (changeData.bookings) {
                    for (const booking of changeData.bookings)
                        newBookings.push(await txManager.recordFactory.storeBooking(booking, true))
                }
                if (changeData.package)
                    await txManager.recordFactory.storePackage(changeData.package)
                if (changeData.messages) {
                    for (const message of changeData.messages)
                        messagesArray.push(
                            await txManager.recordFactory.storeMessage(message, true)
                        )
                }
            })
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            await this.pushBookings(newBookings)
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async chargeBooking(
        booking: Booking,
        pos: boolean,
        source: Source | undefined,
        intent: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined
    ): Promise<Result<Payment | Booking>> {
        const postInfo: any = {}
        postInfo.rate = booking.rate || '0'
        postInfo.type = 'pay'
        if (pos) {
            postInfo.is_pos = true
            if (intent) postInfo.intent = intent
            if (source) postInfo.source = source.id
        }
        if (booking.lines) {
            const jsonLines: any[] = []
            for (const line of booking.lines) jsonLines.push(line.jsonObject())
            postInfo.lines = jsonLines
        }
        if (gratuity) postInfo.gratuity = gratuity
        if (gratuity && gratuityType) postInfo.gratuity_type = gratuityType
        postInfo.allow_surcharge = booking.allowSurcharge
        if (booking.allowSalesTax) {
            postInfo.taxcode = booking.taxcode ? booking.taxcode.id : ''
            postInfo.tax = booking.tax || ''
            postInfo.nexus = booking.nexus || ''
            postInfo.line1 = booking.line1 || ''
            postInfo.line2 = booking.line2 || ''
            postInfo.city = booking.city || ''
            postInfo.state = booking.state || ''
            postInfo.postal_code = booking.postalCode || ''
            postInfo.country_code = booking.countryCode || ''
        }
        else {
            postInfo.taxcode = ''
            postInfo.tax = ''
            postInfo.nexus = ''
        }
        if (booking.discount) {
            postInfo.discount = booking.discount
            postInfo.discount_type = booking.discountType
        }
        else {
            postInfo.discount = ''
            postInfo.discount_type = ''
        }
        const url = `/booking/${booking.id}/complete`
        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            let newPayment: Payment | null = null
            const newBooking = await this.storeBooking(completeData.booking, true)
            if (completeData.payment)
                newPayment = await this.storePayment(completeData.payment, true)
            if (completeData.client) await this.storeClient(completeData.client)
            if (completeData.payee) await this.storePayee(completeData.payee)
            if (completeData.package) await this.storePackage(completeData.package)
            if (completeData.message) await this.storeMessage(completeData.message)
            if (completeData.messages) {
                for (const message of completeData.messages) await this.storeMessage(message)
            }
            // if (completeData.messages || completeData.message)
            //     this.postNotification('MessagesDataSourceChanged', null);
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            await this.pushBookings([newBooking])
            return okResult(newPayment ? newPayment : newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markBooking(booking: Booking, sendReceipt: boolean): Promise<Result<Booking>> {
        const postInfo: any = {}
        postInfo.rate = booking.rate || '0'
        postInfo.type = 'mark'
        postInfo.is_pos = false
        if (booking && booking.markPaidDate)
            postInfo.mark_paid_date = formatTimeStamp(booking.markPaidDate)
        if (booking.lines) {
            const jsonLines: any[] = []
            for (const line of booking.lines) jsonLines.push(line.jsonObject())
            postInfo.lines = jsonLines
        }
        if (booking && booking.refnum) postInfo.refnum = booking.refnum
        if (booking && booking.paymeth) postInfo.paymeth = booking.paymeth
        if (booking && booking.gratuity) postInfo.gratuity = booking.gratuity
        if (booking && booking.gratuity && booking.gratuityType)
            postInfo.gratuity_type = booking.gratuityType
        if (booking && booking.discount) postInfo.discount = booking.discount
        if (booking && booking.discount && booking.discountType)
            postInfo.discount_type = booking.discountType
        if (sendReceipt) postInfo.send_receipt = true
        if (booking.allowSalesTax) {
            postInfo.taxcode = booking.taxcode ? booking.taxcode.id : ''
            postInfo.tax = booking.tax !== 0.0 ? booking.tax : ''
            postInfo.nexus = booking.nexus || ''
            postInfo.line1 = booking.line1 || ''
            postInfo.line2 = booking.line2 || ''
            postInfo.city = booking.city || ''
            postInfo.state = booking.state || ''
            postInfo.postal_code = booking.postalCode || ''
            postInfo.country_code = booking.countryCode || ''
        }
        else {
            postInfo.taxcode = ''
            postInfo.tax = ''
            postInfo.nexus = ''
        }
        const url = `/booking/${booking.id}/complete`

        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            const newBooking = await this.storeBooking(completeData.booking, true)
            if (completeData.client) await this.storeClient(completeData.client)
            if (completeData.payee) await this.storePayee(completeData.payee)
            if (completeData.package) await this.storePackage(completeData.package)
            if (completeData.message) await this.storeMessage(completeData.message)
            if (completeData.messages) {
                for (const message of completeData.messages) await this.storeMessage(message)
            }
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            await this.pushBookings([newBooking])
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelBooking(
        booking: Booking,
        dontNotify: boolean,
        cancelAll: boolean,
        cancellationType?: string,
        cancellationMemo?: string,
        enforce?: boolean
    ): Promise<Result<Payment | Booking>> {
        const postInfo: any = {}
        if (cancelAll) postInfo.cancel_all = true
        if (dontNotify) postInfo.dont_notify = true
        if (cancellationType) postInfo.cancellation_type = cancellationType
        if (cancellationMemo) postInfo.cancellation_memo = cancellationMemo
        if (enforce) postInfo.enforce = true
        const url = `/booking/${booking.id}/cancel`

        try {
            const result = await apiClient.post(url, postInfo)

            const cancelData = result.data
            const newPayment = cancelData.payment
                ? await this.storePayment(cancelData.payment, true)
                : undefined
            const newBooking = await this.storeBooking(cancelData.booking, true)
            if (cancelData.estimate) await this.storeEstimate(cancelData.estimate)
            if (cancelData.package) await this.storePackage(cancelData.package)
            if (cancelData.messages) {
                for (const message of cancelData.messages) await this.storeMessage(message)
            }
            if (cancelData.classes) {
                const classesData = cancelData.classes
                if (classesData.bookings) {
                    for (const booking of classesData.bookings) await this.storeBooking(booking)
                }
            }
            if (cancelData.schedule) await this.storeSchedule(cancelData.schedule)
            if (cancelData.bookings) {
                for (const booking of cancelData.bookings) await this.storeBooking(booking)
            }
            this.postNotification('ScheduleDataSourceChanged', newBooking)
            this.postNotification('DashboardDataSourceChanged', newBooking)
            return okResult(newPayment ? newPayment : newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async promoteBooking(booking: Booking): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/promote`

        try {
            const result = await apiClient.post(url, null)

            const resultData = result.data
            const newBooking = await this.storeBooking(resultData.booking, true)
            this.postNotification('JobDataSourceChanged', null)
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async changeBookingSource(booking: Booking, source: Source): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/source`

        try {
            const result = await apiClient.post(url, { source: source.id })

            const bookingsArray: Booking[] = []
            const bookingsData = result.data
            for (const booking of bookingsData)
                bookingsArray.push(await this.storeBooking(booking, true))
            return okResult(bookingsArray[0])
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markJobPaid(job: Job): Promise<Result<any>> {
        const url = `/job/${job.id}/mark`

        try {
            const result = await apiClient.post(url, null)

            await this.storeJob(result.data.job)
            if (result.data.booking) {
                await this.storeBooking(result.data.booking)
                this.postNotification('ScheduleDataSourceChanged', null)
            }
            this.postNotification('JobDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(await this.fetchRecord(job.id!, Job))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendJob(job: Job): Promise<Result<any>> {
        const url = `/job/${job.id}`

        try {
            const result = await apiClient.post(url, job.jsonObject())

            await this.storeJob(result.data.job)

            this.postNotification('JobDataSourceChanged', null)
            return okResult(await this.fetchRecord(job.id!, Job))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutJob(
        job: Job,
        image?: File,
        longitude?: number,
        latitude?: number
    ): Promise<Result<Booking>> {
        const jsonData: any = {}
        if (latitude) jsonData.latitude = latitude
        if (longitude) jsonData.longitude = longitude
        const url = `/job/${job.id}/checkout`

        try {
            const result = await apiClient.post(url, image ? addImage(jsonData, image) : jsonData)

            const resultData = result.data
            const newBooking = await this.storeBooking(resultData.booking, true)
            if (resultData.messages) {
                await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                    for (const message of resultData.messages)
                        await txManager.recordFactory.storeMessage(message)
                })
            }
            this.postNotification('JobDataSourceChanged', null)
            // if (resultData.messages)
            //     this.postNotification('MessagesTabDataSourceChanged', null);
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkinJob(
        job: Job,
        image?: File,
        longitude?: number,
        latitude?: number
    ): Promise<Result<Booking>> {
        const jsonData: any = {}
        if (latitude) jsonData.latitude = latitude
        if (longitude) jsonData.longitude = longitude
        const url = `/job/${job.id}/checkin`

        try {
            const result = await apiClient.post(url, image ? addImage(jsonData, image) : jsonData)

            const resultData = result.data
            const newBooking = await this.storeBooking(resultData.booking, true)
            if (resultData.messages) {
                await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                    for (const message of resultData.messages)
                        await txManager.recordFactory.storeMessage(message)
                })
            }
            this.postNotification('JobDataSourceChanged', null)
            // if (resultData.messages)
            //     this.postNotification('MessagesTabDataSourceChanged', null);
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkinBooking(booking: Booking): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/checkin`

        try {
            const result = await apiClient.post(url, undefined)
            return await this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async undoCheckinBooking(booking: Booking): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/checkin/undo`

        try {
            const result = await apiClient.post(url, undefined)
            return this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async checkoutBooking(booking: Booking): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/checkout`

        try {
            const result = await apiClient.post(url, undefined)
            return this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async undoCheckoutBooking(booking: Booking): Promise<Result<Booking>> {
        const url = `/booking/${booking.id}/checkout/undo`

        try {
            const result = await apiClient.post(url, undefined)
            return this.handleBookingResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async assignBooking(booking: Booking): Promise<VoidResult> {
        const url = `/booking/${booking.id}/assign`

        try {
            const result = await apiClient.post(url, null)
            await this.handleBookingResult(result)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelInvoice(invoice: Invoice, cancelAll: boolean): Promise<Result<Invoice>> {
        const postInfo: any = {}
        postInfo.cancel_all = cancelAll
        const url = `/invoice/${invoice.id}/cancel`

        try {
            const result = await apiClient.post(url, postInfo)

            const cancelData = result.data
            const newInvoice = await this.storeInvoice(cancelData.invoice, true)

            if (cancelData.estimate) await this.storeEstimate(cancelData.estimate)
            if (cancelData.package) await this.storePackage(cancelData.package)

            if (cancelData.message) {
                await this.storeMessage(cancelData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            if (cancelData.bookings) {
                for (const booking of cancelData.bookings) await this.storeBooking(booking)
            }
            this.postNotification('DashboardDataSourceChanged', newInvoice)
            return okResult(newInvoice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeInvoice(
        invoice: Invoice,
        refnum: string | undefined,
        paymeth: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined,
        markPaidDate: moment.Moment,
        receipt: boolean
    ): Promise<Result<Invoice>> {
        const postInfo: any = {}
        postInfo.refnum = refnum || ''
        postInfo.paymeth = paymeth || ''
        postInfo.mark_paid_date = formatTimeStamp(markPaidDate)
        if (gratuity) postInfo.gratuity = gratuity
        if (gratuity && gratuityType) postInfo.gratuity_type = gratuityType
        if (receipt) postInfo.send_receipt = '1'
        const url = `/invoice/${invoice.id}/mark`

        try {
            const result = await apiClient.post(url, postInfo)

            const markPaidData = result.data
            const newInvoice = await this.storeInvoice(markPaidData.invoice, true)
            return okResult(newInvoice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelGift(gift: Gift): Promise<Result<Gift>> {
        const postInfo: any = {}
        const url = `/gift/${gift.id}/cancel`

        try {
            const result = await apiClient.post(url, postInfo)

            const cancelData = result.data
            const newGift = await this.storeGift(cancelData.gift, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newGift)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeGift(
        giftRef: Gift,
        refnum: string | undefined,
        paymeth: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined,
        markPaidDate: moment.Moment,
        receipt: boolean
    ): Promise<Result<Gift>> {
        const postInfo: any = {}
        postInfo.refnum = refnum || ''
        postInfo.paymeth = paymeth || ''
        postInfo.mark_paid_date = formatTimeStamp(markPaidDate)
        if (gratuity) postInfo.gratuity = gratuity
        if (gratuity && gratuityType) postInfo.gratuity_type = gratuityType
        if (receipt) postInfo.send_receipt = '1'
        const url = `/gift/${giftRef.id}/complete`

        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            const newGift = await this.storeGift(completeData.gift, true)
            return okResult(newGift)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async releasePackage(booking: Booking): Promise<Result<Booking | null>> {
        const postInfo: any = {}
        const url = `/package/${booking.id}/release`

        try {
            const result = await apiClient.post(url, postInfo)

            const resultData = result.data
            const newBooking =
                resultData.length > 0 ? await this.storeBooking(resultData[0], true) : null
            if (newBooking) {
                this.postNotification('ScheduleDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async applyPackage(booking: Booking, aPackage: Package): Promise<Result<Booking>> {
        const url = `/package/${aPackage.id}/apply`
        try {
            const result = await apiClient.post(url, { booking: booking.id })

            const resultData = result.data
            const newBooking = await this.storeBooking(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newBooking)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelPackage(packageRef: Package): Promise<Result<Package>> {
        const url = `/package/${packageRef.id}/cancel`
        try {
            const result = await apiClient.post(url, {})

            const cancelData = result.data
            const newPackage = await this.storePackage(cancelData.package, true)
            if (cancelData.bookings)
                for (const booking of cancelData.bookings) await this.storeBooking(booking)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPackage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markCharge(record: Payment): Promise<Result<Charge>> {
        const postData = record.jsonObject()
        postData.dont_notify = !record.sendReceipt
        try {
            const result = await apiClient.post('/mark', postData)

            const resultData = result.data
            const newCharge = await this.storeCharge(resultData.charge, true)
            if (resultData.client) await this.storeClient(resultData.client)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newCharge)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completePackage(
        aPackage: Package,
        refnum: string | undefined,
        paymeth: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined,
        markPaidDate: moment.Moment,
        receipt: boolean
    ): Promise<Result<Package>> {
        const postInfo: any = {}
        postInfo.refnum = refnum || ''
        postInfo.paymeth = paymeth || ''
        postInfo.mark_paid_date = formatTimeStamp(markPaidDate)
        if (gratuity) postInfo.gratuity = gratuity
        if (gratuity && gratuityType) postInfo.gratuity_type = gratuityType
        if (receipt) postInfo.send_receipt = true

        const url = `/package/${aPackage.id}/complete`
        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            const newPackage = await this.storePackage(completeData.package, true)
            if (completeData.message) await this.storeMessage(completeData.message)
            if (completeData.bookings)
                for (const booking of completeData.bookings) await this.storeBooking(booking)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPackage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPaidPackages(client: User, item?: Item): Promise<Result<Package[]>> {
        try {
            const result = await apiClient.post('/packages/query', {
                client: client.id,
                item: item?.id,
            })

            const packages: Package[] = []
            const resultData = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const aPackage of resultData)
                    packages.push(await txManager.recordFactory.storePackage(aPackage, true))
                if (resultData?.length) console.debug(`SYNC: Synced ${resultData.length} packages`)
            })
            return okResult(packages)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelEstimate(estimate: Estimate): Promise<Result<Estimate>> {
        const postInfo: any = {}
        const url = `/estimate/${estimate.id}/cancel`
        try {
            const result = await apiClient.post(url, postInfo)

            const cancelData = result.data
            const newEstimate = await this.storeEstimate(cancelData.estimate, true)
            if (cancelData.message) await this.storeMessage(cancelData.message)
            this.postNotification('DashboardDataSourceChanged', newEstimate)
            if (cancelData.message) {
                return okResult(newEstimate)
            }
            else {
                return errorResult()
            }
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeTask(idString: string): Promise<Result<Task>> {
        const url = `/task/${idString}/complete`
        try {
            const result = await apiClient.post(url, {})
            const resultData = result.data
            const newTask = await this.storeTask(resultData.task, true)
            if (resultData.message) {
                await this.storeMessage(resultData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newTask)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markEstimate(
        estimate: Estimate,
        refnum: string | undefined,
        paymeth: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined,
        markPaidDate: moment.Moment,
        receipt: boolean
    ): Promise<Result<Estimate>> {
        const postInfo: any = {}
        postInfo.refnum = refnum || ''
        postInfo.paymeth = paymeth || ''
        postInfo.mark_paid_date = formatTimeStamp(markPaidDate)
        if (gratuity) postInfo.gratuity = gratuity
        if (gratuity && gratuityType) postInfo.gratuity_type = gratuityType
        if (receipt) postInfo.send_receipt = true

        const url = `/estimate/${estimate.id}/mark`
        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            const newEstimate = await this.storeEstimate(completeData.estimate, true)
            if (completeData.client) await this.storeClient(completeData.client)
            if (completeData.payee) await this.storePayee(completeData.payee)
            if (completeData.message) {
                await this.storeMessage(completeData.message)
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newEstimate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async chargeEstimate(
        estimate: Estimate,
        pos: boolean,
        source: Source | undefined,
        intent: string | undefined,
        gratuity: number | undefined,
        gratuityType: string | undefined
    ): Promise<Result<Payment | Estimate>> {
        const postInfo: any = {}
        const url = `/estimate/${estimate.id}/charge`
        postInfo['rate'] = estimate.rate ? estimate.rate : '0'
        if (pos) {
            postInfo['is_pos'] = true
            if (intent) postInfo['intent'] = intent
            if (source) postInfo['source'] = source.id
        }
        if (gratuity) postInfo['gratuity'] = gratuity
        if (gratuity && gratuityType) postInfo['gratuity_type'] = gratuityType

        try {
            const result = await apiClient.post(url, postInfo)

            const completeData = result.data
            const newEstimate = await this.storeEstimate(completeData['estimate'], true)
            let newPayment: Payment | null = null
            if (completeData['payment'])
                newPayment = await this.storePayment(completeData['payment'], true)
            if (completeData['client']) await this.storeClient(completeData['client'])
            if (completeData['payee']) await this.storePayee(completeData['payee'])
            if (completeData['message']) {
                await this.storeMessage(completeData['message'])
                this.postNotification('MessagesDataSourceChanged', null)
            }
            this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPayment ? newPayment : newEstimate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async confirmEstimate(
        estimate: Estimate,
        pos: boolean,
        intent: string | undefined
    ): Promise<Result<Payment | Estimate>> {
        const postInfo: any = {}
        const url = `/estimate/${estimate.id}/confirm`
        if (pos) postInfo['is_pos'] = true
        if (intent) postInfo['intent'] = intent
        postInfo['source'] = estimate.source ? estimate.source.id : ''
        if (estimate.gratuity) {
            postInfo['gratuity'] = estimate.gratuity
            postInfo['gratuity_type'] = estimate.gratuityType
        }

        try {
            const result = await apiClient.post(url, postInfo)

            const confirmData = result.data
            const newEstimate = await this.storeEstimate(confirmData['estimate'], true)
            let newPayment: Payment | null = null
            if (confirmData['payment'])
                newPayment = await this.storePayment(confirmData['payment'], true)
            if (confirmData['invoice']) await this.storeInvoice(confirmData['invoice'])
            if (confirmData['client']) await this.storeClient(confirmData['client'])
            if (confirmData['payee']) await this.storePayee(confirmData['payee'])
            if (confirmData['messages']) {
                const messagesArray: Message[] = []
                const messages = confirmData['messages']
                for (const message of messages) {
                    messagesArray.push(await this.storeMessage(message, true))
                }
                this.postNotification('MessagesDataSourceChanged', messagesArray[0])
            }
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPayment ? newPayment : newEstimate)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markRead(message: Message): Promise<Result<any>> {
        const startSync = moment()

        const url = `/message/${message.id}/mark`
        try {
            const result = await apiClient.post(url, null)

            const results = result.data
            const messageData = results['messages']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    messageData,
                    txManager.recordFactory.storeMessage
                )

                if (messageData?.length)
                    console.debug(`SYNC: Synced ${messageData.length} messages`)
                await txManager.setBadgeCount(results['badge'])
                await txManager.setRoleBadgeCount(results['role_badge'])
                console.debug(`markRead Time (ms): ${moment().diff(startSync)}`)
            })
            this.postNotification('MessagesDataSourceChanged', messageData)
            this.postNotification('MessagesTabDataSourceChanged', null)
            return okResult(results)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markUnread(message: Message): Promise<Result<Message>> {
        const url = `/message/${message.id}/unmark`
        try {
            const result = await apiClient.post(url, null)

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData['message'], true)
            await this.executor.setBadgeCount(resultData['badge'])
            await this.executor.setRoleBadgeCount(resultData['role_badge'])
            this.postNotification('MessagesTabDataSourceChanged', null)
            return okResult(newMessage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async notifyTyping(threadId: string, startTyping: boolean): Promise<Result<any>> {
        const postInfo: any = {}
        postInfo['thread'] = threadId
        postInfo['start_typing'] = startTyping
        try {
            return okResult(await apiClient.post('/message/typing', postInfo))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    private async didBookingSearch(result: any): Promise<Result<RecordDataMap[]>> {
        const itemData = result['items']
        const userData = result['users']
        const classData = result['lessons']
        const resultData = result['results']
        const l: RecordDataMap[] = []
        await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
            await txManager.recordFactory.storeAll(itemData, txManager.recordFactory.storeItem)
            await txManager.recordFactory.storeAll(userData, txManager.recordFactory.storeUser)
            await txManager.recordFactory.storeAll(classData, txManager.recordFactory.storeClass)
            for (const result of resultData) {
                const d = new RecordDataMap(result)
                const person = await txManager.recordFactory.storeUser(
                    d.valueForDictionaryKey('person'),
                    true
                )
                const availabilities = d.valueForListKey('availability')
                const availabilityData: Availability[] = []
                if (availabilities)
                    for (const availability of availabilities)
                        availabilityData.push(new Availability(availability))
                const info = new RecordDataMap()
                info.put('type', d.hasValueForKey('type') ? d.valueForKey('type') : 'availability')
                info.put('person', person.id)
                info.put('availability', availabilityData)
                l.push(info)
            }
        })
        return okResult(l)
    }

    async checkConflicts(
        bookable: Bookable,
        date: moment.Moment
    ): Promise<Result<RecordDataMap[]>> {
        const booking = bookable instanceof Booking ? bookable : null
        const order = bookable instanceof Order ? bookable : null
        const lesson = bookable instanceof Lesson ? bookable : null

        if ((!booking && !order && !lesson) || !date) {
            return okResult([])
        }

        const url = '/availability/client'
        const jsonData: any = {}

        let duration = bookable.duration
        if (!duration || duration === 0) {
            if (booking && booking.service) {
                duration = booking.service.duration
            }
        }
        if (!duration) {
            duration = 0
        }

        jsonData['start'] = formatTimeStamp(date)
        jsonData['end'] = formatTimeStamp(date.add(duration, 'minutes'))
        jsonData['duration'] = duration

        if (booking && booking.leadtime !== undefined) {
            jsonData['leadtime'] = booking.leadtime
        }
        else if (order && order.leadtime !== undefined) {
            jsonData['leadtime'] = order.leadtime
        }

        if (bookable.id) {
            jsonData['booking'] = bookable.id
        }

        if (booking && booking.jobs && booking.jobs.length > 0) {
            const l: string[] = []
            for (const job of booking.jobs) l.push(job.person!.id!)
            jsonData['employee'] = l
        }
        else if (order && order.employee) {
            jsonData['employee'] = order.employee.id
        }
        else if (lesson && lesson.employee) {
            jsonData['employee'] = lesson.employee.id
        }

        try {
            const result = await apiClient.post(url, jsonData)
            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchAvailability(
        booking: Booking,
        date: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        item: Item | undefined,
        location: string | undefined,
        duration: number | undefined,
        client: User | undefined,
        forSummary: boolean | undefined,
        filter: CalendarFilter | undefined
    ): Promise<Result<RecordDataMap[]>> {
        const url = '/availability/client'
        const jsonData: any = {}
        jsonData['start'] = date ? formatTimeStamp(date) : ''
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        if (item && item.id) jsonData['item'] = item.id
        if (client && client.id) jsonData['client'] = client.id
        jsonData['location'] = location ? location : ''
        if ((duration === null || duration === 0) && booking.service)
            duration = booking.service.duration
        jsonData['duration'] = duration ? duration : ''
        if (booking && booking.leadtime) jsonData['leadtime'] = booking.leadtime
        if (booking && booking.id) jsonData['booking'] = booking.id
        if (filter?.hasPeople) jsonData['employee'] = filter.pros
        if (filter?.hasResources) jsonData['resource'] = filter.resources
        if (booking.dates.length > 0 && !forSummary) {
            const dates: string[] = []
            for (const d of booking.dates) dates.push(formatTimeStamp(d))
            jsonData['dates'] = dates
        }
        else if (booking.date && !forSummary) jsonData['dates'] = [formatTimeStamp(booking.date)]

        try {
            const result = await apiClient.post(url, jsonData)

            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchEmployeeAvailability(
        booking: Booking,
        date: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        item: Item | undefined,
        employee: User | undefined,
        location: string | undefined,
        duration: number | undefined,
        vendor: User | undefined,
        forSummary: boolean | undefined
    ): Promise<Result<RecordDataMap[]>> {
        const url = '/availability/vendor'
        const jsonData: any = {}
        if (date) jsonData['start'] = formatTimeStamp(date)
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        if (item?.id) jsonData['item'] = item.id
        if (vendor?.id) jsonData['vendor'] = vendor.id
        if (employee?.id) jsonData['employee'] = employee.id
        if ((duration === undefined || duration === 0) && booking.service)
            duration = booking.service.duration
        jsonData['duration'] = duration ? duration : ''
        if (location) jsonData['location'] = location
        if (booking.service?.id) jsonData['service'] = booking.service.id
        if (booking.leadtime) jsonData['leadtime'] = booking.leadtime
        try {
            const result = await apiClient.post(url, jsonData)
            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchClassAvailability(
        date: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        forVendor: boolean | undefined,
        item: Item | undefined,
        location: string | undefined,
        vendor: User | undefined,
        forSummary: boolean | undefined
    ): Promise<Result<RecordDataMap[]>> {
        const url = '/availability/class'
        const jsonData: any = {}
        if (date) jsonData['start'] = formatTimeStamp(date)
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        if (forVendor) jsonData['for_vendor'] = true
        if (item) jsonData['item'] = item.id
        if (vendor) jsonData['vendor'] = vendor.id
        jsonData['location'] = location ? location : ''

        try {
            const result = await apiClient.post(url, jsonData)

            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async bookEmployee(
        booking: Booking,
        date: moment.Moment,
        endDate: moment.Moment,
        forVendor: boolean,
        item: Item,
        location: string,
        duration: number | undefined,
        forSummary: boolean
    ): Promise<Result<RecordDataMap[]>> {
        const url =
            item && item.type === 'reservation' ? '/availability/reserve' : '/availability/vendor'
        const jsonData: any = {}
        if (date) jsonData['start'] = formatTimeStamp(date)
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        jsonData['item'] = item ? item.id : ''
        if (!forVendor && booking.vendor && booking.vendor.allowStaffing) {
            jsonData['staff'] = true
            jsonData['vendor'] = booking.vendor.id
        }
        else if (forVendor) {
            jsonData['staff'] = 'all'
            jsonData['strict'] = true
        }
        if ((duration === null || duration === 0) && booking.service)
            duration = booking.service.duration
        jsonData['duration'] = duration ? duration : ''
        jsonData['location'] = location ? location : ''
        if (booking.leadtime) jsonData['leadtime'] = booking.leadtime

        try {
            const result = await apiClient.post(url, jsonData)

            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchReservationAvailability(
        booking: Booking,
        date: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        forVendor: boolean | undefined,
        item: Item | undefined,
        location: string | undefined,
        duration: number | undefined,
        forSummary: boolean | undefined,
        filter: CalendarFilter | undefined
    ): Promise<Result<RecordDataMap[]>> {
        const url = '/availability/reserve'
        const jsonData: any = {}
        if (date) jsonData['start'] = formatTimeStamp(date)
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        if (forVendor) jsonData['for_vendor'] = true
        if (item?.id) jsonData['item'] = item.id
        if (filter?.hasPeople) jsonData['employee'] = filter.pros
        if (filter?.hasResources) jsonData['resource'] = filter.resources
        jsonData['vendor'] = booking.vendor ? booking.vendor.id : ''
        jsonData['duration'] = duration ? duration : ''
        if (!!location) jsonData['location'] = location
        jsonData['line1'] = booking.line1 ? booking.line1 : ''
        jsonData['line2'] = booking.line2 ? booking.line2 : ''
        jsonData['city'] = booking.city ? booking.city : ''
        jsonData['state'] = booking.state ? booking.state : ''
        jsonData['postal_code'] = booking.postalCode ? booking.postalCode : ''
        jsonData['country_code'] = booking.countryCode ? booking.countryCode : ''
        try {
            const result = await apiClient.post(url, jsonData)
            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async bookOrder(
        order: Order,
        forVendor: boolean,
        forStaff: boolean,
        date: moment.Moment,
        endDate: moment.Moment | undefined,
        duration: number,
        item?: Item,
        location?: string,
        forSummary?: boolean,
        filter?: CalendarFilter
    ): Promise<Result<RecordDataMap[]>> {
        const url = '/availability/order'
        const jsonData: any = {}
        if (date) jsonData['start'] = formatTimeStamp(date)
        if (endDate) jsonData['end'] = formatTimeStamp(endDate)
        if (forSummary) jsonData['summary'] = true
        if (forVendor) jsonData['for_vendor'] = true
        if (item) jsonData['item'] = item.id
        if (forStaff) jsonData['staff'] = true
        if (order.leadtime) jsonData['leadtime'] = order.leadtime
        if (order.id) jsonData['order'] = order.id
        if (order.vendor) jsonData['vendor'] = order.vendor.id
        if (filter && filter.hasPeople) jsonData['employee'] = filter.pros
        if (location) jsonData['location'] = location
        jsonData['duration'] = duration ? duration : ''
        if (order.date && !forSummary) jsonData['dates'] = [formatTimeStamp(order.date)]

        try {
            const result = await apiClient.post(url, jsonData)

            return await this.didBookingSearch(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async moveItem(item: Item, position: number): Promise<Result<Item[]>> {
        const postInfo: any = {}
        const url = `/item/${item.id}/move`
        postInfo['position'] = position

        try {
            const result = await apiClient.post(url, postInfo)

            const itemsArray = result.data
            const newItems: Item[] = []
            for (const item of itemsArray) newItems.push(await this.storeItem(item, true))
            this.postNotification('ItemDataSourceChanged', null)
            return okResult(newItems)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async moveLink(link: PSLink, position: number): Promise<Result<PSLink[]>> {
        const postInfo: any = {}
        const url = `/link/${link.id}/move`
        postInfo['position'] = position
        try {
            const result = await apiClient.post(url, postInfo)

            const linkArray = result.data
            const newLinks: PSLink[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const link of linkArray)
                    newLinks.push(await txManager.recordFactory.storeLink(link, true))
            })
            return okResult(newLinks)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async moveListing(category: Category, item: Item, position: number): Promise<Result<Category>> {
        const postInfo: any = {}
        const url = `/category/${category.id}/move/item`
        postInfo['position'] = position
        postInfo['item'] = item.id
        try {
            const result = await apiClient.post(url, postInfo)

            const newCategory = await this.storeCategory(result.data, true)
            this.postNotification('ItemDataSourceChanged', null)
            return okResult(newCategory)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async moveCategory(category: Category, position: number): Promise<Result<Category[]>> {
        const postInfo: any = {}
        const url = `/category/${category.id}/move`
        postInfo['position'] = position
        try {
            const result = await apiClient.post(url, postInfo)

            const categoryArray = result.data
            const newCategories: Category[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const category of categoryArray)
                    newCategories.push(await txManager.recordFactory.storeCategory(category, true))
            })
            this.postNotification('ItemDataSourceChanged', null)
            return okResult(newCategories)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async moveField(field: Field, position: number): Promise<Result<Field[]>> {
        const postInfo: any = {}
        const url = `/field/${field.id}/move`
        postInfo['position'] = position
        try {
            const result = await apiClient.post(url, postInfo)

            const fieldsArray = result.data
            const newFields: Field[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const field of fieldsArray)
                    newFields.push(await txManager.recordFactory.storeField(field, true))
            })
            this.postNotification('FieldDataSourceChanged', null)
            return okResult(newFields)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelClass(
        lesson: Lesson,
        notifyAll: boolean,
        cancelAll: boolean
    ): Promise<Result<Lesson | null>> {
        const jsonData: any = {}
        const url = `/class/${lesson.id}/cancel`
        jsonData['notify_all'] = notifyAll
        jsonData['cancel_all'] = cancelAll
        try {
            const result = await apiClient.post(url, jsonData)

            const cancelData = result.data
            const newBookings: Bookable[] = []
            const newLessons: Lesson[] = []
            let newLesson: Lesson | null = null
            if (cancelData['lesson']) newLesson = await this.storeClass(cancelData['lesson'], true)
            if (newLesson) newLessons.push(newLesson)
            if (cancelData['item']) await this.storeItem(cancelData['item'])
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                const bookings = RecordFactory.valueForArrayKey(cancelData, 'bookings')
                for (const booking of bookings)
                    newBookings.push(await txManager.recordFactory.storeBooking(booking, true))
                const lessons = RecordFactory.valueForArrayKey(cancelData, 'lessons')
                for (const lesson of lessons)
                    newLessons.push(await txManager.recordFactory.storeClass(lesson, true))
                const messages = RecordFactory.valueForArrayKey(cancelData, 'messages')
                await txManager.recordFactory.storeAll(
                    messages,
                    txManager.recordFactory.storeMessage
                )
            })
            if (cancelData['messages']) this.postNotification('MessagesDataSourceChanged', null)
            if (cancelData['bookings'] || cancelData['lessons']) {
                this.postNotification('ScheduleDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            await this.pushBookings(newLessons)
            return okResult(newLesson)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchEdition(
        edition: string,
        currentState: string
    ): Promise<Result<{ edition: Edition; allowCopyData: boolean }>> {
        const postInfo: any = { edition: edition, state: currentState ?? '' }
        try {
            const result = await apiClient.post('/edition/sync', postInfo)
            if (currentState) {
                const instance = await this.storeEdition(result.data['edition'], true)
                return okResult({
                    edition: instance,
                    allowCopyData: result.data['allow_copy_data'],
                })
            }
            else {
                const instance = await this.storeEdition(result.data, true)
                return okResult({ edition: instance, allowCopyData: false })
            }
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPlans(
        user: User,
        context: string
    ): Promise<
        Result<{
            context: string
            edition: Edition
            industry: Industry
            plans: RecordDataMap[]
            subscription: Subscription
            title: string
            welcome?: string
            welcome_image_uri?: string
            welcome_subtitle?: string
            welcome_title?: string
        }>
    > {
        const url = `/billing/${user.id}/plans`
        const postInfo: any = { context: context }
        try {
            const result = await apiClient.post(url, postInfo)

            const resultData = result.data
            const plans = resultData['plans']
            const planResults: RecordDataMap[] = []
            for (const plan of plans) planResults.push(new RecordDataMap(plan))
            if (resultData['edition'])
                resultData['edition'] = await this.storeEdition(resultData['edition'])
            if (resultData['industry'])
                resultData['industry'] = await this.storeIndustry(resultData['industry'])
            if (resultData['subscription'])
                resultData['subscription'] = await this.storeSubscription(
                    resultData['subscription']
                )
            resultData['plans'] = planResults
            return okResult(resultData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async subscribe(
        user: User,
        plan: string,
        subscription: string,
        source: Source | undefined,
        trialPeriod: number | undefined,
        receipt: string | undefined,
        instance: string | undefined,
        features: string[] | undefined,
        edition: Edition | undefined
    ): Promise<Result<User>> {
        const jsonData: any = {}
        const url = `/billing/${user.id}/subscribe`
        jsonData['source'] = source ? source.id : ''
        jsonData['plan'] = plan
        jsonData['subscription'] = subscription
        if (instance) jsonData['instance'] = instance
        jsonData['receipt'] = receipt ? receipt : ''
        jsonData['trial_period'] = trialPeriod && trialPeriod > 0 ? trialPeriod : 0
        if (features && features.length > 0) jsonData['features'] = features
        if (edition) jsonData['edition'] = edition.id
        try {
            const result = await apiClient.post(url, jsonData)

            const subscribeData = result.data
            const newUser = await this.storeUser(subscribeData['user'], true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (subscribeData['item'])
                    await txManager.recordFactory.storeItem(subscribeData['item'])
                if (subscribeData['number'])
                    await txManager.recordFactory.storeNumber(subscribeData['number'])
                if (subscribeData['taxcode'])
                    await txManager.recordFactory.storeTaxcode(subscribeData['taxcode'])
                if (subscribeData['subscription'])
                    await txManager.recordFactory.storeSubscription(subscribeData['subscription'])
                if (subscribeData['payment'])
                    await txManager.recordFactory.storePayment(subscribeData['payment'])
                if (subscribeData['messages']) {
                    const messages = subscribeData['messages']
                    await txManager.recordFactory.storeAll(
                        messages,
                        txManager.recordFactory.storeMessage
                    )
                }
            })
            // if (subscribeData['messages'])
            //     this.postNotification('MessagesDataSourceChanged', null);
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelPremium(
        user: User,
        reason: string,
        details: string
    ): Promise<Result<{ user: User; numberOfRoles: number }>> {
        const jsonData: any = {}
        const url = `/billing/${user.id}/cancel`
        jsonData['reason'] = reason ? reason : ''
        jsonData['details'] = details ? details : ''
        try {
            const result = await apiClient.post(url, jsonData)

            const cancelData = result.data
            const newUser = await this.storeUser(cancelData['user'], true)
            const numberOfRoles: number = cancelData['number_of_roles']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (cancelData['subscription'])
                    await txManager.recordFactory.storeSubscription(cancelData['subscription'])
                if (cancelData['message'])
                    await txManager.recordFactory.storeMessage(cancelData['message'])
            })
            return okResult({ user: newUser, numberOfRoles })
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async downgradePremium(user: User, reason: string, details: string): Promise<Result<User>> {
        const url = `/billing/${user.id}/downgrade`

        const jsonData: any = {}
        jsonData['reason'] = reason ? reason : ''
        jsonData['details'] = details ? details : ''
        try {
            const result = await apiClient.post(url, jsonData)

            const downgradeData = result.data
            const newUser = await this.storeUser(downgradeData['user'], true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (downgradeData['invoice'])
                    await txManager.recordFactory.storeInvoice(downgradeData['invoice'])
                if (downgradeData['payment'])
                    await txManager.recordFactory.storePayment(downgradeData['payment'])
                if (downgradeData['subscriptions']) {
                    for (const subscription of downgradeData['subscriptions'])
                        await txManager.recordFactory.storeSubscription(subscription)
                }
                if (downgradeData['numbers']) {
                    for (const number of downgradeData['numbers'])
                        await txManager.recordFactory.storeNumber(number)
                }
                if (downgradeData['people']) {
                    for (const person of downgradeData['people'])
                        await txManager.recordFactory.storeUser(person)
                }
                if (downgradeData['messages']) {
                    for (const message of downgradeData['messages'])
                        await txManager.recordFactory.storeMessage(message)
                }
            })
            if (downgradeData['messages']) console.debug('MessagesDataSourceChanged')
            console.debug('DashboardDataSourceChanged')
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async discountPremium(user: User, reason: string): Promise<Result<User>> {
        const jsonData: any = {}
        jsonData['reason'] = reason ? reason : ''
        const url = `/billing/${user.id}/discount`
        try {
            const result = await apiClient.post(url, jsonData)

            const downgradeData = result.data
            const newUser = await this.storeUser(downgradeData['user'], true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (downgradeData['invoice'])
                    await txManager.recordFactory.storeInvoice(downgradeData['invoice'])
                if (downgradeData['payment'])
                    await txManager.recordFactory.storePayment(downgradeData['payment'])
                if (downgradeData['subscriptions']) {
                    for (const subscription of downgradeData['subscriptions'])
                        await txManager.recordFactory.storeSubscription(subscription)
                }
                if (downgradeData['numbers']) {
                    for (const number of downgradeData['numbers'])
                        await txManager.recordFactory.storeNumber(number)
                }
                if (downgradeData['people']) {
                    for (const person of downgradeData['people'])
                        await txManager.recordFactory.storeUser(person)
                }
                if (downgradeData['messages']) {
                    for (const message of downgradeData['messages'])
                        await txManager.recordFactory.storeMessage(message)
                }
            })
            if (downgradeData['messages']) console.debug('MessagesDataSourceChanged')
            console.debug('DashboardDataSourceChanged')
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async increaseHeadcount(user: User): Promise<Result<User>> {
        const url = `/billing/${user.id}/headcount`
        try {
            const result = await apiClient.post(url, null)

            const resultData = result.data
            const newUser = await this.storeUser(resultData['user'], true)
            console.debug('DashboardDataSourceChanged')
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelSubscription(
        subscription: Subscription,
        enforce: boolean
    ): Promise<Result<Subscription>> {
        const jsonData = subscription.jsonObject()
        jsonData['enforce'] = enforce
        const url = `/subscription/${subscription.id}/cancel`
        try {
            const result = await apiClient.post(url, jsonData)

            const cancelData = result.data
            const newSubscription = await this.storeSubscription(cancelData['subscription'], true)
            console.debug('DashboardDataSourceChanged')
            return okResult(newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async confirmSubscription(
        subscription: Subscription,
        pos: boolean
    ): Promise<Result<Payment | Subscription>> {
        const postInfo: any = {}
        postInfo['source'] = subscription.source ? subscription.source.id : ''
        if (subscription.gratuity) {
            postInfo['gratuity'] = subscription.gratuity
            postInfo['gratuity_type'] = subscription.gratuityType
        }
        postInfo['is_pos'] = pos
        const url = `/subscription/${subscription.id}/confirm`
        try {
            const result = await apiClient.post(url, postInfo)

            const confirmData = result.data
            let newPayment: Payment | null = null
            const newSubscription = await this.storeSubscription(confirmData['subscription'], true)
            if (confirmData['invoice']) await this.storeInvoice(confirmData['invoice'])
            if (confirmData['payment'])
                newPayment = await this.storePayment(confirmData['payment'], true)
            if (confirmData['package']) await this.storePackage(confirmData['package'])
            if (confirmData['message']) await this.storeMessage(confirmData['message'])
            if (confirmData['message']) console.debug('MessagesDataSourceChanged')
            if (confirmData['payment']) console.debug('DashboardDataSourceChanged')
            return okResult(newPayment ? newPayment : newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async changeSubscriptionSource(
        subscription: Subscription,
        source: Source
    ): Promise<Result<Subscription>> {
        const url = `/subscription/${subscription.id}/source`
        try {
            const result = await apiClient.post(url, { source: source.id })

            const changeData = result.data
            const newSubscription = await this.storeSubscription(changeData['subscription'], true)
            console.debug('DashboardDataSourceChanged')
            return okResult(newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async skipNextRenewal(subscription: Subscription): Promise<Result<Subscription>> {
        const url = `/subscription/${subscription.id}/skip`
        try {
            const result = await apiClient.post(url, subscription.jsonObject())

            const skipData = result.data
            const newSubscription = await this.storeSubscription(skipData['subscription'], true)
            console.debug('DashboardDataSourceChanged')
            return okResult(newSubscription)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async skipNextInvoice(invoice: Invoice): Promise<Result<Invoice>> {
        const url = `/invoice/${invoice.id}/skip`
        try {
            const result = await apiClient.post(url, invoice.jsonObject())

            const skipResult = result.data
            const newInvoice = await this.storeInvoice(skipResult['invoice'], true)
            console.debug('DashboardDataSourceChanged')
            return okResult(newInvoice)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitFeatures(user: User): Promise<Result<User>> {
        const url = `/user/${user.id}/feature`
        const jsonData = user.jsonObject()
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData['user'], true)
            if (resultData['taxcode']) await this.storeTaxcode(resultData['taxcode'])
            console.debug('DashboardDataSourceChanged')
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async purchaseCredits(
        user: User,
        source: Source,
        amount: number,
        renewCredits: boolean
    ): Promise<Result<RecordDataMap>> {
        const url = `/user/${user.id}/credits`
        const jsonData: any = {}
        if (source && source.id) jsonData['source'] = source.id
        if (amount > 0) jsonData['amount'] = amount
        jsonData['renew_credits'] = renewCredits
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData['user'], true)
            if (resultData['payment']) await this.storePayment(resultData['payment'])
            if (resultData['message']) await this.storeMessage(resultData['message'])
            this.postNotification('DashboardDataSourceChanged', null)
            const credits = resultData['credits']
            return okResult(new RecordDataMap({ user: newUser, credits: credits }))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async requestCredits(user: User, amount: number): Promise<Result<RecordDataMap>> {
        const url = `/user/${user.id}/request-credits`
        const jsonData: any = {}
        jsonData['amount'] = amount
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData['user'], true)
            const credits = resultData['credits']
            if (resultData['message']) {
                await this.storeMessage(resultData['message'])
                this.postNotification('MessagesDataSourceChanged', null)
            }
            return okResult(new RecordDataMap({ user: newUser, credits: credits }))
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitDataImport(user: User): Promise<Result<User>> {
        const url = `/user/${user.id}/import`
        const jsonData: any = {}
        jsonData['prices'] = '1'
        jsonData['clients'] = '1'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendAlias(alias: Alias): Promise<Result<Alias>> {
        const url = alias.id ? `/alias/${alias.id}` : '/alias'
        try {
            const result = await apiClient.post(url, alias.jsonObject())

            const resultData = result.data
            const newAlias = await this.storeAlias(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newAlias)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitHero(user: User, image: File, color: string): Promise<Result<User>> {
        const url = `/user/${user.id}/hero`
        const jsonData: any = {}
        jsonData['color'] = color ? color : ''
        try {
            const result = await apiClient.post(url, addImage(jsonData, image))

            const resultData = result.data
            const newUser = await this.storeUser(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitVerification(
        user: User,
        name?: string,
        address?: string,
        dob?: moment.Moment,
        ssn?: string,
        image?: File
    ): Promise<Result<User>> {
        const url = `/user/${user.id}/verify`
        const jsonData: any = {}
        jsonData['id'] = user.id
        if (name) jsonData['name'] = name
        if (ssn?.length === 4) jsonData['ssn_last_4'] = ssn
        if (ssn?.length === 9) jsonData['ssn'] = ssn
        if (address) jsonData['address'] = address
        if (dob) jsonData['dob'] = dob.format('YYYY-MM-DD')
        try {
            const result = await apiClient.post(url, addImage(jsonData, image))

            const resultData = result.data
            const newUser = await this.storeUser(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('SetupDashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitTaxcode(user: User): Promise<Result<User>> {
        const url = `/user/${user.id}/taxcode`
        const jsonData: any = {}
        jsonData['always_collect_tax'] = user.alwaysCollectTax
        jsonData['taxcode'] = user.taxcode ? user.taxcode.id : ''
        jsonData['tax_address'] = user.taxcode ? user.taxAddress : ''
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitUpgrade(user: User): Promise<Result<User>> {
        const url = `/user/${user.id}/upgrade`
        const jsonData: any = {}
        jsonData['size'] = user.size
        jsonData['volume'] = user.volume
        jsonData['entity'] = user.entity
        jsonData['industry'] = user.industry!.id
        jsonData['allow_booking'] = user.allowBooking
        jsonData['allow_invoice'] = user.allowInvoice
        jsonData['allow_payment'] = user.allowPayment
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newUser = await this.storeUser(resultData, true)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (resultData['item']) await txManager.recordFactory.storeItem(resultData['item'])
                if (resultData['number'])
                    await txManager.recordFactory.storeNumber(resultData['number'])
                if (resultData['edition'])
                    await txManager.recordFactory.storeEdition(resultData['edition'])
                if (resultData['subscription'])
                    await txManager.recordFactory.storeSubscription(resultData['subscription'])
                if (resultData['messages']) {
                    for (const message of resultData['messages'])
                        await txManager.recordFactory.storeMessage(message)
                }
            })
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newUser)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitDataExport(
        user: User,
        client: User | undefined,
        types: string[],
        schedule: string
    ): Promise<Result<any>> {
        const url = `/user/${user.id}/export`
        const jsonData: any = {}
        jsonData['client'] = client ? client.id : ''
        jsonData['notes'] = types.includes('notes') ? '1' : '0'
        jsonData['payees'] = types.includes('payees') ? '1' : '0'
        jsonData['orders'] = types.includes('orders') ? '1' : '0'
        jsonData['bookings'] = types.includes('bookings') ? '1' : '0'
        jsonData['contracts'] = types.includes('contracts') ? '1' : '0'
        jsonData['packages'] = types.includes('packages') ? '1' : '0'
        jsonData['subscriptions'] = types.includes('subscriptions') ? '1' : '0'
        jsonData['records'] = types.includes('records') ? '1' : '0'
        jsonData['estimates'] = types.includes('estimates') ? '1' : '0'
        jsonData['invoices'] = types.includes('invoices') ? '1' : '0'
        jsonData['payments'] = types.includes('payments') ? '1' : '0'
        jsonData['clients'] = types.includes('clients') ? '1' : '0'
        jsonData['active'] = types.includes('active') ? '1' : '0'
        jsonData['paying'] = types.includes('paying') ? '1' : '0'
        jsonData['posts'] = types.includes('posts') ? '1' : '0'
        jsonData['subscribers'] = types.includes('subscribers') ? '1' : '0'
        jsonData['classes'] = types.includes('classes') ? '1' : '0'
        jsonData['triggers'] = types.includes('triggers') ? '1' : '0'
        jsonData['gifts'] = types.includes('gifts') ? '1' : '0'
        jsonData['items'] = types.includes('items') ? '1' : '0'
        jsonData['tasks'] = types.includes('tasks') ? '1' : '0'
        jsonData['royalties'] = types.includes('royalties') ? '1' : '0'
        jsonData['locations'] = types.includes('locations') ? '1' : '0'
        jsonData['inventory'] = types.includes('inventory') ? '1' : '0'
        jsonData['subaccounts'] = types.includes('subaccounts') ? '1' : '0'
        jsonData['schedule'] = schedule ? schedule : '1d'
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            return okResult(resultData['status'])
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitVerificationBySource(
        source: Source,
        amount: number,
        amount2: number
    ): Promise<Result<Source>> {
        const url = `/source/${source.id}/verify`
        const jsonData: any = {}
        jsonData['id'] = source.id
        jsonData['amount'] = amount !== 0 ? amount : undefined
        jsonData['amount2'] = amount2 !== 0 ? amount2 : undefined

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSource = await this.storeSource(resultData, true)
            this.postNotification('SourceDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newSource)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async dialNumber(number: string, caller: string): Promise<VoidResult> {
        try {
            await apiClient.post('/number/dial', { caller: caller, number: number })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async requestChangeByUser(user: User, phone: string, email: string): Promise<VoidResult> {
        const url = `/user/${user.id}/request`
        const jsonData: any = {}
        jsonData['phone'] = phone || undefined
        jsonData['email'] = email || undefined

        try {
            const result = await apiClient.post(url, jsonData)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async requestChange(change: Change): Promise<VoidResult> {
        const url = `/change/${change.id}/request`
        try {
            const result = await apiClient.post(url, undefined)

            const resultData = result.data
            if (resultData['message']) await this.storeMessage(resultData['message'])
            this.postNotification('MessagesDataSourceChanged', null)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitRefund(payment: Payment, amount: number): Promise<Result<Payment>> {
        const url = `/payment/${payment.id}/refund`
        const jsonData: any = {}
        jsonData['amount'] = amount
        jsonData['reason'] = payment.refundReason
        jsonData['details'] = payment.refundDetails

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newPayment = await this.storePayment(resultData['payment'], true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPayment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async submitAuthorization(source: Source, accountNumber: string): Promise<Result<Source>> {
        const url = `/source/${source.id}/authorize`
        const jsonData: any = {}
        jsonData['id'] = source.id
        jsonData['account_number'] = accountNumber

        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSource = await this.storeSource(resultData, true)
            this.postNotification('SourceDataSourceChanged', null)
            return okResult(newSource)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async launchConference(booking: Booking): Promise<Result<RecordDataMap>> {
        const start = moment()

        try {
            const result = await apiClient.post(`/conference/${booking.id}/launch`, {
                booking: booking.id,
            })

            const resultsArray: string[] = []
            const results = result.data
            const instance = new RecordDataMap(results)
            const typeData = results['extra_types']
            const resultData = results['extra_results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                Promise.all(
                    resultData.map(async (result: any) => {
                        await txManager.recordFactory.store(typeData, result)
                        resultsArray.push('')
                    })
                )
                console.debug(`SYNC: Synced ${resultsArray.length} extra instances`)
                console.debug(`FETCH: ReferralMetrics Fetch Time: ${moment().diff(start)}fms`)
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async joinConference(conference: Conference): Promise<Result<RecordDataMap>> {
        const start = moment()

        try {
            const result = await apiClient.post(`/conference/${conference.id}/join`, undefined)

            const resultsArray: string[] = []
            const results = result.data
            const instance = new RecordDataMap(results)
            const typeData = results['extra_types']
            const resultData = results['extra_results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                Promise.all(
                    resultData.map(async (result: any) => {
                        await txManager.recordFactory.store(typeData, result)
                        resultsArray.push('')
                    })
                )
                console.debug(`SYNC: Synced ${resultsArray.length} extra instances`)
                console.debug(`FETCH: ReferralMetrics Fetch Time: ${moment().diff(start)}fms`)
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeConference(conference: Conference): Promise<Result<Conference>> {
        const start = moment()

        try {
            const result = await apiClient.post(`/conference/${conference.id}/complete`, undefined)

            const results = result.data
            const newConference = await this.storeConference(results['conference'], true)
            console.debug(`completeConference (ms): ${moment().diff(start)}`)
            return okResult(newConference)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async completeAnyActiveConference(booking: Booking): Promise<Result<Conference | null>> {
        const start = moment()

        try {
            const result = await apiClient.post('/conference/complete', { booking: booking.id })

            const results = result.data
            let newConference = null
            if (results['conference'])
                newConference = await this.storeConference(results['conference'], true)
            console.debug(`completeConference (ms): ${moment().diff(start)}`)
            return okResult(newConference)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendConference(booking: Booking): Promise<VoidResult> {
        const start = moment()

        try {
            const result = await apiClient.post(`/conference/${booking.id}/send`, undefined)

            console.debug(`sendConference (ms): ${moment().diff(start)}`)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchReferralMetrics(dateType: string): Promise<Result<RecordDataMap>> {
        const start = moment()
        const url = '/dashboard/referrals'
        const serverArgs = { type: 'referrals', date_type: dateType }

        try {
            const result = await apiClient.post(url, serverArgs)

            const resultsArray: string[] = []
            const results = result.data
            const instance = new RecordDataMap(results)
            const typeData = results['extra_types']
            const resultData = results['extra_results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                Promise.all(
                    resultData.map(async (result: any) => {
                        await txManager.recordFactory.store(typeData, result)
                        resultsArray.push('')
                    })
                )
                console.debug(`SYNC: Synced ${resultsArray.length} extra instances`)
                console.debug(`FETCH: ReferralMetrics Fetch Time: ${moment().diff(start)}fms`)
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBilling(): Promise<Result<any>> {
        const start = moment()

        const url = '/billing/sync'
        try {
            const result = await apiClient.post(url, undefined)

            const instance: any = {}
            const resultData = result.data
            if (resultData['subscription'])
                instance['subscription'] = this.storeSubscription(resultData['subscription'])
            instance['headcount'] = resultData['headcount']
            instance['credit_count'] = resultData['credit_count']
            instance['number_count'] = resultData['number_count']
            instance['reader_count'] = resultData['reader_count']
            instance['employee_count'] = resultData['employee_count']
            instance['employee_price'] = resultData['employee_price']
            instance['employee_charge_amount'] = resultData['employee_charge_amount']
            console.debug(`FETCH: fetchBilling Time: ${moment().diff(start)}ms`)
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPremiumHistory(user: User): Promise<Result<any>> {
        const url = `/billing/${user.id}/history`
        try {
            const result = await apiClient.get(url)

            const results = result.data
            const data = results['data']
            const header = results['header_title']
            const items: any[] = []
            for (const item of data) items.push(new RecordDataMap(item))
            const r: any = {}
            r['header_title'] = header
            r['items'] = items
            return okResult(r)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPremiumFeatures(user: User): Promise<Result<any[]>> {
        const url = `/billing/${user.id}/features`
        try {
            const result = await apiClient.get(url)

            const results = result.data
            const items: any[] = []
            for (const item of results) items.push(new RecordDataMap(item))
            return okResult(items)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchCalendar(
        keywords: string,
        person?: User,
        filter?: CalendarFilter,
        signal?: AbortSignal
    ): Promise<Result<Bookable[]>> {
        const start = moment()
        const url = '/calendar/search'
        const postArgs: any = {}
        postArgs['keywords'] = keywords
        if (person) postArgs['person'] = person.id
        if (filter) {
            if (filter.hasPeople) postArgs['pros'] = filter.pros
            if (filter.hasItems) postArgs['items'] = filter.allItems
            if (filter.hasResources) postArgs['resources'] = filter.resources
        }
        try {
            const result = await apiClient.post(url, postArgs, { signal })
            const resultsArray: Bookable[] = []
            const results = result.data
            const typeData = results['types']
            const resultData = results['results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < resultData.length; i++) {
                    const record = await txManager.recordFactory.store(typeData[i], resultData[i])
                    if (
                        record instanceof Booking ||
                        record instanceof Lesson ||
                        record instanceof PSEvent ||
                        record instanceof Task ||
                        record instanceof Block ||
                        record instanceof Order ||
                        record instanceof Item
                    )
                        resultsArray.push(record)
                }
                console.debug(
                    `SYNC: Synced ${resultsArray.length} results, ${resultsArray.length} bookables`
                )
                console.debug(
                    `SEARCH: Calendar Search Time (${keywords}): ${moment().diff(start)}ms`
                )
            })
            return okResult(resultsArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchMessenger(
        keywords: string,
        employee?: User,
        cancelToken?: CancelToken
    ): Promise<Result<Message[]>> {
        const start = moment()
        const url = '/message/search'
        try {
            const result = await apiClient.post(
                url,
                employee ? { keywords: keywords, employee: employee.id } : { keywords: keywords },
                { cancelToken }
            )

            const resultsArray: Message[] = []
            const results = result.data
            const typeData = results['types']
            const resultData = results['results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < resultData.length; i++) {
                    const record = await txManager.recordFactory.store(typeData[i], resultData[i])
                    if (record instanceof Message) {
                        resultsArray.push(record)
                    }
                }
                console.debug(`SYNC: Synced ${resultsArray.length} threads`)
                console.debug(
                    `SEARCH: Messenger Search Time (${keywords}): ${moment().diff(start)}ms`
                )
            })
            return okResult(resultsArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchDashboard(
        dashboard: Dashboard,
        keywords: string,
        dateType: string
    ): Promise<Result<any[]>> {
        const start = moment()
        const url = `/dashboard/${dashboard.id}/search`
        const searchArgs: any = {}
        searchArgs['keywords'] = keywords
        searchArgs['view'] = dashboard.view
        if (dateType) searchArgs['date_type'] = dateType
        if (dashboard.person) searchArgs['person'] = dashboard.person.id
        if (dashboard.subaccount) searchArgs['subaccount'] = dashboard.subaccount.id
        try {
            const result = await apiClient.post(url, searchArgs)

            const resultsArray: any[] = []
            const results = result.data
            const resultData = results['results']
            const extraTypeData = results['extra_types']
            const extraResultData = results['extra_results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const result of resultData)
                    resultsArray.push(
                        extraTypeData && extraTypeData.length > 0
                            ? txManager.recordFactory.store(extraTypeData, result)
                            : new RecordDataMap(result)
                    )
                console.debug(`SYNC: Synced ${resultsArray.length} results`)
                Promise.all(
                    resultData.map(async (result: any) => {
                        await txManager.recordFactory.store(extraTypeData, result)
                    })
                )
                console.debug(`SYNC: Synced ${extraResultData.length} extra results`)
                console.debug(
                    `SEARCH: Dashboard Search Time (${keywords}): ${moment().diff(start)}ms`
                )
            })
            return okResult(resultsArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchContact(keywords: string): Promise<Result<any[]>> {
        const start = moment()
        const url = '/contact/search'
        try {
            const result = await apiClient.post(url, { keywords: keywords })

            const resultsArray: any[] = []
            const results = result.data
            const resultData = results['results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const result of resultData) {
                    const contactResult = txManager.recordFactory.storeContact(
                        new RecordDataMap(result)
                    )
                    resultsArray.push(contactResult)
                }
                console.debug(`SYNC: Synced ${resultsArray.length} contacts`)
                console.debug(
                    `SEARCH: Contact Search Time (${keywords}): ${moment().diff(start)}ms`
                )
            })
            return okResult(resultsArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async searchUser(
        keywords: string,
        type?: 'client' | 'client+subaccount' | 'payee' | 'subscriber' | 'user',
        cancelToken?: CancelToken
    ): Promise<Result<(User | Subaccount)[]>> {
        const start = moment()
        const url = '/user/search'
        const jsonData = type ? { keywords: keywords, type: type } : { keywords: keywords }
        try {
            const result = await apiClient.post(url, jsonData, { cancelToken })
            const resultsArray: (User | Subaccount)[] = []
            const results = result.data
            const typeData = results['types']
            const resultData = results['results']
            const subaccountData = results['subaccounts']
            const extraTypeData = results['extra_types']
            const extraResultData = results['extra_results']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < resultData.length; i++) {
                    const instance = await txManager.recordFactory.store(typeData[i], resultData[i])
                    if (instance instanceof User) resultsArray.push(instance)
                }
                console.debug(`SEARCH: Matched ${resultsArray.length} users`)
                if (subaccountData?.length) {
                    for (const subaccount of subaccountData)
                        resultsArray.push(
                            await txManager.recordFactory.storeSubaccount(subaccount, true)
                        )
                    console.debug(`SEARCH: Matched ${subaccountData.length} subaccounts`)
                }
                if (extraTypeData?.length) {
                    for (let i = 0; i < extraTypeData.length; i++)
                        await txManager.recordFactory.store(extraTypeData[i], extraResultData[i])
                    console.debug(`SEARCH: synced ${extraResultData.length} extra results`)
                }
                console.debug(`SEARCH: User Search Time (${keywords}): ${moment().diff(start)}ms`)
            })
            return okResult(resultsArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async signContract(signature: Signature): Promise<Result<Signature>> {
        const url = `/signature/${signature.id}/sign`
        try {
            const result = await apiClient.post(url, signature.jsonObject())

            const resultData = result.data
            const newSignature = await this.storeSignature(resultData['signature'], true)
            if (resultData['messages']) {
                const messages = resultData['messages']
                await this.storeAll(messages, this.storeMessage)
            }
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('ContractDataSourceChanged', null)
            return okResult(newSignature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async signRecord(record: PSRecord, signature: string): Promise<Result<Signature>> {
        const url = `/record/${record.id}/sign`
        const jsonData = { signature_data: signature }
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSignature = await this.storeSignature(resultData['signature'], true)
            if (resultData['record']) await this.storeRecord(resultData['record'])
            if (resultData['messages']) {
                const messages = resultData['messages']
                await this.storeAll(messages, this.storeMessage)
            }
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('ContractDataSourceChanged', null)
            return okResult(newSignature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async signTransaction(transaction: Transaction, signature: string): Promise<Result<Signature>> {
        const url = `/${transaction.recordType.toLowerCase()}/${transaction.id}/sign`
        const jsonData = { signature_data: signature }
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSignature = await this.storeSignature(resultData['signature'], true)
            if (resultData['invoice']) await this.storeInvoice(resultData['invoice'])
            if (resultData['estimate']) await this.storeEstimate(resultData['estimate'])
            if (resultData['booking']) await this.storeBooking(resultData['booking'])
            if (resultData['package']) await this.storePackage(resultData['package'])
            if (resultData['gift']) await this.storeGift(resultData['gift'])
            if (resultData['order']) await this.storeOrder(resultData['order'])
            if (resultData['subscription']) await this.storeSubscription(resultData['subscription'])
            if (resultData['messages']) {
                const messages = resultData['messages']
                await this.storeAll(messages, this.storeMessage)
            }
            this.postNotification('MessagesDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            this.postNotification('ContractDataSourceChanged', null)

            return okResult(newSignature)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fieldProfile(client: Client, change: Change): Promise<Result<Client>> {
        const jsonData: any = {}
        if (change) jsonData['change'] = change.id
        jsonData['field_values'] = Field.serializeFieldData(
            client.fieldValues,
            client.owner!.id,
            undefined
        )
        const url = `/client/${client.id}/profile`
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newClient = await this.storeClient(resultData['client'], true)
            if (resultData['change']) await this.storeChange(resultData['change'])
            if (resultData['message']) {
                await this.storeMessage(resultData['message'])
                this.postNotification('MessagesTabDataSourceChanged', null)
            }
            this.postNotification('ProfileDataSourceChanged', null)
            return okResult(newClient)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fieldRecord(record: PSRecord, client: Client): Promise<Result<PSRecord | undefined>> {
        const url = `/record/${record.id}/subaccount`
        const jsonData: any = {}
        if (client) {
            jsonData['client'] = client.id
            jsonData['owner'] = record.owner!.id
            if (client.birthDay) jsonData['birthday'] = moment(client.birthDay).format('YYYY-MM-DD')
            if (client.person!.line1) {
                jsonData['line1'] = client.person!.line1
                jsonData['line2'] = client.person!.line2
                jsonData['city'] = client.person!.city
                jsonData['state'] = client.person!.state
                jsonData['postal_code'] = client.person!.postalCode
            }
            jsonData['field_values'] = Field.serializeFieldData(
                client.fieldValues,
                client.owner!.id,
                undefined
            )
        }
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            let newRecord: PSRecord | undefined = undefined
            if (resultData['record']) newRecord = await this.storeRecord(resultData['record'], true)
            if (resultData['client']) {
                await this.storeClient(resultData['client'])
                this.postNotification('SubaccountDataSourceChanged', null)
            }
            return okResult(newRecord)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async subaccountProfile(subaccount: Subaccount, change: Change): Promise<Result<Subaccount>> {
        const jsonData: any = {}
        if (change) jsonData['change'] = change.id
        jsonData['field_values'] = Field.serializeFieldData(
            subaccount.fieldValues,
            subaccount.owner!.id,
            undefined
        )
        const url = `/subaccount/${subaccount.id}/profile`
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            const newSubaccount = await this.storeSubaccount(resultData['subaccount'], true)
            if (resultData['change']) await this.storeChange(resultData['change'])
            if (resultData['message']) {
                await this.storeMessage(resultData['message'])
                this.postNotification('MessagesTabDataSourceChanged', null)
            }
            this.postNotification('ProfileDataSourceChanged', null)
            this.postNotification('SubaccountDataSourceChanged', null)
            return okResult(newSubaccount)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async subaccountRecord(
        record: PSRecord,
        subaccount: Subaccount
    ): Promise<Result<PSRecord | undefined>> {
        const url = `/record/${record.id}/subaccount`
        const jsonData: any = {}
        if (subaccount) {
            jsonData['subaccount'] = subaccount.id
            jsonData['name'] = subaccount.name
            if (subaccount.birthDay)
                jsonData['birthday'] = moment(subaccount.birthDay).format('YYYY-MM-DD')
            if (subaccount.line1) {
                jsonData['line1'] = subaccount.line1
                jsonData['line2'] = subaccount.line2
                jsonData['city'] = subaccount.city
                jsonData['state'] = subaccount.state
                jsonData['postal_code'] = subaccount.postalCode
            }
            jsonData['field_values'] = Field.serializeFieldData(
                subaccount.fieldValues,
                subaccount.owner!.id,
                undefined
            )
        }
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            let newRecord: PSRecord | undefined = undefined
            if (resultData['record']) newRecord = await this.storeRecord(resultData['record'], true)
            if (resultData['subaccount']) {
                await this.storeSubaccount(resultData['subaccount'])
                this.postNotification('SubaccountDataSourceChanged', null)
            }
            return okResult(newRecord)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fieldTransaction(
        transaction: Transaction,
        client: Client
    ): Promise<Result<Transaction | Client | undefined>> {
        const jsonData: any = {}
        jsonData['client'] = client.id
        jsonData['owner'] = transaction.owner!.id
        if (client.birthDay) jsonData['birthday'] = moment(client.birthDay).format('YYYY-MM-DD')
        if (client.person!.line1) {
            jsonData['line1'] = client.person!.line1
            jsonData['line2'] = client.person!.line2
            jsonData['city'] = client.person!.city
            jsonData['state'] = client.person!.state
            jsonData['postal_code'] = client.person!.postalCode
        }
        if (isCustomizable(transaction) && transaction.id)
            jsonData['field_values'] = Field.serializeFieldData(
                // @ts-ignore Customizable.fieldValues exists
                transaction.fieldValues,
                transaction.owner!.id,
                undefined
            )
        else
            jsonData['field_values'] = Field.serializeFieldData(
                client.fieldValues,
                client.owner!.id,
                undefined
            )

        const url = transaction.id
            ? `/${transaction.recordType.toLowerCase()}/${transaction.id}/field`
            : `/${transaction.recordType.toLowerCase()}/field`
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            let newClient: Client | undefined
            let newTransaction: Transaction | undefined
            if (resultData['user']) await this.storeUser(resultData['user'])
            if (resultData['booking'])
                newTransaction = await this.storeBooking(resultData['booking'], true)
            if (resultData['order'])
                newTransaction = await this.storeOrder(resultData['order'], true)
            if (resultData['client']) newClient = await this.storeClient(resultData['client'], true)
            return okResult(newTransaction ? newTransaction : newClient)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // todo: implement
    // public void fieldLocation(Location location, final ServerCallBack onload, final ServerCallBack onfail) {
    //     ServerCallBack callBack = result -> {
    //         try {
    //             JSONObject resultData = new JSONObject(result.toString());
    //             Location newLocation = storeLocation(resultData.getJSONObject("location"));
    //             if (onload != null)
    //                 onload.execute(newLocation);
    //         }
    //         catch (JSONException e) {
    //             ErrorLogic.log(e);
    //         }
    //     };

    //     RecordDataMap jsonData = new RecordDataMap();
    //     jsonData.put("owner", location.owner().getId());
    //     jsonData.put("location", location.getId());
    //     jsonData.put("field_values", Field.serializeFieldData(location.fieldValues(), location.owner().getId(), null));
    //     String url = String.format("/location/%s/profile", location.getId());
    //     ServerFactory.getInstance().post(url,
    //             jsonData,
    //             callBack,
    //             result -> {
    //                 if (onfail != null)
    //                     onfail.execute(result);
    //             });
    // }

    async subaccountTransaction(
        transaction: Transaction,
        subaccount: Subaccount
    ): Promise<Result<Transaction | undefined>> {
        const jsonData: any = {}
        if (subaccount) {
            jsonData['subaccount'] = subaccount.id
            jsonData['name'] = subaccount.name
            if (subaccount.birthDay)
                jsonData['birthday'] = moment(subaccount.birthDay).format('YYYY-MM-DD')
            if (subaccount.line1) {
                jsonData['line1'] = subaccount.line1
                jsonData['line2'] = subaccount.line2
                jsonData['city'] = subaccount.city
                jsonData['state'] = subaccount.state
                jsonData['postal_code'] = subaccount.postalCode
            }
        }
        if (isCustomizable(transaction))
            jsonData['field_values'] = Field.serializeFieldData(
                // @ts-ignore transaction.fieldValues exists in Customizable
                transaction.fieldValues,
                transaction.owner?.id,
                undefined
            )
        else
            jsonData['field_values'] = subaccount
                ? Field.serializeFieldData(subaccount.fieldValues, subaccount.owner!.id, undefined)
                : null

        const url = transaction.id
            ? `/${transaction.recordType.toLowerCase()}/${transaction.id}/subaccount`
            : `/${transaction.recordType.toLowerCase()}/subaccount`
        try {
            const result = await apiClient.post(url, jsonData)

            const resultData = result.data
            let newTransaction: Transaction | undefined
            if (resultData['invoice'])
                newTransaction = await this.storeInvoice(resultData['invoice'], true)
            if (resultData['estimate'])
                newTransaction = await this.storeEstimate(resultData['estimate'], true)
            if (resultData['booking'])
                newTransaction = await this.storeBooking(resultData['booking'], true)
            if (resultData['package'])
                newTransaction = await this.storePackage(resultData['package'], true)
            if (resultData['gift']) newTransaction = await this.storeGift(resultData['gift'], true)
            if (resultData['order'])
                newTransaction = await this.storeOrder(resultData['order'], true)
            if (resultData['subscription'])
                newTransaction = await this.storeSubscription(resultData['subscription'], true)
            if (resultData['subaccount']) {
                await this.storeSubaccount(resultData['subaccount'])
                this.postNotification('SubaccountDataSourceChanged', null)
            }

            return okResult(newTransaction)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async rollbackTransaction(transaction: Transaction): Promise<Result<Transaction | undefined>> {
        const typeString = transaction.recordType.split('.')
        const url = `/${typeString[typeString.length - 1]}/${transaction.id}/rollback`
        try {
            const result = await apiClient.post(url, undefined)

            const resultData = result.data
            let newTransaction: Transaction | undefined
            if (resultData['estimate'])
                newTransaction = await this.storeEstimate(resultData['estimate'], true)
            if (resultData['booking'])
                newTransaction = await this.storeBooking(resultData['booking'], true)
            if (resultData['royalty'])
                newTransaction = await this.storeRoyalty(resultData['royalty'], true)
            if (resultData['charge'])
                newTransaction = await this.storeCharge(resultData['charge'], true)
            if (resultData['gift']) newTransaction = await this.storeGift(resultData['gift'], true)
            if (resultData['order'])
                newTransaction = await this.storeOrder(resultData['order'], true)
            if (resultData['package'])
                newTransaction = await this.storePackage(resultData['package'], true)
            if (resultData['invoice'])
                newTransaction = await this.storeInvoice(resultData['invoice'], true)
            if (resultData['bookings']) {
                const bookings = resultData['bookings']
                await this.storeAll(bookings, this.storeBooking)
            }
            if (resultData['job']) await this.storeJob(resultData['job'])
            if (newTransaction instanceof Booking)
                this.postNotification('ScheduleDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)

            return okResult(newTransaction)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendReceipt(transaction: Transaction): Promise<Result<Message>> {
        const type = transaction.recordType.toLowerCase()
        const url = `/${type}/${transaction.id}/receipt`
        try {
            const result = await apiClient.post(url, undefined)

            const resultData = result.data
            const newMessage = await this.storeMessage(resultData, true)
            this.postNotification('MessagesDataSourceChanged', null)
            return okResult(newMessage)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    /**
     * Batch store records to sql lite
     * @param objs  array of objects to store
     * @param storeCallback  callback to store each object
     * @returns array of stored object ids
     */
    private async storeAll<T = any>(
        objs: any[],
        storeCallback: (obj: any, loadFks?: boolean) => Promise<T>
    ) {
        return await batch<T>(objs, (obj: any) => storeCallback.call(this, obj, false), 250)
    }

    async sync(userId: string): Promise<void> {
        const lastSyncDate = await SQLiteFactory.getInstance().willSync(userId, false)
        let url = '/sync'
        if (lastSyncDate) {
            url += `/${lastSyncDate.format('yyyyMMDDHHmmss')}`
        }
        const start = new Date()
        if (IS_DEVELOPMENT) console.debug(`${lastSyncDate ? 'partial' : 'full'} sync`)
        const response = await apiClient.post(url)
        const startOfSqlStore = new Date()
        if (IS_DEVELOPMENT)
            console.debug(`api response ${startOfSqlStore.getTime() - start.getTime()}ms`)

        const allSql: SQLBuilder[] = []
        const results = response.data
        await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
            if (results.industries)
                await txManager.recordFactory.storeAll(
                    results.industries,
                    txManager.recordFactory.storeIndustry
                )
            if (results.features)
                await txManager.recordFactory.storeAll(
                    results.features,
                    txManager.recordFactory.storeFeature
                )
            if (results.locations)
                await txManager.recordFactory.storeAll(
                    results.locations,
                    txManager.recordFactory.storeLocation
                )
            if (results.goals)
                await txManager.recordFactory.storeAll(
                    results.goals,
                    txManager.recordFactory.storeGoal
                )
            if (results.aliases)
                await txManager.recordFactory.storeAll(
                    results.aliases,
                    txManager.recordFactory.storeAlias
                )
            if (results.taxcodes)
                await txManager.recordFactory.storeAll(
                    results.taxcodes,
                    txManager.recordFactory.storeTaxcode
                )
            if (results.channels)
                await txManager.recordFactory.storeAll(
                    results.channels,
                    txManager.recordFactory.storeChannel
                )
            if (results.types)
                await txManager.recordFactory.storeAll(
                    results.types,
                    txManager.recordFactory.storeType
                )
            if (results.records)
                await txManager.recordFactory.storeAll(
                    results.records,
                    txManager.recordFactory.storeRecord
                )
            if (results.templates)
                await txManager.recordFactory.storeAll(
                    results.templates,
                    txManager.recordFactory.storeTemplate
                )
            if (results.campaigns)
                await txManager.recordFactory.storeAll(
                    results.campaigns,
                    txManager.recordFactory.storeCampaign
                )
            if (results.fields)
                await txManager.recordFactory.storeAll(
                    results.fields,
                    txManager.recordFactory.storeField
                )
            if (results.contracts)
                await txManager.recordFactory.storeAll(
                    results.contracts,
                    txManager.recordFactory.storeContract
                )
            if (results.signatures)
                await txManager.recordFactory.storeAll(
                    results.signatures,
                    txManager.recordFactory.storeSignature
                )
            if (results.users)
                await txManager.recordFactory.storeAll(
                    results.users,
                    txManager.recordFactory.storeUser
                )
            if (results.clients)
                await txManager.recordFactory.storeAll(
                    results.clients,
                    txManager.recordFactory.storeClient
                )
            if (results.subaccounts)
                await txManager.recordFactory.storeAll(
                    results.subaccounts,
                    txManager.recordFactory.storeSubaccount
                )
            if (results.payees)
                await txManager.recordFactory.storeAll(
                    results.payees,
                    txManager.recordFactory.storePayee
                )
            if (results.teams)
                await txManager.recordFactory.storeAll(
                    results.teams,
                    txManager.recordFactory.storeTeam
                )
            if (results.numbers)
                await txManager.recordFactory.storeAll(
                    results.numbers,
                    txManager.recordFactory.storeNumber
                )
            if (results.readers)
                await txManager.recordFactory.storeAll(
                    results.readers,
                    txManager.recordFactory.storeReader
                )
            if (results.items)
                await txManager.recordFactory.storeAll(
                    results.items,
                    txManager.recordFactory.storeItem
                )
            if (results.orders)
                await txManager.recordFactory.storeAll(
                    results.orders,
                    txManager.recordFactory.storeOrder
                )
            if (results.sources)
                await txManager.recordFactory.storeAll(
                    results.sources,
                    txManager.recordFactory.storeSource
                )
            if (results.bookings)
                await txManager.recordFactory.storeAll(
                    results.bookings,
                    txManager.recordFactory.storeBooking
                )
            if (results.invoices)
                await txManager.recordFactory.storeAll(
                    results.invoices,
                    txManager.recordFactory.storeInvoice
                )
            if (results.estimates)
                await txManager.recordFactory.storeAll(
                    results.estimates,
                    txManager.recordFactory.storeEstimate
                )
            if (results.payments)
                await txManager.recordFactory.storeAll(
                    results.payments,
                    txManager.recordFactory.storePayment
                )
            if (results.deposits)
                await txManager.recordFactory.storeAll(
                    results.deposits,
                    txManager.recordFactory.storeDeposit
                )
            if (results.links)
                await txManager.recordFactory.storeAll(
                    results.links,
                    txManager.recordFactory.storeLink
                )
            if (results.notes)
                await txManager.recordFactory.storeAll(
                    results.notes,
                    txManager.recordFactory.storeNote
                )
            if (results.tasks)
                await txManager.recordFactory.storeAll(
                    results.tasks,
                    txManager.recordFactory.storeTask
                )
            if (results.gifts)
                await txManager.recordFactory.storeAll(
                    results.gifts,
                    txManager.recordFactory.storeGift
                )
            if (results.classes)
                await txManager.recordFactory.storeAll(
                    results.classes,
                    txManager.recordFactory.storeClass
                )
            if (results.packages)
                await txManager.recordFactory.storeAll(
                    results.packages,
                    txManager.recordFactory.storePackage
                )
            if (results.subscriptions)
                await txManager.recordFactory.storeAll(
                    results.subscriptions,
                    txManager.recordFactory.storeSubscription
                )
            if (results.categories)
                await txManager.recordFactory.storeAll(
                    results.categories,
                    txManager.recordFactory.storeCategory
                )
            if (results.devices)
                await txManager.recordFactory.storeAll(
                    results.devices,
                    txManager.recordFactory.storeDevice
                )
            if (results.events)
                await txManager.recordFactory.storeAll(
                    results.events,
                    txManager.recordFactory.storeEvent
                )
            if (results.messages)
                await txManager.recordFactory.storeAll(
                    results.messages,
                    txManager.recordFactory.storeMessage
                )
            if (results.balances)
                await txManager.recordFactory.storeAll(
                    results.balances,
                    txManager.recordFactory.storeBalance
                )
            if (results.jobs)
                await txManager.recordFactory.storeAll(
                    results.jobs,
                    txManager.recordFactory.storeJob
                )
            if (results.blocks)
                await txManager.recordFactory.storeAll(
                    results.blocks,
                    txManager.recordFactory.storeBlock
                )
            if (results.editions)
                await txManager.recordFactory.storeAll(
                    results.editions,
                    txManager.recordFactory.storeEdition
                )
            if (results.triggers)
                await txManager.recordFactory.storeAll(
                    results.triggers,
                    txManager.recordFactory.storeTrigger
                )
            if (results.credentials)
                await txManager.recordFactory.storeAll(
                    results.credentials,
                    txManager.recordFactory.storeCredential
                )
            if (results.calendars)
                await txManager.recordFactory.storeAll(
                    results.calendars,
                    txManager.recordFactory.storeCalendar
                )
            if (results.dashboards)
                await txManager.recordFactory.storeAll(
                    results.dashboards,
                    txManager.recordFactory.storeDashboard
                )
            if (results.tcrs)
                await txManager.recordFactory.storeAll(
                    results.tcrs,
                    txManager.recordFactory.storeTCR
                )
            if (results.reports)
                await txManager.recordFactory.storeAll(
                    results.reports,
                    txManager.recordFactory.storeReport
                )
            if (results.resources)
                await txManager.recordFactory.storeAll(
                    results.resources,
                    txManager.recordFactory.storeResource
                )
            if (results.requirements)
                await txManager.recordFactory.storeAll(
                    results.requirements,
                    txManager.recordFactory.storeRequirement
                )
            if (results.criterias)
                await txManager.recordFactory.storeAll(
                    results.criterias,
                    txManager.recordFactory.storeCriteria
                )
            if (results.components)
                await txManager.recordFactory.storeAll(
                    results.components,
                    txManager.recordFactory.storeComponent
                )
            if (results.defaults?.editions)
                await txManager.recordFactory.storeAll(
                    results.defaults.editions,
                    txManager.recordFactory.storeEdition
                )
        })

        await this.executor.setBadgeCount(results.badge)
        await this.executor.setRoleBadgeCount(results.role_badge)
        await SQLiteFactory.getInstance().didSync(userId, results.version)
        if (IS_DEVELOPMENT) {
            console.debug(`sql store ${new Date().getTime() - startOfSqlStore.getTime()}ms`)
            console.debug(`finished sync: ${new Date().getTime() - start.getTime()}ms`)
        }
    }

    static stringForArrayEntry(json: any[], idx: number): string | undefined {
        try {
            return json && json.length > idx ? json[idx] : undefined
        }
        catch (error: any) {
            console.debug(error)
            return undefined
        }
    }

    static valueForArrayEntry(json: any[], idx: number): any {
        try {
            return json && json.length > idx ? json[idx] : undefined
        }
        catch (error: any) {
            console.debug(error)
            return undefined
        }
    }

    static valueForArrayKey(json: any, key: string): any[] {
        try {
            return json && json[key] && json[key] !== '' && json[key] !== 'null' ? json[key] : []
        }
        catch (error: any) {
            console.debug(error)
            return []
        }
    }

    static valueForObjectKey(json: any, key: string): any {
        try {
            return json && json[key] && json[key] !== '' && json[key] !== 'null' ? json[key] : {}
        }
        catch (error: any) {
            console.debug(error)
            return {}
        }
    }

    static valueForBoolKey(json: any, key: string): boolean {
        try {
            return json && json[key] ? json[key] : false
        }
        catch (error: any) {
            console.debug(error)
            return false
        }
    }

    static valueForIntKey(json: any, key: string): number | undefined {
        try {
            return json && json[key] ? json[key] : undefined
        }
        catch (error: any) {
            console.debug(error)
            return undefined
        }
    }

    static valueForStringKey(json: any, key: string): string | undefined {
        try {
            return json && json[key] ? json[key] : undefined
        }
        catch (error: any) {
            console.debug(error)
            return undefined
        }
    }

    async fetchBooking(type: string, booking: Booking, forVendor: boolean): Promise<Result<any>> {
        const url = '/booking/sync'
        const start = moment()
        const jsonData: any = {}
        jsonData['id'] = booking.id
        jsonData['type'] = type
        jsonData['for_vendor'] = forVendor
        jsonData['date'] = formatTimeStamp(booking.date)
        jsonData['item'] = booking.item ? booking.item.id : ''
        const services: string[] = booking.services.map((service) => service.id!)
        jsonData['services'] = services
        jsonData['client'] = booking.client ? booking.client.id : ''
        jsonData['vendor'] = booking.vendor ? booking.vendor.id : ''
        if (booking.package) jsonData['package'] = booking.package.id
        if (!forVendor && booking.jobs && booking.jobs.length > 0)
            jsonData['employee'] = booking.jobs[0]!.person!.id
        jsonData['location'] = booking.location || ''
        jsonData['duration'] = booking.duration && booking.duration > 0 ? booking.duration : ''

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const result: any = {}
            const results = serverResult.data
            const itemData = RecordFactory.valueForArrayKey(results, 'items')
            const giftData = RecordFactory.valueForArrayKey(results, 'gifts')
            const formData = RecordFactory.valueForArrayKey(results, 'forms')
            const fieldData = RecordFactory.valueForArrayKey(results, 'fields')
            const lessonData = RecordFactory.valueForArrayKey(results, 'lessons')
            const packageData = RecordFactory.valueForArrayKey(results, 'packages')
            const employeeData = RecordFactory.valueForArrayKey(results, 'employees')
            const contractData = RecordFactory.valueForArrayKey(results, 'contracts')
            const categoryData = RecordFactory.valueForArrayKey(results, 'categories')
            const recordData = RecordFactory.valueForArrayKey(results, 'records')
            const signatureData = RecordFactory.valueForArrayKey(results, 'signatures')
            const packageAvailabilityData = RecordFactory.valueForObjectKey(
                results,
                'available_packages'
            )

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['booking'])
                    result['booking'] = await txManager.recordFactory.storeBooking(
                        RecordFactory.valueForObjectKey(results, 'booking')
                    )
                if (results['user'])
                    result['user'] = await txManager.recordFactory.storeUser(
                        RecordFactory.valueForObjectKey(results, 'user')
                    )
                if (results['client'])
                    result['client'] = await txManager.recordFactory.storeClient(
                        RecordFactory.valueForObjectKey(results, 'client')
                    )
                if (results['package'])
                    result['package'] = await txManager.recordFactory.storePackage(
                        RecordFactory.valueForObjectKey(results, 'package')
                    )
                if (results['promotion'])
                    result['promotion'] = await txManager.recordFactory.storePromotion(
                        RecordFactory.valueForObjectKey(results, 'promotion')
                    )
                if (results['packages']) {
                    const packages: Package[] = []
                    for (const data of packageData)
                        packages.push(await txManager.recordFactory.storePackage(data, true))
                    result['packages'] = packages
                    if (packages?.length) console.debug(`SYNC: synced ${packages.length} packages`)
                }
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['source']) await txManager.recordFactory.storeSource(results['source'])
                if (results['items']) {
                    const items: Item[] = []
                    if (!forVendor && type === 'load') {
                        await txManager.executeUpdate(
                            'DELETE FROM keys where exists(select id from items where owner=? and items.id=keys.id)',
                            booking.vendor!.id!
                        )
                        await txManager.executeUpdate(
                            'DELETE FROM items where owner=?',
                            booking.vendor!.id!
                        )
                    }
                    for (const data of itemData)
                        items.push(await txManager.recordFactory.storeItem(data, true))
                    result['items'] = items
                    if (items?.length) console.debug(`SYNC: synced ${items.length} items`)
                }
                if (results['employees']) {
                    const employees: User[] = []
                    for (const data of employeeData)
                        employees.push(await txManager.recordFactory.storeUser(data, true))
                    result['employees'] = employees
                    if (employees?.length)
                        console.debug(`SYNC: synced ${employees.length} employees`)
                }
                if (results['gifts']) {
                    const gifts: Gift[] = []
                    for (const data of giftData)
                        gifts.push(await txManager.recordFactory.storeGift(data, true))
                    result['gifts'] = gifts
                    if (gifts?.length) console.debug(`SYNC: synced ${gifts.length} gifts`)
                }
                if (results['fields']) {
                    const fields: Field[] = []
                    for (const data of fieldData)
                        fields.push(await txManager.recordFactory.storeField(data, true))
                    result['fields'] = fields
                    if (fields?.length) console.debug(`SYNC: synced ${fields.length} fields`)
                }
                if (results['lessons']) {
                    const lessons: Lesson[] = []
                    for (const data of lessonData)
                        lessons.push(await txManager.recordFactory.storeClass(data, true))
                    result['lessons'] = lessons
                    if (lessons?.length) result['lesson'] = lessons[0]
                    if (lessons?.length) console.debug(`SYNC: synced ${lessons.length} lessons`)
                }
                if (results['forms']) {
                    const forms: Type[] = []
                    for (const data of formData)
                        forms.push(await txManager.recordFactory.storeType(data, true))
                    result['forms'] = forms
                    if (forms?.length) console.debug(`SYNC: synced ${forms.length} forms`)
                }
                if (results['contracts']) {
                    const contracts: Contract[] = []
                    for (const data of contractData)
                        contracts.push(await txManager.recordFactory.storeContract(data, true))
                    result['contracts'] = contracts
                    if (contracts?.length)
                        console.debug(`SYNC: synced ${contracts.length} contracts`)
                }
                if (results['categories']) {
                    const categories: Category[] = []
                    for (const data of categoryData)
                        categories.push(await txManager.recordFactory.storeCategory(data, true))
                    result['categories'] = categories
                    if (categories?.length)
                        console.debug(`SYNC: synced ${categories.length} categories`)
                }
                if (recordData?.length)
                    await txManager.recordFactory.storeAll(
                        recordData,
                        txManager.recordFactory.storeRecord
                    )
                if (recordData?.length) console.debug(`SYNC: Synced ${recordData.length} records`)
                if (signatureData?.length)
                    await txManager.recordFactory.storeAll(
                        signatureData,
                        txManager.recordFactory.storeSignature
                    )
                if (signatureData?.length)
                    console.debug(`SYNC: Synced ${signatureData.length} signatures`)
                if (results['available_packages']) {
                    const availablePackages = new RecordDataMap(packageAvailabilityData)
                    if (availablePackages.size > 0)
                        console.debug(
                            `SYNC: synced ${availablePackages.size} available package links`
                        )
                    result['available_packages'] = availablePackages
                    if (
                        booking.package &&
                        Object.values(availablePackages).includes(booking.package.id) &&
                        (booking.lines.length === 0 ||
                            booking.lines.filter(
                                (line) =>
                                    availablePackages.hasValueForKey(line.item!.id!) &&
                                    booking.package!.id! ===
                                        availablePackages.valueForKey(line.item!.id!)
                            ).length > 0)
                    )
                        result['package'] = booking.package
                }
                console.debug(`Booking Sync Time (ms) `, moment().diff(start))
            })
            return okResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchOrder(type: string, order: Order, forVendor: boolean): Promise<Result<any>> {
        const url = '/order/sync'
        const start = moment()
        const jsonData: any = {}
        jsonData['id'] = order.id
        jsonData['type'] = type
        jsonData['for_vendor'] = forVendor
        if (order.date) jsonData['date'] = formatTimeStamp(order.date)
        jsonData['item'] = order.item ? order.item.id : ''
        const products: string[] = order.products.map((product) => product.id!)
        jsonData['products'] = products
        jsonData['client'] = order.client ? order.client.id : ''
        jsonData['vendor'] = order.vendor ? order.vendor.id : ''
        if (!forVendor && order.employee) jsonData['employee'] = order.employee.id
        jsonData['location'] = order.location || ''

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const result: any = {}
            const results = serverResult.data
            const itemData = RecordFactory.valueForArrayKey(results, 'items')
            const giftData = RecordFactory.valueForArrayKey(results, 'gifts')
            const productData = RecordFactory.valueForArrayKey(results, 'products')
            const contractData = RecordFactory.valueForArrayKey(results, 'contracts')
            const categoryData = RecordFactory.valueForArrayKey(results, 'categories')
            const recordData = RecordFactory.valueForArrayKey(results, 'records')
            const signatureData = RecordFactory.valueForArrayKey(results, 'signatures')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (results['order'])
                    result['order'] = await txManager.recordFactory.storeOrder(
                        RecordFactory.valueForObjectKey(results, 'order')
                    )
                if (results['client'])
                    result['client'] = await txManager.recordFactory.storeClient(
                        RecordFactory.valueForObjectKey(results, 'client')
                    )
                if (results['promotion'])
                    result['promotion'] = await txManager.recordFactory.storePromotion(
                        RecordFactory.valueForObjectKey(results, 'promotion')
                    )
                if (results['taxcode'])
                    await txManager.recordFactory.storeTaxcode(results['taxcode'])
                if (results['signature'])
                    await txManager.recordFactory.storeSignature(results['signature'])
                if (results['record']) await txManager.recordFactory.storeRecord(results['record'])
                if (results['form']) await txManager.recordFactory.storeType(results['form'])
                if (results['contract'])
                    await txManager.recordFactory.storeContract(results['contract'])
                if (results['subaccount'])
                    await txManager.recordFactory.storeSubaccount(results['subaccount'])
                if (results['source']) await txManager.recordFactory.storeSource(results['source'])
                if (results['items']) {
                    const items: Item[] = []
                    if (!forVendor && type === 'load') {
                        await txManager.executeUpdate(
                            'DELETE FROM keys where exists(select id from items where owner=? and items.id=keys.id)',
                            order.vendor!.id!
                        )
                        await txManager.executeUpdate(
                            'DELETE FROM items where owner=?',
                            order.vendor!.id!
                        )
                    }
                    for (const data of itemData)
                        items.push(await txManager.recordFactory.storeItem(data, true))
                    result['items'] = items
                    if (items?.length) console.debug(`SYNC: synced ${items.length} items`)
                }
                if (results['products']) {
                    const products: Item[] = []
                    for (const data of productData) products.push(new Item(data))
                    result['products'] = products
                    if (products?.length) console.debug(`SYNC: synced ${products.length} products`)
                }
                if (results['employee'])
                    result['employee'] = await txManager.recordFactory.storeUser(
                        RecordFactory.valueForObjectKey(results, 'employee')
                    )
                if (results['gifts']) {
                    const gifts: Gift[] = []
                    for (const data of giftData)
                        gifts.push(await txManager.recordFactory.storeGift(data, true))
                    result['gifts'] = gifts
                    if (gifts?.length) console.debug(`SYNC: synced ${gifts.length} gifts`)
                }
                if (recordData?.length)
                    await txManager.recordFactory.storeAll(
                        recordData,
                        txManager.recordFactory.storeRecord
                    )
                if (recordData?.length) console.debug(`SYNC: Synced ${recordData.length} records`)
                if (signatureData?.length)
                    await txManager.recordFactory.storeAll(
                        signatureData,
                        txManager.recordFactory.storeSignature
                    )
                if (signatureData?.length)
                    console.debug(`SYNC: Synced ${signatureData.length} signatures`)
                console.debug(`Order Sync Time (ms) `, moment().diff(start))
            })
            return okResult(result)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    // todo: implement
    // public void fetchRoyalty(final String type, final Royalty royalty, final boolean forVendor, final ServerCallBack onload, final ServerCallBack onfail) {
    //     String url = "/royalty/sync";
    //     final Date start = new Date();
    //     ServerFactory serverFactory = ServerFactory.getInstance();
    //     HashMap<String, Object> jsonData = new HashMap<>();
    //     jsonData.put("id", royalty.getId());
    //     jsonData.put("type", type);
    //     jsonData.put("for_vendor", forVendor);
    //     jsonData.put("owner", royalty.owner() != null ? royalty.owner().getId() : "");
    //     jsonData.put("client", royalty.client() != null ? royalty.client().getId() : "");
    //     serverFactory.post(url, jsonData, serverResult -> {
    //         final RecordDataMap result = new RecordDataMap();
    //         final JSONObject results = StringUtil.parseJSON(serverResult.toString());
    //         SQLiteFactory.getInstance().executeTransaction(() -> {
    //             try {
    //                 if (results.has("royalty"))
    //                     result.put("royalty", storeRoyalty(valueForObjectKey(results, "royalty")));
    //                 if (results.has("client"))
    //                     result.put("client", storeUser(valueForObjectKey(results, "client")));
    //                 if (results.has("source"))
    //                     result.put("source", storeSource(valueForObjectKey(results, "source")));
    //                 if (results.has("items")) {
    //                     final JSONArray itemData = valueForArrayKey(results, "items");
    //                     for (int i = 0; i < itemData.length(); i++)
    //                         storeItem(itemData.getJSONObject(i));
    //                     if (itemData.length() > 0)
    //                         Log.i("SYNC", String.format("synced %d items", itemData.length()));
    //                 }
    //                 Log.i("Royalty Sync Time (ms) ", String.valueOf((new Date().getTime() - start.getTime())));
    //             }
    //             catch (Exception e) {
    //                 ErrorLogic.log(e);
    //             }
    //         });
    //         if (onload != null)
    //             onload.execute(result);
    //     },
    //     result -> {
    //         if (onfail != null) {
    //             onfail.execute(result);
    //         }
    //     });
    // }

    async fetchThread(
        thread: BaseRecord,
        employee?: User,
        createdBeforeDate?: moment.Moment,
        cancelToken?: CancelToken
    ): Promise<Result<Message[]>> {
        let url = '/thread/sync'
        if (createdBeforeDate) {
            url += '/' + createdBeforeDate.format('YYYYMMDDHHmmss')
        }
        const start = moment()
        const jsonData: any = employee
            ? { thread: thread.id, employee: employee.id }
            : { thread: thread.id }

        try {
            const serverResult = await apiClient.post(url, jsonData, { cancelToken })

            const messages: Message[] = []
            const results = serverResult.data
            const messageData = RecordFactory.valueForArrayKey(results, 'messages')
            const bookingData = RecordFactory.valueForArrayKey(results, 'bookings')
            const orderData = RecordFactory.valueForArrayKey(results, 'orders')
            const royaltyData = RecordFactory.valueForArrayKey(results, 'royalties')
            const invoiceData = RecordFactory.valueForArrayKey(results, 'invoices')
            const paymentData = RecordFactory.valueForArrayKey(results, 'payments')
            const chargeData = RecordFactory.valueForArrayKey(results, 'charges')
            const classData = RecordFactory.valueForArrayKey(results, 'classes')
            const taskData = RecordFactory.valueForArrayKey(results, 'tasks')
            const giftData = RecordFactory.valueForArrayKey(results, 'gifts')
            const typeData = RecordFactory.valueForArrayKey(results, 'forms')
            const payeeData = RecordFactory.valueForArrayKey(results, 'payees')
            const clientData = RecordFactory.valueForArrayKey(results, 'clients')
            const recordData = RecordFactory.valueForArrayKey(results, 'records')
            const packageData = RecordFactory.valueForArrayKey(results, 'packages')
            const contactData = RecordFactory.valueForArrayKey(results, 'contacts')
            const estimateData = RecordFactory.valueForArrayKey(results, 'estimates')
            const contractData = RecordFactory.valueForArrayKey(results, 'contracts')
            const signatureData = RecordFactory.valueForArrayKey(results, 'signatures')
            const conferenceData = RecordFactory.valueForArrayKey(results, 'conferences')
            const subaccountData = RecordFactory.valueForArrayKey(results, 'subaccounts')
            const subscriptionData = RecordFactory.valueForArrayKey(results, 'subscriptions')
            const reviewData = RecordFactory.valueForArrayKey(results, 'reviews')
            const changeData = RecordFactory.valueForArrayKey(results, 'changes')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
                await txManager.recordFactory.storeAll(
                    subaccountData,
                    txManager.recordFactory.storeSubaccount
                )
                if (subaccountData?.length)
                    console.debug(`SYNC: Synced ${subaccountData.length} subaccounts`)
                await txManager.recordFactory.storeAll(
                    payeeData,
                    txManager.recordFactory.storePayee
                )
                if (payeeData?.length) console.debug(`SYNC: Synced ${payeeData.length} payees`)
                await txManager.recordFactory.storeAll(
                    bookingData,
                    txManager.recordFactory.storeBooking
                )
                if (bookingData?.length)
                    console.debug(`SYNC: Synced ${bookingData.length} bookings`)
                await txManager.recordFactory.storeAll(
                    orderData,
                    txManager.recordFactory.storeOrder
                )
                if (orderData?.length) console.debug(`SYNC: Synced ${orderData.length} orders`)
                await txManager.recordFactory.storeAll(
                    royaltyData,
                    txManager.recordFactory.storeRoyalty
                )
                if (royaltyData?.length)
                    console.debug(`SYNC: Synced ${royaltyData.length} royalties`)
                await txManager.recordFactory.storeAll(
                    invoiceData,
                    txManager.recordFactory.storeInvoice
                )
                if (invoiceData?.length)
                    console.debug(`SYNC: Synced ${invoiceData.length} invoices`)
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                if (paymentData?.length)
                    console.debug(`SYNC: Synced ${paymentData.length} payments`)
                await txManager.recordFactory.storeAll(
                    chargeData,
                    txManager.recordFactory.storeCharge
                )
                if (chargeData?.length) console.debug(`SYNC: Synced ${chargeData.length} charges`)
                await txManager.recordFactory.storeAll(
                    estimateData,
                    txManager.recordFactory.storeEstimate
                )
                if (estimateData?.length)
                    console.debug(`SYNC: Synced ${estimateData.length} estimates`)
                await txManager.recordFactory.storeAll(
                    classData,
                    txManager.recordFactory.storeClass
                )
                if (classData?.length) console.debug(`SYNC: Synced ${classData.length} classes`)
                await txManager.recordFactory.storeAll(
                    packageData,
                    txManager.recordFactory.storePackage
                )
                if (packageData?.length)
                    console.debug(`SYNC: Synced ${packageData.length} packages`)
                await txManager.recordFactory.storeAll(
                    subscriptionData,
                    txManager.recordFactory.storeSubscription
                )
                if (subscriptionData?.length)
                    console.debug(`SYNC: Synced ${subscriptionData.length} subscriptions`)
                await txManager.recordFactory.storeAll(taskData, txManager.recordFactory.storeTask)
                if (taskData?.length) console.debug(`SYNC: Synced ${taskData.length} tasks`)
                await txManager.recordFactory.storeAll(giftData, txManager.recordFactory.storeGift)
                if (giftData?.length) console.debug(`SYNC: Synced ${giftData.length} gifts`)
                await txManager.recordFactory.storeAll(typeData, txManager.recordFactory.storeType)
                if (typeData?.length) console.debug(`SYNC: Synced ${typeData.length} types`)
                await txManager.recordFactory.storeAll(
                    recordData,
                    txManager.recordFactory.storeRecord
                )
                if (recordData?.length) console.debug(`SYNC: Synced ${recordData.length} records`)
                await txManager.recordFactory.storeAll(
                    contractData,
                    txManager.recordFactory.storeContract
                )
                if (contractData?.length)
                    console.debug(`SYNC: Synced ${contractData.length} contracts`)
                await txManager.recordFactory.storeAll(
                    signatureData,
                    txManager.recordFactory.storeSignature
                )
                if (signatureData?.length)
                    console.debug(`SYNC: Synced ${signatureData.length} signatures`)
                await txManager.recordFactory.storeAll(
                    conferenceData,
                    txManager.recordFactory.storeConference
                )
                if (conferenceData?.length)
                    console.debug(`SYNC: Synced ${conferenceData.length} conferences`)
                await txManager.recordFactory.storeAll(
                    reviewData,
                    txManager.recordFactory.storeReview
                )
                if (reviewData?.length) console.debug(`SYNC: Synced ${reviewData.length} reviews`)
                await txManager.recordFactory.storeAll(
                    changeData,
                    txManager.recordFactory.storeChange
                )
                if (changeData?.length) console.debug(`SYNC: Synced ${changeData.length} changes`)
                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                messages.push(
                    ...(await Promise.all(
                        messageData.map(
                            async (m: Message) =>
                                await txManager.recordFactory.storeMessage(m, true)
                        )
                    ))
                )
                if (messageData?.length)
                    console.debug(`SYNC: Synced ${messageData.length} messages`)
                if (!employee)
                    await txManager.executeUpdate(
                        'REPLACE INTO threads(id, lastSyncDate, createdDate) VALUES (?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)',
                        thread.id!
                    )
                console.debug(`Thread Sync Time (ms) `, moment().diff(start))
            })
            return okResult(messages)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchMessage(id: string): Promise<Result<Message>> {
        const url = '/message/sync'
        const start = moment()
        const jsonData: any = { id: id }

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const messages: Message[] = []
            const results = serverResult.data
            const messageData = RecordFactory.valueForArrayKey(results, 'messages')
            const bookingData = RecordFactory.valueForArrayKey(results, 'bookings')
            const orderData = RecordFactory.valueForArrayKey(results, 'orders')
            const royaltyData = RecordFactory.valueForArrayKey(results, 'royalties')
            const invoiceData = RecordFactory.valueForArrayKey(results, 'invoices')
            const paymentData = RecordFactory.valueForArrayKey(results, 'payments')
            const chargeData = RecordFactory.valueForArrayKey(results, 'charges')
            const estimateData = RecordFactory.valueForArrayKey(results, 'estimates')
            const classData = RecordFactory.valueForArrayKey(results, 'classes')
            const taskData = RecordFactory.valueForArrayKey(results, 'tasks')
            const giftData = RecordFactory.valueForArrayKey(results, 'gifts')
            const typeData = RecordFactory.valueForArrayKey(results, 'forms')
            const recordData = RecordFactory.valueForArrayKey(results, 'records')
            const signatureData = RecordFactory.valueForArrayKey(results, 'signatures')
            const conferenceData = RecordFactory.valueForArrayKey(results, 'conferences')
            const contactData = RecordFactory.valueForArrayKey(results, 'contacts')
            const reviewData = RecordFactory.valueForArrayKey(results, 'reviews')
            const changeData = RecordFactory.valueForArrayKey(results, 'changes')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    bookingData,
                    txManager.recordFactory.storeBooking
                )
                if (bookingData?.length)
                    console.debug(`SYNC: Synced ${bookingData.length} bookings`)
                await txManager.recordFactory.storeAll(
                    orderData,
                    txManager.recordFactory.storeOrder
                )
                if (orderData?.length) console.debug(`SYNC: Synced ${orderData.length} orders`)
                await txManager.recordFactory.storeAll(
                    royaltyData,
                    txManager.recordFactory.storeRoyalty
                )
                if (royaltyData?.length)
                    console.debug(`SYNC: Synced ${royaltyData.length} royalties`)
                await txManager.recordFactory.storeAll(
                    invoiceData,
                    txManager.recordFactory.storeInvoice
                )
                if (invoiceData?.length)
                    console.debug(`SYNC: Synced ${invoiceData.length} invoices`)
                await txManager.recordFactory.storeAll(
                    paymentData,
                    txManager.recordFactory.storePayment
                )
                if (paymentData?.length)
                    console.debug(`SYNC: Synced ${paymentData.length} payments`)
                await txManager.recordFactory.storeAll(
                    chargeData,
                    txManager.recordFactory.storeCharge
                )
                if (chargeData?.length) console.debug(`SYNC: Synced ${chargeData.length} charges`)
                await txManager.recordFactory.storeAll(
                    estimateData,
                    txManager.recordFactory.storeEstimate
                )
                if (estimateData?.length)
                    console.debug(`SYNC: Synced ${estimateData.length} estimates`)
                await txManager.recordFactory.storeAll(
                    classData,
                    txManager.recordFactory.storeClass
                )
                if (classData?.length) console.debug(`SYNC: Synced ${classData.length} classes`)
                await txManager.recordFactory.storeAll(taskData, txManager.recordFactory.storeTask)
                if (taskData?.length) console.debug(`SYNC: Synced ${taskData.length} tasks`)
                await txManager.recordFactory.storeAll(giftData, txManager.recordFactory.storeGift)
                if (giftData?.length) console.debug(`SYNC: Synced ${giftData.length} gifts`)
                await txManager.recordFactory.storeAll(typeData, txManager.recordFactory.storeType)
                if (typeData?.length) console.debug(`SYNC: Synced ${typeData.length} types`)
                await txManager.recordFactory.storeAll(
                    recordData,
                    txManager.recordFactory.storeRecord
                )
                if (recordData?.length) console.debug(`SYNC: Synced ${recordData.length} records`)
                await txManager.recordFactory.storeAll(
                    signatureData,
                    txManager.recordFactory.storeSignature
                )
                if (signatureData?.length)
                    console.debug(`SYNC: Synced ${signatureData.length} signatures`)
                await txManager.recordFactory.storeAll(
                    conferenceData,
                    txManager.recordFactory.storeConference
                )
                if (conferenceData?.length)
                    console.debug(`SYNC: Synced ${conferenceData.length} conferences`)
                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    reviewData,
                    txManager.recordFactory.storeReview
                )
                if (reviewData?.length) console.debug(`SYNC: Synced ${reviewData.length} reviews`)
                await txManager.recordFactory.storeAll(
                    changeData,
                    txManager.recordFactory.storeChange
                )
                if (changeData?.length) console.debug(`SYNC: Synced ${changeData.length} changes`)
                for (const data of messageData)
                    messages.push(await txManager.recordFactory.storeMessage(data, true))
                if (messageData?.length)
                    console.debug(`SYNC: Synced ${messageData.length} messages`)
                console.debug(`Message Sync Time (ms) `, moment().diff(start))
            })
            return okResult(messages[0])
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchInbox(
        personId: string | undefined,
        createdBeforeDate?: moment.Moment,
        cancelToken?: CancelToken
    ): Promise<Result<Message[]>> {
        const start = moment()
        const postArgs: any = {}
        postArgs['limit'] = 50
        if (createdBeforeDate) {
            postArgs['created_before'] = createdBeforeDate.format('YYYYMMDDHHmmss')
        }
        const url = personId ? `/message/inbox/${personId}` : '/message/inbox'

        try {
            const serverResult = await apiClient.post(url, postArgs, { cancelToken })

            const messages: Message[] = []
            const resultData = serverResult.data
            const messageData = RecordFactory.valueForArrayKey(resultData, 'messages')
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')
            const clientData = RecordFactory.valueForArrayKey(resultData, 'clients')
            const payeeData = RecordFactory.valueForArrayKey(resultData, 'payees')
            const teamData = RecordFactory.valueForArrayKey(resultData, 'teams')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                messages.push(
                    ...(await Promise.all(
                        messageData.map(
                            async (m: Message) =>
                                await txManager.recordFactory.storeMessage(m, true)
                        )
                    ))
                )
                if (messageData?.length)
                    console.debug(`SYNC: Synced ${messageData.length} messages`)
                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
                await txManager.recordFactory.storeAll(
                    payeeData,
                    txManager.recordFactory.storePayee
                )
                if (payeeData?.length) console.debug(`SYNC: Synced ${payeeData.length} payees`)
                await txManager.recordFactory.storeAll(teamData, txManager.recordFactory.storeTeam)
                if (teamData?.length) console.debug(`SYNC: Synced ${teamData.length} teams`)
                // if (createdSinceDate && !personId)
                // todo: this.executor.setLastInboxSyncDate();
                if (!personId) await txManager.setBadgeCount(resultData['badge'])
                console.debug(
                    `FETCH: fetchInbox Time: ${moment().diff(start)}ms (${messages.length} messages)`
                )
            })
            return okResult(messages)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchClients(
        page: string,
        client: User | undefined,
        cancelToken?: CancelToken
    ): Promise<Result<any>> {
        const start = moment()
        const postArgs: any = { page: page.toLowerCase(), limit: 250 }
        if (client) postArgs['person'] = client.id
        const url = '/client/sync'

        try {
            const serverResult = await apiClient.post(url, postArgs, { cancelToken })

            const users: User[] = []
            const subaccounts: Subaccount[] = []
            const instance: any = {}
            const resultData = serverResult.data
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')
            const clientData = RecordFactory.valueForArrayKey(resultData, 'clients')
            const subaccountData = RecordFactory.valueForArrayKey(resultData, 'subaccounts')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                instance['users'] = users
                instance['subaccounts'] = subaccounts
                instance['limit'] = RecordFactory.valueForIntKey(resultData, 'limit')
                users.push(
                    ...(await Promise.all(
                        contactData.map((data) => txManager.recordFactory.storeUser(data, true))
                    ))
                )
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
                for (const data of subaccountData) {
                    subaccounts.push(await txManager.recordFactory.storeSubaccount(data, true))
                }
                if (subaccountData?.length)
                    console.debug(`SYNC: Synced ${subaccountData.length} subaccounts`)
                console.debug(
                    `FETCH: fetchClients Time: ${moment().diff(start)}ms for page ${page} (${users.length} clients)`
                )
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchContacts(page: string): Promise<Result<any>> {
        const start = moment()
        const url = '/contact/sync'

        try {
            const serverResult = await apiClient.post(url, { type: 'view', page: page })

            const users: Contact[] = []
            const instance: any = {}
            const resultData = serverResult.data
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')

            instance['users'] = users
            instance['limit'] = RecordFactory.valueForIntKey(resultData, 'limit')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const data of contactData)
                    users.push(await txManager.recordFactory.storeContact(data, true))
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                console.debug(
                    `FETCH: fetchContacts Time: ${moment().diff(start)}ms for page ${page} (${users.length} contacts)`
                )
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPayees(page?: string): Promise<Result<any>> {
        const start = moment()
        const url = '/payee/sync'

        try {
            const serverResult = await apiClient.post(url, { page })

            const users: User[] = []
            const instance: any = {}
            const resultData = serverResult.data
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')
            const payeeData = RecordFactory.valueForArrayKey(resultData, 'payees')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                instance['users'] = users
                instance['limit'] = RecordFactory.valueForIntKey(resultData, 'limit')
                for (const data of contactData)
                    users.push(await txManager.recordFactory.storeUser(data, true))
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    payeeData,
                    txManager.recordFactory.storePayee
                )
                if (payeeData?.length) console.debug(`SYNC: Synced ${payeeData.length} payees`)
                console.debug(
                    `FETCH: fetchPayees Time: ${moment().diff(start)}ms for page ${page} (${users.length} payees)`
                )
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchSubscribers(page: string): Promise<Result<RecordDataMap>> {
        const start = moment()
        const postArgs: any = { page: page || '' }
        const url = '/subscriber/sync'

        try {
            const serverResult = await apiClient.post(url, postArgs)

            const users: User[] = []
            const instance = new RecordDataMap()
            const resultData = JSON.parse(serverResult.data)
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')
            const subscriberData = RecordFactory.valueForArrayKey(resultData, 'subscribers')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                instance.put('users', users)
                instance.put('limit', RecordFactory.valueForIntKey(resultData, 'limit'))
                for (const data of contactData)
                    users.push(await txManager.recordFactory.storeUser(data, true))
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    subscriberData,
                    txManager.recordFactory.storeSubscriber
                )
                if (subscriberData?.length)
                    console.debug(`SYNC: Synced ${subscriberData.length} subscribers`)
                console.debug(
                    `FETCH: fetchSubscribers Time: ${moment().diff(start)}ms for page ${page} (${users.length} subscribers)`
                )
            })
            return okResult(instance)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCredits(
        trigger: Trigger,
        date: moment.Moment | undefined,
        keywords: string,
        client: User | undefined
    ): Promise<Result<RecordDataMap[]>> {
        const postArgs: any = {}
        if (date) postArgs['created_before'] = date.format('YYYYMMDDHHmmss')
        if (keywords) postArgs['keywords'] = keywords
        if (client) postArgs['client'] = client.id
        if (trigger) postArgs['trigger'] = trigger.id

        const start = moment()
        const credits: RecordDataMap[] = []
        const url = '/credits/sync'

        try {
            const serverResult = await apiClient.post(url, postArgs)

            const resultData = JSON.parse(serverResult.data)
            const creditData = RecordFactory.valueForArrayKey(resultData, 'messages')
            const contactData = RecordFactory.valueForArrayKey(resultData, 'contacts')
            const clientData = RecordFactory.valueForArrayKey(resultData, 'clients')

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const data of creditData) credits.push(new RecordDataMap(data))
                if (credits?.length) console.debug(`SYNC: Synced ${credits.length} credits`)
                await txManager.recordFactory.storeAll(
                    contactData,
                    txManager.recordFactory.storeUser
                )
                if (contactData?.length)
                    console.debug(`SYNC: Synced ${contactData.length} contacts`)
                await txManager.recordFactory.storeAll(
                    clientData,
                    txManager.recordFactory.storeClient
                )
                if (clientData?.length) console.debug(`SYNC: Synced ${clientData.length} clients`)
                console.debug(
                    `SYNC: fetchCredits Time: ${moment().diff(start)}ms (${credits.length} results, ${clientData.length} clients)`
                )
            })
            return okResult(credits)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchDashboards(
        person: string | undefined,
        dashboard: string | undefined,
        subaccount: string | undefined,
        tab: DASHBOARD_TAB | undefined
    ): Promise<Result<RecordDataMap>> {
        if (!tab) tab = DASHBOARD_TAB.DASHBOARD_TAB_NONE
        const url = '/dashboards'
        const start = moment()
        const postData: any = {}
        if (person) postData['person'] = person
        if (dashboard) postData['dashboard'] = dashboard
        if (subaccount) postData['subaccount'] = subaccount
        if (tab !== DASHBOARD_TAB.DASHBOARD_TAB_NONE)
            postData['tab'] = tab === DASHBOARD_TAB.DASHBOARD_TAB_MAIN ? 'main' : 'account'

        try {
            const serverResult = await apiClient.post(url, postData)

            const results = serverResult.data
            const newTab = RecordFactory.valueForStringKey(results, 'tab')
            const tabs = RecordFactory.valueForArrayKey(results, 'tabs')
            const edition = RecordFactory.valueForObjectKey(results, 'edition')
            const dashboards = RecordFactory.valueForArrayKey(results, 'dashboards')
            const extraTypeData = RecordFactory.valueForArrayKey(results, 'extra_types')
            const extraResultData = RecordFactory.valueForArrayKey(results, 'extra_results')
            const extraDashboards = RecordFactory.valueForArrayKey(results, 'extra_dashboards')
            const newTabs: RecordDataMap[] = tabs.map((t) => new RecordDataMap(t))
            const newDashboards: Dashboard[] = []
            let newEdition: Edition | undefined = undefined

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const data of dashboards) {
                    const newDashboard = !person
                        ? await txManager.recordFactory.storeDashboard(data, true)
                        : new Dashboard(data)
                    if (
                        newDashboard.forSetup ||
                        newDashboard.forAlert ||
                        newDashboard.type === DASHBOARD_TYPE.DASHBOARD_TYPE_QUICKSTART ||
                        newDashboard.type === DASHBOARD_TYPE.DASHBOARD_TYPE_VERIFICATIONS
                    )
                        newDashboard.results = newDashboard._results
                    if (newDashboard.showOnDashboard) newDashboards.push(newDashboard)
                }
                console.debug(`FETCH: Fetched ${dashboards.length} dashboards`)
                for (const data of extraDashboards) newDashboards.push(new Dashboard(data))
                if (extraDashboards?.length)
                    console.debug(`FETCH: Fetched ${extraDashboards.length} extra dashboards`)
                for (let i = 0; i < extraResultData.length; i++)
                    await txManager.recordFactory.store(extraTypeData[i], extraResultData[i])
                if (extraResultData?.length)
                    console.debug(`FETCH: Fetched ${extraResultData.length} extra results`)
                console.debug(`FETCH: Dashboards Fetch Time: ${moment().diff(start)}ms`)
                newEdition = await txManager.recordFactory.storeEdition(edition, true)
            })

            return okResult(
                // TODO: remove DashboardComponent dependency on edition being returned by getting it from owner
                new RecordDataMap({
                    dashboards: newDashboards,
                    tabs: newTabs,
                    tab: newTab,
                    edition: newEdition,
                })
            )
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchDashboard(
        dashboardId: string,
        person: string | undefined,
        subaccount: string | undefined,
        view: number,
        dateType: string
    ): Promise<Result<Dashboard>> {
        const start = moment()
        const url = `/dashboard/${dashboardId}`
        const fetchArgs: any = { view: view, date_type: dateType }
        if (person) fetchArgs['person'] = person
        if (subaccount) fetchArgs['subaccount'] = subaccount

        try {
            const serverResult = await apiClient.post(url, fetchArgs)

            const resultsArray: any[] = []
            const results = serverResult.data
            const typeData = RecordFactory.valueForArrayKey(results, 'types')
            const resultData = RecordFactory.valueForArrayKey(results, 'results')
            const extraTypeData = RecordFactory.valueForArrayKey(results, 'extra_types')
            const extraResultData = RecordFactory.valueForArrayKey(results, 'extra_results')
            let newDashboard = new Dashboard(RecordFactory.valueForObjectKey(results, 'dashboard'))

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < resultData.length; i++)
                    resultsArray.push(
                        typeData.length > 0
                            ? await txManager.recordFactory.store(typeData[i], resultData[i])
                            : new RecordDataMap(resultData[i])
                    )
                for (let i = 0; i < extraResultData.length; i++)
                    await txManager.recordFactory.store(extraTypeData[i], extraResultData[i])
            })

            if (resultsArray?.length) console.debug(`FETCH: Fetched ${resultsArray.length} results`)
            if (extraResultData?.length)
                console.debug(`FETCH: Fetched ${extraResultData.length} extra results`)
            console.debug(`FETCH: Dashboard Fetch Time: ${moment().diff(start)}ms`)

            newDashboard.results = resultsArray
            return okResult(newDashboard)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchWelcomeDashboard(dashboard: Dashboard): Promise<Result<Dashboard>> {
        const url = `/dashboard/${dashboard.id}/welcome`

        try {
            const serverResult = await apiClient.post(url, null)

            const results = serverResult.data
            let newDashboard = await this.storeDashboard(
                RecordFactory.valueForObjectKey(results, 'dashboard'),
                true
            )
            return okResult(newDashboard)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async markDashboard(
        dashboard: Dashboard,
        setting: string | undefined,
        label: string | undefined
    ): Promise<Result<Dashboard>> {
        const start = moment()
        const url = `/dashboard/${dashboard.id}/mark`
        const postData: any = {}
        if (setting) postData['setting'] = setting
        if (label) postData['label'] = label

        try {
            const result = await apiClient.post(url, postData)
            const results = result.data
            const dashboardData = results['dashboard']
            const typeData = dashboardData['types']
            const resultData = dashboardData['results']
            const extraTypeData = dashboardData['extra_types']
            const extraResultData = dashboardData['extra_results']
            const newDashboard = new Dashboard(dashboardData)
            const resultsArray: any[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                if (resultData) {
                    for (let i = 0; i < resultData.length; i++)
                        resultsArray.push(
                            typeData && typeData.length > 0
                                ? await txManager.recordFactory.store(typeData[i], resultData[i])
                                : new RecordDataMap(resultData[i])
                        )
                }

                if (extraResultData) {
                    for (let i = 0; i < extraResultData.length; i++)
                        await txManager.recordFactory.store(extraTypeData[i], extraResultData[i])
                }
            })

            if (resultsArray?.length) console.debug(`FETCH: Stored ${resultsArray.length} results`)
            if (extraResultData?.length)
                console.debug(`FETCH: Stored ${extraResultData.length} extra results`)
            console.debug(`markDashboard Fetch Time: ${moment().diff(start)}ms`)

            newDashboard.results = resultsArray
            return okResult(newDashboard)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async requestCall(user: User): Promise<VoidResult> {
        const url = `/user/${user.id}/request-call`
        try {
            await apiClient.post(url)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async loadReport(idString?: string): Promise<Report | undefined> {
        if (idString) return await this.fetchRecord(idString, Report)
        return undefined
    }

    async loadComponent(idString?: string): Promise<PSComponent | undefined> {
        if (idString) return await this.fetchRecord(idString, PSComponent)
        return undefined
    }

    async loadCriteria(idString?: string): Promise<Criteria | undefined> {
        if (idString) return await this.fetchRecord(idString, Criteria)
        return undefined
    }

    async storeReport<LoadFKST extends boolean>(
        report: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Report : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO reports(id, owner, feature, title, seqnum, jsonData, createdDate, cancelledDate, lastModifiedDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                report.id,
                report.owner,
                report.feature,
                report.title,
                report.seqnum,
                JSON.stringify(report),
                report.created_date,
                report.cancelled_date,
                report.last_modified_date
            ),
            this.executor.executeUpdate(KEY_SQL, report.id, 'report'),
        ])
        CacheFactory.getInstance().removeRecord(Report.dataType(), report)
        return loadFks ? this.fetchRecord(report.id, Report) : report.id
    }

    async storeComponent<LoadFKST extends boolean>(
        component: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? PSComponent : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO components(id, type, feature, title, field, seqnum, jsonData, cancelledDate, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                component.id,
                component.type,
                component.feature,
                component.title,
                component.field,
                component.seqnum,
                JSON.stringify(component),
                component.cancelled_date,
                component.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, component.id, 'component'),
        ])
        CacheFactory.getInstance().removeRecord(PSComponent.dataType(), component)
        return loadFks ? this.fetchRecord(component.id, PSComponent) : component.id
    }

    async storeCriteria<LoadFKST extends boolean>(
        criteria: any,
        loadFks: LoadFKST = true as LoadFKST
    ): Promise<LoadFKST extends true ? Criteria : string> {
        await Promise.all([
            this.executor.executeUpdate(
                'REPLACE INTO criterias(id, feature, title, field, seqnum, jsonData, createdDate, lastSyncDate) VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)',
                criteria.id,
                criteria.feature,
                criteria.title,
                criteria.field,
                criteria.seqnum,
                JSON.stringify(criteria),
                criteria.created_date
            ),
            this.executor.executeUpdate(KEY_SQL, criteria.id, 'criteria'),
        ])
        CacheFactory.getInstance().removeRecord(Criteria.dataType(), criteria)
        return loadFks ? this.fetchRecord(criteria.id, Criteria) : criteria.id
    }

    async loadReports(): Promise<Report[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('reports.jsonData')
        sql.addTable('reports')
        sql.filterBy('reports.cancelledDate IS NULL')
        sql.filterBy("(reports.owner IS NULL OR reports.owner == '' OR reports.owner=?)", owner.id!)
        sql.orderBy(
            'CASE WHEN CAST(reports.seqnum as integer) > 0 THEN CAST(reports.seqnum as integer) ELSE 1000 END'
        )
        sql.orderBy('reports.title')
        sql.fk = Report.fk

        const items = await this.executor.executeSql(sql)
        const results: Report[] = []
        for (const item of items) {
            const report = new Report(JSON.parse(item['jsonData']))
            if (Report.allowReport(report, owner)) results.push(report)
        }
        return results
    }

    async loadComponents(type: string): Promise<PSComponent[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('components.jsonData')
        sql.addTable('components')
        sql.filterBy('components.cancelledDate IS NULL')
        if (type) sql.filterBy('components.type=?', type)
        sql.orderBy('components.type')
        sql.orderBy('CAST(components.seqnum as integer)')
        sql.fk = PSComponent.fk

        const items = await this.executor.executeSql(sql)
        const results: PSComponent[] = []
        for (const item of items) {
            const component = new PSComponent(JSON.parse(item['jsonData']))
            if (Report.allowComponent(component, owner)) results.push(component)
        }
        return results
    }

    async loadCriterias(): Promise<Criteria[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('criterias.jsonData')
        sql.addTable('criterias')
        sql.filterBy('criterias.cancelledDate IS NULL')
        sql.orderBy('CAST(criterias.seqnum as integer)')
        sql.fk = Criteria.fk

        const items = await this.executor.executeSql(sql)
        const results: Criteria[] = []
        for (const item of items) {
            const criteria = new Criteria(JSON.parse(item['jsonData']))
            if (Report.allowCriteria(criteria, owner)) results.push(criteria)
        }
        return results
    }

    async sendReport(report: Report, isCreatingNewReport: boolean): Promise<Result<Report>> {
        const url = report.id && !isCreatingNewReport ? `/report/${report.id}` : '/report'
        try {
            const result = await apiClient.post(url, report.jsonObject())

            const resultData = result.data
            const newReport = await this.storeReport(
                RecordFactory.valueForObjectKey(resultData, 'report'),
                true
            )
            this.postNotification('ReportDataSourceChanged', null)
            return okResult(newReport)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteReport(idString: string): Promise<Result<Report>> {
        const url = `/report/${idString}/delete`
        try {
            const result = await apiClient.post(url, null)

            const resultData = result.data
            const newReport = await this.storeReport(resultData['report'], true)
            this.postNotification('ReportDataSourceChanged', null)
            return okResult(newReport)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchReport(reportId: string): Promise<Result<Report | undefined>> {
        const start = moment()
        const url = '/report/sync'
        const jsonData: any = {}
        if (reportId) jsonData['id'] = reportId

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const results = serverResult.data
            const reports = RecordFactory.valueForArrayKey(results, 'reports')
            const typeData = RecordFactory.valueForArrayKey(results, 'extra_types')
            const resultData = RecordFactory.valueForArrayKey(results, 'extra_results')
            const instances: Report[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const report of reports)
                    instances.push(await txManager.recordFactory.storeReport(report, true))
                if (reports?.length) console.debug(`SYNC: Synced ${reports.length} reports`)
                for (let i = 0; i < resultData.length; i++)
                    await txManager.recordFactory.store(
                        RecordFactory.stringForArrayEntry(typeData, i),
                        RecordFactory.valueForArrayEntry(resultData, i)
                    )
                if (resultData?.length)
                    console.debug(`SYNC: Synced ${resultData.length} extra instances`)
                console.debug(`fetchReport Fetch Time (ms) `, moment().diff(start))
            })
            return okResult(reportId && instances.length > 0 ? instances[0] : undefined)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchComponent(
        componentId?: string,
        type?: string
    ): Promise<Result<PSComponent | undefined>> {
        const start = moment()
        const url = '/component/sync'
        const jsonData: any = {}
        if (type) jsonData['type'] = type
        if (componentId) jsonData['id'] = componentId

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const results = serverResult.data
            const components = RecordFactory.valueForArrayKey(results, 'components')
            const typeData = RecordFactory.valueForArrayKey(results, 'extra_types')
            const resultData = RecordFactory.valueForArrayKey(results, 'extra_results')
            const instances: PSComponent[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const component of components)
                    instances.push(await txManager.recordFactory.storeComponent(component, true))
                if (components?.length)
                    console.debug(`SYNC: Synced ${components.length} components`)
                for (let i = 0; i < resultData.length; i++)
                    await txManager.recordFactory.store(
                        RecordFactory.stringForArrayEntry(typeData, i),
                        RecordFactory.valueForArrayEntry(resultData, i)
                    )
                if (resultData?.length)
                    console.debug(`SYNC: Synced ${resultData.length} extra instances`)
                console.debug(`fetchComponent Fetch Time (ms) `, moment().diff(start))
            })
            return okResult(componentId && instances.length > 0 ? instances[0] : undefined)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCriteria(criteriaId: string): Promise<Result<Criteria | undefined>> {
        const start = moment()
        const url = '/criteria/sync'
        const jsonData: any = {}
        if (criteriaId) jsonData['id'] = criteriaId

        try {
            const serverResult = await apiClient.post(url, jsonData)

            const result: any = {}
            const results = serverResult.data
            const criterias = RecordFactory.valueForArrayKey(results, 'criterias')
            const typeData = RecordFactory.valueForArrayKey(results, 'extra_types')
            const resultData = RecordFactory.valueForArrayKey(results, 'extra_results')
            const instances: Criteria[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const criteria of criterias)
                    instances.push(await txManager.recordFactory.storeCriteria(criteria, true))
                if (criterias?.length) console.debug(`SYNC: Synced ${criterias.length} criterias`)
                for (let i = 0; i < resultData.length; i++)
                    await txManager.recordFactory.store(
                        RecordFactory.stringForArrayEntry(typeData, i),
                        RecordFactory.valueForArrayEntry(resultData, i)
                    )
                if (resultData?.length)
                    console.debug(`SYNC: Synced ${resultData.length} extra instances`)
                console.debug(`fetchCriteria Fetch Time (ms) `, moment().diff(start))
            })
            return okResult(criteriaId && instances.length > 0 ? instances[0] : undefined)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    private handleReportByType(typeResult: RecordDataMap): RecordDataMap {
        const results: ReportResult[] = []
        const subresults: RecordDataMap[] = []
        const jrs: any[] = typeResult.valueForListKey('results')
        const jsrs: any[] = typeResult.valueForListKey('subresults')
        for (let i = 0; i < jrs.length; i++) {
            results.push(new ReportResult(jrs[i]))
            if (jsrs && jsrs.length > 0)
                subresults.push(this.handleReportByType(new RecordDataMap(jsrs[i])))
        }
        const d = new RecordDataMap()
        d.put('dimension', typeResult.valueForKey('dimension'))
        d.put('results', results)
        d.put('subresults', subresults)
        return d
    }

    private handleReportByDate(dateResult: RecordDataMap): RecordDataMap {
        const keys: moment.Moment[] = []
        const totals: ReportTotal[] = []
        const jks: string[] = dateResult.valueForListKey('keys')
        const jts: RecordDataMap[] = dateResult.valueForListKey('totals')
        for (let i = 0; i < jks.length; i++) {
            keys.push(moment(jks[i]))
            const jtsi = jts[i]
            totals.push(new ReportTotal(jtsi instanceof RecordDataMap ? jtsi.serialize() : jtsi))
        }
        const d = new RecordDataMap()
        d.put('date_bin', dateResult.valueForKey('date_bin'))
        d.put('keys', keys)
        d.put('totals', totals)
        return d
    }

    private handleReportByDow(dowResult: RecordDataMap): RecordDataMap {
        const keys: string[] = []
        const totals: RecordDataMap[] = []
        const jks: string[] = dowResult.valueForListKey('keys')
        const jts: any[] = dowResult.valueForListKey('totals')
        for (let i = 0; i < jks.length; i++) {
            keys.push(jks[i])
            const totalByDow = new RecordDataMap(jts[i])
            for (const dow of Object.keys(totalByDow)) {
                const totalByDowDow = totalByDow.get(dow)
                totalByDow.put(
                    dow,
                    new ReportTotal(
                        totalByDowDow instanceof RecordDataMap
                            ? totalByDowDow.serialize()
                            : totalByDowDow
                    )
                )
            }
            totals.push(totalByDow)
        }
        const d = new RecordDataMap()
        d.put('keys', keys)
        d.put('totals', totals)
        return d
    }

    private parseComponents(components: any[]): string[] {
        const cls: string[] = []
        for (const component of components) {
            cls.push(component.toString())
        }
        return cls
    }

    private parseReportResult(results: any): RecordDataMap {
        const reportData = new RecordDataMap()
        const result = new RecordDataMap(results)
        reportData.put('date', result.valueForDateKey('date'))
        reportData.put('end_date', result.valueForDateKey('end_date'))
        reportData.put('date_type', result.valueForKey('date_type'))
        reportData.put('date_compare_type', result.valueForKey('date_compare_type'))
        reportData.put('measures', this.parseComponents(result.valueForListKey('measures')))
        reportData.put('dimensions', this.parseComponents(result.valueForListKey('dimensions')))
        const totalJS = result.valueForDictionaryKey('total')!
        const dowJS = result.valueForDictionaryKey('total_by_dow')!
        const typeJS = result.valueForDictionaryKey('total_by_type')!
        const sortJS = result.valueForDictionaryKey('sort_by_type')!
        const dateJS = result.valueForDictionaryKey('total_by_date')!
        const totals = new RecordDataMap()
        const dowTotals = new RecordDataMap()
        const typeTotals = new RecordDataMap()
        const dateTotals = new RecordDataMap()
        for (const key of Object.keys(totalJS)) {
            totals.put(key, new ReportTotal(totalJS[key]))
            if (dowJS.hasValueForKey(key))
                dowTotals.put(key, this.handleReportByDow(dowJS.valueForDictionaryKey(key)!))
            if (typeJS.hasValueForKey(key))
                typeTotals.put(key, this.handleReportByType(typeJS.valueForDictionaryKey(key)!))
            if (dateJS.hasValueForKey(key))
                dateTotals.put(key, this.handleReportByDate(dateJS.valueForDictionaryKey(key)!))
        }
        reportData.put('total', totals)
        reportData.put('total_by_dow', dowTotals)
        reportData.put('total_by_type', typeTotals)
        reportData.put('total_by_date', dateTotals)
        reportData.put('sort_by_type', sortJS)
        return reportData
    }

    async runReport(
        measureId: string,
        measures: PSComponent[],
        dimensions: PSComponent[],
        criterias: ReportCriteria[],
        date?: moment.Moment,
        endDate?: moment.Moment,
        dateType?: string,
        dateCompareType?: string,
        person?: User,
        subaccount?: Subaccount
    ): Promise<Result<RecordDataMap>> {
        const start = moment()
        const jsonData: any = {}
        jsonData['measure'] = measureId
        if (measures) {
            const lm: string[] = []
            for (const m of measures) lm.push(m.id!)
            jsonData['measures'] = lm
        }
        if (dimensions) {
            const ld: string[] = []
            for (const d of dimensions) ld.push(d.id!)
            jsonData['dimensions'] = ld
        }
        if (criterias) {
            const lc: any[] = []
            for (const c of criterias) lc.push(c.jsonObject())
            jsonData['criterias'] = lc
        }
        if (date && dateType === 'custom') jsonData['date'] = date.format('YYYY-MM-DD')
        if (endDate && dateType === 'custom') jsonData['end_date'] = endDate.format('YYYY-MM-DD')
        if (dateType) jsonData['date_type'] = dateType
        if (dateCompareType) jsonData['date_compare_type'] = dateCompareType
        if (person) jsonData['person'] = person.id
        if (subaccount) jsonData['subaccount'] = subaccount.id
        jsonData['show_all_metrics'] = true

        try {
            const result = await apiClient.post('/report/metrics', jsonData)
            const results = result.data
            const extraTypes = results['extra_types']
            const extraResults = results['extra_results']
            const reportData = this.parseReportResult(results)
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < extraResults.length; i++)
                    await txManager.recordFactory.store(extraTypes[i], extraResults[i])
                if (extraResults?.length)
                    console.debug(`SYNC: Synced ${extraResults.length} extra instances`)
                console.debug(`runReport Fetch Time (ms) ${moment().diff(start)}`)
            })
            return okResult(reportData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async runDetailReport(
        measureId: string,
        dimensions: PSComponent[] | undefined,
        values: ReportResult[],
        criterias: ReportCriteria[] | undefined,
        keywords: string | undefined,
        date: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        dateType: string | undefined,
        dateCompareType: string | undefined,
        page: number,
        person: User | undefined,
        subaccount: Subaccount | undefined
    ): Promise<Result<RecordDataMap>> {
        const start = moment()

        const url = '/report/records'
        const jsonData: any = {}
        jsonData['measure'] = measureId
        if (values) {
            const lv: string[] = []
            for (const r of values) lv.push(r.id ?? '')
            jsonData['values'] = lv
        }
        if (dimensions) {
            const ld: string[] = []
            for (const d of dimensions) ld.push(d.id ?? '')
            jsonData['dimensions'] = ld
        }
        if (criterias) {
            const lc: any[] = []
            for (const c of criterias) lc.push(c.jsonObject())
            jsonData['criterias'] = lc
        }
        jsonData['page'] = page
        if (date) jsonData['date'] = date.format('YYYY-MM-DD')
        if (endDate) jsonData['end_date'] = endDate.format('YYYY-MM-DD')
        if (dateType) jsonData['date_type'] = dateType
        if (dateCompareType) jsonData['date_compare_type'] = dateCompareType
        if (keywords) jsonData['keywords'] = keywords
        if (person) jsonData['person'] = person.id
        if (subaccount) jsonData['subaccount'] = subaccount.id

        try {
            const result = await apiClient.post(url, jsonData)
            const resultData = result.data
            const extraTypes = resultData['extra_types']
            const extraResults = resultData['extra_results']
            const reportData = new RecordDataMap()
            reportData.put('subtitle', resultData['subtitle'])
            reportData.put('limit', resultData['limit'])
            reportData.put('total', new ReportTotal(resultData['total']))

            const jrs = resultData['results']
            const results: ReportResult[] = []
            for (const jr of jrs) results.push(new ReportResult(jr))
            reportData.put('results', results)

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < extraResults.length; i++)
                    await txManager.recordFactory.store(extraTypes[i], extraResults[i])
                if (extraResults?.length)
                    console.debug(`SYNC: Synced ${extraResults.length} extra instances`)
                console.debug(`runDetailReport Fetch Time (ms) ${moment().diff(start)}`)
            })
            return okResult(reportData)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchLeaderboard(
        type: string
    ): Promise<Result<{ leaderboard: Leaderboard; aliases: RecordDataMap[] }>> {
        const start = moment()
        const jsonData: any = { type: type }

        try {
            const result = await apiClient.post('/leaderboard', jsonData)
            const results = result.data
            const aliasResults = results['aliases']
            const aliasArray = aliasResults.map((alias: any) => new RecordDataMap(alias))

            const leaderboard = new Leaderboard(results['leaderboard'])
            console.debug(
                `FETCH: Leaderboard Fetch Time (${leaderboard.id}): ${moment().diff(start)}ms`
            )

            return okResult({ leaderboard: leaderboard, aliases: aliasArray })
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchAliases(type: string): Promise<Result<Alias[]>> {
        const start = moment()
        const jsonData: any = { type: type }

        try {
            const result = await apiClient.post('/aliases', jsonData)
            const aliasResults = result.data
            const aliasArray: Alias[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < aliasResults.length; i++)
                    aliasArray.push(await txManager.recordFactory.storeAlias(aliasResults[i], true))
                if (aliasResults?.length)
                    console.debug(`FETCH: Synced ${aliasResults.length} aliases`)
            })

            console.debug(`FETCH: Leaderboard aliases Sync Time: ${moment().diff(start)}ms`)

            return okResult(aliasArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchFeeds(
        date: moment.Moment | undefined,
        poster: User | undefined,
        keywords: string
    ): Promise<Result<Feed[]>> {
        const start = moment()
        const jsonData: any = {}
        if (date !== undefined) jsonData['date'] = date.format('YYYYMMDDHHmmss')
        if (poster !== undefined) jsonData['poster'] = poster.id
        if (keywords) jsonData['keywords'] = keywords

        try {
            const result = await apiClient.post('/feed/sync', jsonData)
            const results = result.data
            const postResult = results['posts']
            const userResult = results['users']
            const feedResult = results['feeds']
            const feeds: Feed[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < userResult.length; i++)
                    await txManager.recordFactory.storeUser(userResult[i])
                if (userResult?.length) console.debug(`FETCH: Synced ${userResult.length} users`)
                for (let i = 0; i < postResult.length; i++)
                    await txManager.recordFactory.storePost(postResult[i])
                if (postResult?.length) console.debug(`FETCH: Synced ${postResult.length} posts`)
                for (let i = 0; i < feedResult.length; i++)
                    feeds.push(await txManager.recordFactory.storeFeed(feedResult[i], true))
                if (feedResult?.length) console.debug(`FETCH: Synced ${feedResult.length} feeds`)
            })

            console.debug(`FETCH: fetchFeeds Exec Time: ${moment().diff(start)}ms`)

            return okResult(feeds)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchPosts(
        date: moment.Moment | undefined,
        report: string,
        keywords: string
    ): Promise<Result<Post[]>> {
        const start = moment()
        const jsonData: any = {}
        if (date !== undefined) jsonData['date'] = date.format('YYYYMMDDHHmmss')
        if (report) jsonData['report'] = report
        if (keywords) jsonData['keywords'] = keywords

        try {
            const result = await apiClient.post('/post/sync', jsonData)
            const results = result.data
            const postResult = results['posts']
            const userResult = results['users']
            const posts: Post[] = []

            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (let i = 0; i < userResult.length; i++)
                    await txManager.recordFactory.storeUser(userResult[i])
                if (userResult?.length) console.debug(`FETCH: Synced ${userResult.length} users`)
                for (let i = 0; i < postResult.length; i++)
                    posts.push(await txManager.recordFactory.storePost(postResult[i], true))
                if (postResult?.length) console.debug(`FETCH: Synced ${postResult.length} posts`)
            })

            console.debug(`FETCH: Posts Fetch Time: ${moment().diff(start)}ms`)

            return okResult(posts)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendPost(post: Post, image?: File): Promise<Result<Post>> {
        const url = post.id ? `/post/${post.id}` : '/post'
        try {
            const result = await apiClient.post(url, addImage(post.jsonObject(), image))

            const newPost = await this.storePost(result.data.post, true)
            this.postNotification('PostDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPost)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deletePost(id: string): Promise<Result<Post>> {
        try {
            const result = await apiClient.post(`/post/${id}/delete`)

            const newPost = await this.storePost(JSON.parse(result.data), true)
            this.postNotification('PostDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPost)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async archivePost(id: string): Promise<Result<Post>> {
        try {
            const result = await apiClient.post(`/post/${id}/archive`)

            const newPost = await this.storePost(JSON.parse(result.data), true)
            this.postNotification('PostDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newPost)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async logFeed(feed: Feed, type: string): Promise<Result<Feed>> {
        try {
            const result = await apiClient.post(`/feed/${feed.id}/${type}`)

            const newFeed = await this.storeFeed(result.data.feed, true)
            if (type === 'open' || type === 'click') {
                this.postNotification('PostDataSourceChanged', null)
                this.postNotification('DashboardDataSourceChanged', null)
            }
            return okResult(newFeed)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchBadges(idString: string): Promise<Result<Badge[]>> {
        const start = moment()
        const url = `/user/${idString}/badges`

        try {
            const result = await apiClient.post(url)
            const badges = result.data.badges
            const badgeArray: Badge[] = []

            for (const badge of badges) badgeArray.push(new Badge(badge))

            console.debug(`FETCH: Synced ${badges.length} badges`)
            console.debug(`Badges Fetch Time (ms) ${moment().diff(start)}`)
            return okResult(badgeArray)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async syncEvents(forceDelete: boolean): Promise<Result<any[] | null>> {
        const idString = PSApplication.me().id
        let deleteKeys: string[] = []

        if (forceDelete) {
            //Delete all the events that are not removed from PS on both sqlite and server
            const events = await this.loadEvents()
            for (const ev of events) if (!ev.cancelledDate) deleteKeys.push(ev.id!)
            if (deleteKeys?.length) {
                await apiClient.post('/events/delete', { events: deleteKeys })
                await this.executor.executeUpdate(
                    'DELETE FROM events where cancelledDate IS NULL and device=?',
                    (await Device.currentDevice())!.id!
                )
                console.debug(`DELETED events ${deleteKeys.length}`)
                return this.syncEvents(false)
            }
            else {
                return this.syncEvents(false)
            }
        }

        // todo: ComputeFactory calendarDisabled, calendarEnabled
        // if (ComputeFactory.getInstance().calendarDisabled() && idString) {
        //     const events = await this.loadEvents();
        //     for (const ev of events)
        //         deleteKeys.push(ev.id!);
        //     if (deleteKeys?.length) {
        //         await apiClient.post("/events/delete", { events: deleteKeys });
        //         await this.executor.executeUpdate("DELETE FROM events where device=?", (await Device.currentDevice())!.id!);
        //         console.debug(`DELETED events ${deleteKeys.length}`);
        //     }
        //     await this.syncEvents(false, onLoad);
        //     return;
        // }
        //
        // if (!ComputeFactory.getInstance().calendarEnabled() || !idString) {
        //     //calendar is not enabled, no sync will be performed
        //     if (onLoad)
        //         onLoad(null);
        //     return;
        // }

        const keys: { [key: string]: PSEvent | undefined } = {}
        const events = await this.loadEvents()
        for (const event of events) keys[event.id!] = event

        let newEvents = 0
        let updatedEvents = 0
        let deletedEvents = 0

        const eventsToSync: PSEvent[] = [] // events to be updated/inserted
        //fetch all the local events (both PS and non-PS) from users' sync-from calendars
        // todo: const localEvents = ComputeFactory.getInstance().fetchAvailability();
        //Get all the events that are not PS events
        // todo: for (const eventInfo of localEvents) {
        //     const event = new PSEvent(eventInfo);
        //     if (event.isPS || !event.id)
        //         // we don't sync PS events here
        //         continue;
        //     if (keys[event.id!] && keys[event.id!]?.cancelledDate) {
        //         keys[event.id!] = undefined;
        //         // we don't care about events that were cancelled by the user
        //         continue;
        //     }
        //     if (keys[event.id!]) {
        //         //existing events
        //         const existingEvent = keys[event.id!] as PSEvent;
        //         event.importedDate = existingEvent.importedDate;
        //         event.cancelledDate = existingEvent.cancelledDate;
        //         //modifiedDate from event is not reliable, we compare two copies of the event to check if it was updated
        //         if (!this.isSameEvent(event, existingEvent)) {
        //             updatedEvents++;
        //             eventsToSync.push(event);
        //         }
        //         keys[event.id!] = undefined;
        //     } else {
        //         //new events
        //         newEvents++;
        //         eventsToSync.push(event);
        //     }
        // }

        // delete all the events that are deleted from local calendars
        deleteKeys = Object.keys(keys).filter((key) => keys[key] !== undefined)
        deletedEvents = deleteKeys.length
        if (deleteKeys?.length) {
            await apiClient.post('/events/delete', { events: deleteKeys })
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const id of deleteKeys)
                    await txManager.executeUpdate('DELETE FROM events where id = ?', id)
            })
        }

        for (const key in keys) {
            delete keys[key]
        }

        //store all the non-ps events from local calendar to sqlite DB
        const url = '/events/sync'
        const submitArray: any[] = []
        if (eventsToSync?.length) {
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const event of eventsToSync) {
                    submitArray.push(event.jsonObject())
                    await txManager.recordFactory.storeEvent(event.jsonObject())
                }
            })
        }

        const count = updatedEvents + newEvents + deletedEvents
        //push all the non-ps events from local calendar to sqlite DB
        if (submitArray?.length) {
            const syncJsonData = { events: submitArray }
            try {
                const result = await apiClient.post(url, syncJsonData)

                console.debug(`POSTED events ${submitArray.length}`)
                this.postNotification('ScheduleDataSourceChanged', null)
                if (result) {
                    const alert = `${count} ${count !== 1 ? 'appointments' : 'appointment'} synced`
                    // Toast.makeText(activity, alert, Toast.LENGTH_SHORT).show();
                }
                return okResult(submitArray)
            }
            catch (error) {
                return okResult(null)
            }
        }
        else {
            if (deletedEvents > 0) {
                this.postNotification('ScheduleDataSourceChanged', null)
            }
            return okResult(submitArray)
        }
    }

    private isSameEvent(e1: PSEvent, e2: PSEvent): boolean {
        if (!e1 || !e2) return !e1 && !e2

        const sameDate = (!e1.date && !e2.date) || (e1.date && e2.date && e1.date.isSame(e2.date))
        const sameEndDate =
            (!e1.endDate && !e2.endDate) ||
            (e1.endDate && e2.endDate && e1.endDate.isSame(e2.endDate))
        const sameURL = (!e1.url && !e2.url) || (e1.url && e2.url && e1.url === e2.url)
        const sameTitle =
            (!e1.title && !e2.title) || (e1.title && e2.title && e1.title === e2.title)
        const sameLocation =
            (!e1.location && !e2.location) ||
            (e1.location && e2.location && e1.location === e2.location)

        return !!sameDate && !!sameEndDate && !!sameURL && !!sameTitle && !!sameLocation
    }

    // todo: implement syncAddrBook in React
    // async syncAddrBook(forceDelete: boolean, onLoad?: (result: any) => void) {
    //     const idString = PSApplication.me().id;
    //     if (forceDelete) {
    //         try {
    //             const result = await apiClient.post("/contacts/delete");
    //             const resultData = result.data;
    //             if (resultData.device)
    //                 this.storeDevice(resultData.device);
    //             await this.executor.executeUpdate("DELETE FROM contacts where device=?", (await Device.currentDevice())!.id!);
    //             console.debug("DELETED contacts", resultData.count);
    //             if (onLoad)
    //                 await this.syncAddrBook(false, onLoad);
    //         }
    //         catch (error) {
    //             console.debug("POST DELETE failure", "");
    //             if (onLoad)
    //                 await this.syncAddrBook(false, onLoad);
    //         }
    //         return;
    //     }
    //     //todo: implement ComputeFactory.getInstance().addrBookEnabled()
    //     if (!ComputeFactory.getInstance().addrBookEnabled() || !idString) {
    //         if (onLoad)
    //             onLoad(null);
    //         return;
    //     }
    //     const device = await Device.currentDevice()!;
    //     const lastSyncDate = device && device.lastContactDate ? device.lastContactDate : undefined;
    //     const projection = [
    //         ContactsContract.Data.CONTACT_ID,
    //         ContactsContract.Contacts.DISPLAY_NAME,
    //         ContactsContract.Data.MIMETYPE,
    //         ContactsContract.Data.DATA1,
    //         ContactsContract.CommonDataKinds.Contactables.CONTACT_LAST_UPDATED_TIMESTAMP,
    //         ContactsContract.CommonDataKinds.Phone.TYPE,
    //         ContactsContract.CommonDataKinds.StructuredPostal.STREET,
    //         ContactsContract.CommonDataKinds.StructuredPostal.REGION,
    //         ContactsContract.CommonDataKinds.StructuredPostal.CITY,
    //         ContactsContract.CommonDataKinds.StructuredPostal.POSTCODE,
    //         ContactsContract.CommonDataKinds.StructuredPostal.FORMATTED_ADDRESS
    //     ];
    //     const selection = `${ContactsContract.Data.MIMETYPE} in (?, ?, ?, ?, ?)`;
    //     const selectionArgs = [
    //         ContactsContract.CommonDataKinds.Email.CONTENT_ITEM_TYPE,
    //         ContactsContract.CommonDataKinds.Phone.CONTENT_ITEM_TYPE,
    //         ContactsContract.CommonDataKinds.StructuredPostal.CONTENT_ITEM_TYPE,
    //         ContactsContract.CommonDataKinds.Organization.CONTENT_ITEM_TYPE,
    //         ContactsContract.CommonDataKinds.Note.CONTENT_ITEM_TYPE
    //     ];
    //     const sortOrder = ContactsContract.Data.CONTACT_ID;
    //     const uri = ContactsContract.Data.CONTENT_URI;
    //     const cursor = await PSApplication.getAppContext().getContentResolver().query(uri, projection, selection, selectionArgs, sortOrder);
    //
    //     const contactsInfo: PSRecord<string, any> = {}; // mapping contact_ID to the all the info of the contact
    //     const localContacts: Contact[] = [];
    //
    //     if (cursor) {
    //         const mimeTypeIdx = cursor.getColumnIndex(ContactsContract.Data.MIMETYPE);
    //         const idIdx = cursor.getColumnIndex(ContactsContract.Data.CONTACT_ID);
    //         const nameIdx = cursor.getColumnIndex(ContactsContract.Contacts.DISPLAY_NAME);
    //         const dataIdx = cursor.getColumnIndex(ContactsContract.Data.DATA1);
    //         const mdIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.Contactables.CONTACT_LAST_UPDATED_TIMESTAMP);
    //         const streetIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.StructuredPostal.STREET);
    //         const postcodeIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.StructuredPostal.POSTCODE);
    //         const cityIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.StructuredPostal.CITY);
    //         const regionIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.StructuredPostal.REGION);
    //         const phoneTypeIdx = cursor.getColumnIndex(ContactsContract.CommonDataKinds.Phone.TYPE);
    //
    //         while (cursor.moveToNext()) {
    //             const md = cursor.getLong(mdIdx);
    //             const mD = moment(md);
    //             if (lastSyncDate && mD.isBefore(lastSyncDate)) {
    //                 //we are doing an incremental sync and nothing has changed since we last sync
    //                 continue;
    //             }
    //
    //             //Extracting information from each contact
    //             const id = cursor.getString(idIdx);
    //             let currentContact;
    //             if (!contactsInfo[id]) {
    //                 currentContact = {};
    //                 contactsInfo[id] = currentContact;
    //             }
    //             else
    //                 currentContact = contactsInfo[id];
    //
    //             currentContact["externalId"] = id;
    //             currentContact["modified_date"] = mD;
    //             currentContact["creation_date"] = mD;
    //
    //             const name = cursor.getString(nameIdx);
    //             const firstName = Contact.firstName(name);
    //             const lastName = Contact.lastName(name);
    //             currentContact["name"] = name;
    //             currentContact["first"] = firstName;
    //             currentContact["last"] = lastName;
    //
    //             const data = cursor.getString(dataIdx);
    //             const mimeType = cursor.getString(mimeTypeIdx);
    //
    //             if (!data)
    //                 continue;
    //
    //             if (mimeType === ContactsContract.CommonDataKinds.Email.CONTENT_ITEM_TYPE)
    //                 currentContact["email"] = data;
    //             else if (mimeType === ContactsContract.CommonDataKinds.Phone.CONTENT_ITEM_TYPE) {
    //                 const phoneType = cursor.getInt(phoneTypeIdx);
    //                 const phoneTypeString = ContactsContract.CommonDataKinds.Phone.getTypeLabel(PSApplication.getAppContext().getResources(), phoneType, "");
    //                 if (currentContact["label"])
    //                     currentContact["label"].push(phoneTypeString);
    //                 else
    //                     currentContact["label"] = [phoneTypeString];
    //                 if (currentContact["phone"])
    //                     currentContact["phone"].push(data);
    //                 else
    //                     currentContact["phone"] = [data];
    //             }
    //             else if (mimeType === ContactsContract.CommonDataKinds.Note.CONTENT_ITEM_TYPE)
    //                 currentContact["notes"] = data;
    //             else if (mimeType === ContactsContract.CommonDataKinds.StructuredPostal.CONTENT_ITEM_TYPE) {
    //                 currentContact["line1"] = cursor.getString(streetIdx);
    //                 currentContact["state"] = cursor.getString(regionIdx);
    //                 currentContact["city"] = cursor.getString(cityIdx);
    //                 currentContact["postalCode"] = cursor.getString(postcodeIdx);
    //             }
    //             else if (mimeType === ContactsContract.CommonDataKinds.Organization.CONTENT_ITEM_TYPE) {
    //                 currentContact["entity"] = data;
    //             }
    //         }
    //         cursor.close();
    //     }
    //
    //     for (const contact of Object.values(contactsInfo)) {
    //         const theContact = contact as PSRecord<string, any>;
    //         const numbers = theContact["phone"] ? theContact["phone"] : [];
    //         const labels = theContact["label"] ? theContact["label"] : [];
    //         //parsing contact by unique phone numbers
    //         if (numbers.length === 0) {
    //             theContact["phone"] = undefined;
    //             if (theContact["email"])
    //                 localContacts.push(new Contact(theContact));
    //         }
    //         else {
    //             let index = 0;
    //             for (const number of numbers) {
    //                 theContact["phone"] = number;
    //                 theContact["label"] = labels[index++];
    //                 localContacts.push(new Contact(theContact));
    //             }
    //         }
    //     }
    //
    //     for (const contact of localContacts) {
    //         const mD = contact.modifiedDate;
    //
    //         if (contact.name && (contact.phone || contact.email)) {
    //             let searchString = contact.phone ? contact.phone.replace(/\D+/g, "") : "";
    //             if (!searchString)
    //                 searchString = contact.email ? contact.email : "";
    //             if (!searchString)
    //                 continue;
    //             if (!lastSyncDate || moment(mD).isAfter(lastSyncDate)) {
    //                 if (contact.phone)
    //                     contact.phone = contact.phone ? contact.phone.replace(/\D+/g, "") : "";
    //             }
    //         }
    //     }
    //
    //     console.debug(`Fetched ${localContacts.length} contacts`, `lastSyncDate: ${formatTimeStamp(moment(lastSyncDate))}`);
    //     let url = "/contacts/sync";
    //     const submitArray: any[] = [];
    //     if (lastSyncDate) {
    //         url += `/${moment(lastSyncDate).format("YYYYMMDDHHmmss")}`;
    //     }
    //
    //     await SQLiteFactory.getInstance().executeTransaction(async () => {
    //         for (const contact of localContacts) {
    //             submitArray.push(JSON.parse(contact.jsonObject()));
    //         }
    //     });
    //
    //     if (submitArray?.length) {
    //         console.debug("SAVING contacts", `${submitArray.length}`);
    //         try {
    //             const result = await apiClient.post(url, { contacts: submitArray });
    //             console.debug("POSTED contacts", `${submitArray.length}`);
    //             if (onLoad)
    //                 onLoad(submitArray);
    //             if (result) {
    //                 const alert = `${submitArray.length} ${submitArray.length !== 1 ? "contacts" : "contact"} synced`;
    //                 const activity = PSApplication.getInstance().currentActivity;
    //                 if (activity) {
    //                     // activity.runOnUiThread(() => Toast.makeText(activity, alert, Toast.LENGTH_SHORT).show());
    //                 }
    //             }
    //         }
    //         catch (error) {
    //             console.debug("POST failure contacts", `${submitArray.length}`);
    //             if (onLoad)
    //                 onLoad(null);
    //         }
    //     }
    //     else if (onLoad)
    //         onLoad(submitArray);
    // }

    async login(
        args:
            | { e164: string; code: string; redirectUrl: string | null }
            | { associatedAccountUserID: string },
        queryClient: QueryClient,
        rollbar: Rollbar,
        shouldReturnEarlyIfRequiresRole = false
    ): Promise<Result<LoginResponse>> {
        const loginResult = await (async () => {
            if ('e164' in args) {
                return loginWithCode(args.e164, args.code, args.redirectUrl)
            }
            else {
                return loginWithAssociatedAccountUserID(args.associatedAccountUserID)
            }
        })()
        if (!loginResult.ok) {
            return loginResult
        }

        const loginResponse = loginResult.value

        queryClient.clear()
        CacheFactory.getInstance().clearAll()

        // Update the device ID now that we have it
        localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, loginResponse.device.device)
        apiClient.defaults.headers['X-Requested-Device'] = loginResponse.device.device

        // The user logged in successfully, redirect to dashboard
        localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_KEY, '1')
        localStorage.setItem(LOCAL_STORAGE_USER_ID_KEY, loginResponse.login.id)
        localStorage.setItem(LOCAL_STORAGE_INDUSTRIES_KEY, JSON.stringify(loginResponse.industries))

        // TODO Call new /sync to get employer and referral saved to localStorage

        rollbar.configure({
            payload: {
                person: {
                    id: loginResponse.login.id,
                },
            },
        })

        if (shouldReturnEarlyIfRequiresRole && loginResponse.requiresRole) {
            return okResult(loginResponse)
        }

        await SQLiteFactory.getInstance().dropDB()

        await SQLiteFactory.getInstance().initializeSql()

        await Promise.all([
            RecordFactory.getInstance().fetchUser(loginResponse.login.id),
            RecordFactory.getInstance().sync(loginResponse.login.id),
        ])

        // initializeCache will store both the user and the device in the database + cache
        await initializeCache()

        return okResult(loginResponse)
    }

    async signup(
        user: User,
        args:
            | { type: 'business'; planID: string }
            | { type: 'mle'; associatedAccountUserIDs: string[] },
        queryClient: QueryClient,
        rollbar: Rollbar
    ): Promise<Result<LoginResponse>> {
        const data = user.jsonObject()

        if (args.type === 'business') {
            data.plan = args.planID
        }
        else {
            data.locations = args.associatedAccountUserIDs
        }

        const result = await apiClient.post('/signup', data)
        if (!result.data.user) {
            return errorResult(new Error('Failed to sign up'))
        }

        return await this.login(
            { associatedAccountUserID: result.data.user.id },
            queryClient,
            rollbar
        )
    }

    // todo: implement logout
    // async logout(activity: Activity, doNavigate: boolean, onLoad?: (result: any) => void) {
    //     const jsonData: any = {};
    //     if (Device.currentDevice())
    //         jsonData['device'] = (await Device.currentDevice())!.id!;
    //     try {
    //         await apiClient.post("/logout", jsonData);
    //         this.executor.willSync(undefined, true);
    //         PSApplication.didLogout(doNavigate);
    //         if (onLoad)
    //             onLoad(null);
    //     } catch (error) {
    //         console.error("Logout error", error);
    //     }
    // }

    // todo: fetchConnectionToken needs ConnectionTokenCallback
    // async fetchConnectionToken(connectionTokenCallback: ConnectionTokenCallback) {
    //     try {
    //         const result = await apiClient.post("/checkout/token");
    //         const resultData = result.data;
    //         connectionTokenCallback.onSuccess(resultData.token);
    //     } catch (error) {
    //         connectionTokenCallback.onFailure(new ConnectionTokenException(error.description));
    //     }
    // }

    async createIntent(
        payment: Payment,
        reader: string,
        version: string | undefined,
        battery: number | undefined,
        stripeReaderId: string | undefined,
        stripeLocationId: string | undefined,
        sourceType: string | undefined,
        sendReader: boolean | undefined
    ): Promise<Result<any>> {
        const jsonData: any = payment.jsonObject()
        let type = payment.type
        // if (type === 'confirm') // the below statment isn't true for wise e & we don't need to support bnpl on desktop (no way for clients to log in)
        //     // we only confirm with bnpl and we are actually not confirming we are completeing the order when using financing
        //     jsonData['type'] = 'complete'
        // else if (type === 'checkout')
        //     // checkout is a frontend only type used to track new checkout, thus for bnpl intent types use the booking type
        //     jsonData['type'] = payment.booking ? payment.booking.item!.type : 'complete'
        jsonData.type = type
        if (!('total' in jsonData)) jsonData['total'] = payment.total
        if (reader) jsonData['reader'] = reader
        if (version) jsonData['version'] = version
        if (battery) jsonData['battery'] = battery
        if (stripeReaderId) jsonData['stripe_reader_id'] = stripeReaderId
        if (stripeLocationId) jsonData['location'] = stripeLocationId
        if (sourceType) jsonData['source_type'] = sourceType
        if (payment.gift) jsonData['gift'] = payment.gift.id
        if (payment.order) jsonData['order'] = payment.order.id
        if (payment.booking) jsonData['booking'] = payment.booking.id
        if (payment.aPackage) jsonData['package'] = payment.aPackage.id
        if (payment.invoice) jsonData['invoice'] = payment.invoice.id
        if (payment.estimate) jsonData['estimate'] = payment.estimate.id
        if (sendReader)
            // send charge to internet readers
            jsonData['send_reader'] = true
        try {
            const result = await apiClient.post(`/checkout/${payment.owner!.id!}/intent`, jsonData)
            const resultData = result.data
            const info: any = {
                secret: resultData.secret,
                intent: resultData.intent,
                client: await this.storeUser(resultData.client),
                reader: resultData.reader ? await this.storeReader(resultData.reader) : undefined,
                stripeIntentId: resultData.stripe_intent_id,
            }
            return okResult(info)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchReader(readerId?: string, all?: boolean): Promise<Result<Reader | null>> {
        const start = new Date()
        const jsonData: any = {}
        if (all) jsonData['all'] = true
        if (readerId) jsonData['id'] = readerId
        try {
            const result = await apiClient.post('/reader/sync', jsonData)
            const results = result.data
            const readerData = results['readers']
            const instances: Reader[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const reader of readerData)
                    instances.push(await txManager.recordFactory.storeReader(reader, true))
                console.debug(`SYNC: Synced ${readerData.length} card readers`)
                console.debug(`Reader Sync (ms) ${new Date().getTime() - start.getTime()}`)
            })
            return okResult(readerId && instances.length > 0 ? instances[0] : null)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async purchaseReader(
        reader: Reader,
        source: Source,
        quantity: number,
        taxcode?: string
    ): Promise<Result<Reader[]>> {
        const start = new Date()
        const postInfo: any = reader.jsonObject()
        postInfo['source'] = source.id
        postInfo['quantity'] = quantity
        postInfo['type'] = reader.type
        if (taxcode) postInfo['taxcode'] = taxcode
        try {
            const result = await apiClient.post('/reader/purchase', postInfo)
            const results = result.data
            const readerData = results['readers']
            const newReaders: Reader[] = []
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                for (const reader of readerData)
                    newReaders.push(await txManager.recordFactory.storeReader(reader, true))
                if (readerData?.length)
                    console.debug(`SYNC: Synced ${readerData.length} card readers`)
                const paymentData = results['payment']
                if (paymentData) await txManager.recordFactory.storePayment(paymentData)
                const messageData = results['message']
                if (messageData) await txManager.recordFactory.storeMessage(messageData)
                const orderMessageData = results['order_message']
                if (orderMessageData) await txManager.recordFactory.storeMessage(orderMessageData)
            })
            console.debug(
                `FETCH: purchaseReader Time: ${new Date().getTime() - start.getTime()} (${newReaders.length} card readers)`
            )
            return okResult(newReaders)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendReader(reader: Reader, image?: File): Promise<Result<Reader>> {
        const url = reader.id ? `/reader/${reader.id}` : '/reader'
        try {
            const result = await apiClient.post(url, addImage(reader.jsonObject(), image))
            const response = await this.storeReader(result.data, true)
            return okResult(response)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelInternetReaderIntent(reader: Reader): Promise<Result<Reader>> {
        const jsonData: any = reader.jsonObject()
        const url = reader.id ? `/reader/${reader.id}` : '/reader'
        jsonData['cancel_action'] = true
        try {
            const result = await apiClient.post(url, jsonData)
            const response = await this.storeReader(result.data, true)
            return okResult(response)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteReader(readerId: string): Promise<Result<Reader>> {
        try {
            const result = await apiClient.post(`/reader/${readerId}/delete`)
            const response = await this.storeReader(result.data, true)
            return okResult(response)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async sendReaderReceipt(payment: Payment): Promise<Result<Payment>> {
        const jsonData: any = {}
        if (payment.name) jsonData['name'] = payment.name
        if (payment.phone) jsonData['phone'] = payment.phone
        if (payment.email) jsonData['email'] = payment.email
        try {
            const result = await apiClient.post(`/checkout/${payment.id}/receipt`, jsonData)
            const resultData = result.data
            await this.storeUser(resultData.person)
            await this.storeClient(resultData.client)
            await this.storeMessage(resultData.message)
            const newPayment = await this.storePayment(resultData.payment, true)
            return okResult(newPayment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchCredentials(): Promise<VoidResult> {
        const start = moment()
        const jsonData: any = {}

        try {
            const result = await apiClient.post('/credential/sync', jsonData)
            const jsonObject = result.data
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                const credentials = jsonObject['credentials']
                await txManager.recordFactory.storeAll(
                    credentials,
                    txManager.recordFactory.storeCredential
                )
            })
            console.debug(`FETCH: fetchCredentials Time: ${moment().diff(start)}`)
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async cancelCredential(credential: PSCredential): Promise<Result<PSCredential>> {
        const postInfo = credential.jsonObject()
        const url = `/credential/${credential.id}/cancel`

        try {
            const result = await apiClient.post(url, postInfo)
            const newCredential = await this.storeCredential(result.data, true)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newCredential)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deactivateAccount(user: User): Promise<Result<any>> {
        const url = `/user/${user.id}/deactivate`

        try {
            const result = await apiClient.post(url)
            return okResult(result.data)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async fetchAdjustmentHistory(item: Item): Promise<VoidResult> {
        const url = `/adjustment/${item.id}/history`

        try {
            const result = await apiClient.post(url)
            const resultData = result.data['adjustment']
            await SQLiteFactory.getInstance().executeTransaction(async (txManager) => {
                await txManager.recordFactory.storeAll(
                    resultData,
                    txManager.recordFactory.storeAdjustment
                )
                if (resultData?.length)
                    console.debug(`SYNC: Synced ${resultData.length} adjustment lines`)
            })
            return voidResult()
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async deleteAdjustment(adjustment: Adjustment): Promise<Result<Adjustment>> {
        const url = `/adjustment/${adjustment.id}/delete`

        try {
            const result = await apiClient.post(url, adjustment.jsonObject())
            const newAdjustment = await this.storeAdjustment(result.data['adjustment'], true)
            this.postNotification('AdjustmentHistoryDataSourceChanged', null)
            this.postNotification('DashboardDataSourceChanged', null)
            return okResult(newAdjustment)
        }
        catch (error) {
            return errorResult(error)
        }
    }

    async loadAdjustment(id: string): Promise<Adjustment | undefined> {
        if (!id) return undefined
        return this.fetchRecord(id, Adjustment)
    }
}

export const initializeCache = async () => {
    // load common data into the redux store for sync accessability
    // load all industries
    const industriesTask = SQLiteFactory.getInstance().executeSql(
        new SQLBuilder({
            columns: ['jsonData'],
            tables: ['industries'],
        })
    )
    const editionsTask = SQLiteFactory.getInstance().executeSql(
        new SQLBuilder({
            columns: ['jsonData'],
            tables: ['editions'],
        })
    )
    const taxcodesTask = SQLiteFactory.getInstance().executeSql(
        new SQLBuilder({
            columns: ['jsonData'],
            tables: ['taxcodes'],
        })
    )

    const deviceId = CacheFactory.getInstance().deviceId
    let deviceTask = Promise.resolve()
    if (deviceId)
        deviceTask = SQLiteFactory.getInstance().executeSingleRowQuery(
            new SQLBuilder({
                columns: ['jsonData'],
                tables: ['devices'],
                filters: ['device = ?'],
                binds: [deviceId],
                fk: Device.fk,
            })
        )

    let userTask = Promise.resolve()
    const userId = CacheFactory.getInstance().userId
    if (userId)
        userTask = SQLiteFactory.getInstance().executeSingleRowQuery(
            new SQLBuilder({
                columns: ['jsonData'],
                tables: ['users'],
                filters: ['id = ?'],
                binds: [userId],
                fk: User.fk,
            })
        )

    try {
        // caching happens at the sql lite layer, we just need to query sql to ensure data is in redux store for synchrounous access
        await Promise.all([industriesTask, taxcodesTask, editionsTask, deviceTask, userTask])
        await RecordFactory.getInstance().loadFieldsByOwner(PSApplication.me().owner!.id!)
    }
    catch (err) {
        // store.dispatch(userLogout())
    }
}

export const useSqlDb = (create: boolean = false) => {
    const sqliteContext = SQLiteFactory.getInstance()
    const [dbReady, setDbReady] = React.useState(false)

    useEffect(() => {
        const f = async () => {
            if (create) {
                await sqliteContext.dropDB()
                await sqliteContext.createSqlDb()
            }
            await sqliteContext.initializeSql()
            await initializeCache()
            setDbReady(true)
        }
        f()
    }, [sqliteContext, create])

    return dbReady
}
